import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThreeCircles } from 'react-loader-spinner';
import * as XLSX from 'xlsx';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SchoolIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BarChartIcon from '@mui/icons-material/BarChart';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ForumIcon from '@mui/icons-material/Forum';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import BadgeIcon from '@mui/icons-material/Badge';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SettingsIcon from '@mui/icons-material/Settings';
import css from './TeacherDashboard.module.css';
import adminCss from './SchoolAdminDashboard.module.css';
import { PREBUILT_EXAM_CATALOG } from '../../utils/prebuiltExamCatalog';
import MessagingSegment from './MessagingSegment';
import TimetableGrid from './TimetableGrid';
import SchoolPasswordInput from './SchoolPasswordInput';
import { formatLargeText, renderMarkdownNote } from '../../utils/client-functions';
import { compressImageIfNeeded, validateImageFile } from '../../utils/imageUtils';
import { SPECIAL_SYMBOLS } from '../../utils/specialSymbols';
import { ensureMarkingGuideMarks, reconcileQuestionsForDisplay } from '../../utils/examMarkingGuide';
import CareerPathSummaryCard from './CareerPathSummaryCard';
import SchoolPWAInstallBanner from './SchoolPWAInstallBanner';
import SchoolPoweredFooter from './SchoolPoweredFooter';
import SchoolNotificationCenter from './SchoolNotificationCenter';
import useSchoolExamSync from '../../hooks/useSchoolExamSync';
import ExamModeChoiceModal from './ExamModeChoiceModal';
import ManualScriptUploadDrawer from './ManualScriptUploadDrawer';
import ManualScoreEntryDrawer from './ManualScoreEntryDrawer';
import ManualExamMissedAction from './ManualExamMissedAction';
import ExamActionsMenu from './ExamActionsMenu';
import { RetakeAssignmentModal, RetakeBadge, RetakeHistoryList } from './RetakeControls';

// ─── Special Symbols Modal ────────────────────────────────────────────────────
function SpecialSymbolsModal({ onInsert, onClose }) {
  const [search, setSearch] = React.useState('');
  const [activeCategory, setActiveCategory] = React.useState('All');
  const [copiedSymbol, setCopiedSymbol] = React.useState(null);

  const categories = React.useMemo(
    () => ['All', ...Array.from(new Set(SPECIAL_SYMBOLS.map(s => s.category)))],
    []
  );

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return SPECIAL_SYMBOLS.filter(s => {
      const matchesSearch = !q || s.name.toLowerCase().includes(q) || s.symbol === search.trim();
      const matchesCategory = activeCategory === 'All' || s.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [search, activeCategory]);

  const handleSymbol = (s) => {
    navigator.clipboard.writeText(s.symbol).catch(() => {});
    onInsert(s.symbol);
    setCopiedSymbol(s.symbol);
    setTimeout(() => setCopiedSymbol(null), 1200);
  };

  return (
    <div className={css.symOverlay} onClick={onClose}>
      <div className={`${css.symModal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <div className={css.symHeader}>
          <span className={css.symTitle}>Special Symbols</span>
          <button className={css.symCloseBtn} type="button" onClick={onClose}>✕</button>
        </div>
        <div className={css.symSearchRow}>
          <input
            className={css.symSearchInput}
            placeholder="Search by name (e.g. alpha, integral, arrow…)"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <div className={css.symCategoryTabs}>
          {categories.map(cat => (
            <button
              key={cat}
              type="button"
              className={`${css.symCatTab} ${activeCategory === cat ? css.symCatTabActive : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
        <div className={css.symGrid}>
          {filtered.length === 0
            ? <div className={css.symEmpty}>No symbols match your search.</div>
            : filtered.map((s, i) => (
              <button
                key={i}
                type="button"
                className={`${css.symBtn} ${copiedSymbol === s.symbol ? css.symBtnCopied : ''}`}
                onClick={() => handleSymbol(s)}
                title={s.name}
              >
                {s.symbol}
              </button>
            ))
          }
        </div>
        {copiedSymbol && (
          <div className={css.symCopiedMsg}>"{copiedSymbol}" copied &amp; inserted!</div>
        )}
      </div>
    </div>
  );
}

// ─── API helper ───────────────────────────────────────────────────────────────
function teacherFetch(path, opts = {}) {
  const token = localStorage.getItem('school-token');
  const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  return fetch(path, {
    ...opts,
    headers: {
      'x-access-token': token || '',
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(opts.headers || {}),
    },
  }).then(async (r) => {
    const data = await r.json().catch(() => ({}));
    return { ok: r.ok, status: r.status, data };
  });
}

// Raw-Response variant (mirrors schoolFetch/parentFetch/studentFetch's contract) —
// used by <MessagingSegment/>, which expects a Response object (res.ok/res.json()).
function teacherFetchRaw(path, opts = {}) {
  const token = localStorage.getItem('school-token');
  const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  return fetch(path, {
    ...opts,
    headers: {
      'x-access-token': token || '',
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(opts.headers || {}),
    },
  });
}

// ─── Nav config ───────────────────────────────────────────────────────────────
const NAV = [
  { key: 'overview',       label: 'Overview',        Icon: DashboardIcon    },
  { key: 'exams',          label: 'Tests & Exams',   Icon: AssignmentIcon   },
  { key: 'students',       label: 'My Students',     Icon: PeopleIcon       },
  { key: 'class-summary',  label: 'Class Analytics', Icon: BarChartIcon     },
  { key: 'topic-analytics',label: 'Topic Analytics', Icon: TrendingUpIcon   },
  { key: 'insights',       label: 'Teaching Insights',Icon: LightbulbIcon   },
  { key: 'question-bank',  label: 'Question Bank',   Icon: LibraryBooksIcon },
  { key: 'curriculum',     label: 'Curriculum',       Icon: MenuBookIcon     },
  { key: 'calendar',       label: 'Calendar',         Icon: CalendarMonthIcon },
  { key: 'timetable',      label: 'Timetable',        Icon: ScheduleIcon     },
  { key: 'attendance',     label: 'Attendance',       Icon: EventAvailableIcon },
  { key: 'lesson-plans',   label: 'Lesson Plans',     Icon: AutoStoriesIcon },
  { key: 'payslips',       label: 'My Payslips',      Icon: BadgeIcon },
  { key: 'inventory',      label: 'Store & Inventory',Icon: Inventory2Icon },
  { key: 'settings',       label: 'Settings',         Icon: SettingsIcon },
  { key: 'messages',       label: 'Messages',        Icon: ForumIcon },
];

function isFallbackAvatar(url) {
  return !url || url === '/uploads/avatar.png' || String(url).endsWith('/uploads/avatar.png');
}

function getInitials(firstName, lastName) {
  const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.trim().toUpperCase();
  return initials || '?';
}

function AvatarOrInitials({ src, firstName, lastName, size = 40, className = '', rounded = '50%' }) {
  if (src && !isFallbackAvatar(src)) {
    return (
      <img
        src={src}
        alt=""
        className={className}
        style={{ width: size, height: size, borderRadius: rounded }}
      />
    );
  }

  return (
    <div
      className={className}
      style={{
        width: size,
        height: size,
        borderRadius: rounded,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #e0f4f5, #f7fbfc)',
        border: '1px solid #b2e0e3',
        color: '#0c7c84',
        fontWeight: 800,
        letterSpacing: '0.04em',
        flexShrink: 0,
      }}
    >
      {getInitials(firstName, lastName)}
    </div>
  );
}

// ─── Tiny helpers ─────────────────────────────────────────────────────────────
function examDisplayStatus(exam) {
  if (exam.status === 'pending_approval') return exam.status;
  if (exam.examMode === 'manual_scores') {
    return exam.manualScoreStatus === 'recorded' ? 'manual_score_recorded' : 'manual_score_ready';
  }
  if (exam.examMode === 'manual' && exam.status === 'closed' && exam.manualGradingStatus === 'ready') {
    return 'manual_grading_ready';
  }
  return exam.status;
}

function StatusChip({ status }) {
  const map = {
    draft:     css.chipDraft,
    scheduled: css.chipScheduled,
    closed:    css.chipClosed,
    manual_score_ready: css.chipScheduled,
    manual_score_recorded: css.chipClosed,
    manual_grading_ready: css.chipScheduled,
    pending_approval: css.chipPending,
    // legacy/fallback
    published: css.chipClosed,
    pass:      css.chipClosed,
    fail:      css.chipDraft,
  };
  const labels = {
    closed: 'closed', pass: 'pass', fail: 'fail', pending_approval: 'pending approval',
    manual_score_ready: 'ready for score recording',
    manual_score_recorded: 'Scores recorded',
    manual_grading_ready: 'Ready for Script Grading',
  };
  return (
    <span className={`${css.chip} ${map[status] || css.chipDraft}`}>
      {labels[status] || status}
    </span>
  );
}

function renderExamDate(dateStr) {
  if (!dateStr) return <span className={css.dateCellEmpty}>—</span>;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return <span className={css.dateCellEmpty}>—</span>;
  const dateFormatted = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const timeFormatted = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return (
    <div className={css.dateCellStacked}>
      <span className={css.dateCellDate}>{dateFormatted}</span>
      <span className={css.dateCellTime}>{timeFormatted}</span>
    </div>
  );
}

function EmptyState({ message }) {
  return <div className={css.emptyState}>{message}</div>;
}

// ─── Skeleton loader for table sections ───────────────────────────────────────
function SkeletonTableRows({ rows = 5, cols = 6 }) {
  const widths = ['72%', '55%', '50%', '52%', '60%', '44%'];
  return (
    <div className={css.tableWrap}>
      <table className={css.table}>
        <tbody>
          {Array.from({ length: rows }, (_, i) => (
            <tr key={i}>
              {Array.from({ length: cols }, (_, j) => (
                <td key={j}>
                  <span className={css.skel} style={{ height: 13, width: widths[j % widths.length] }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Tiny risk badge ──────────────────────────────────────────────────────────
function RiskBadge({ risk }) {
  const colors = { high: '#c62828', medium: '#f57c00', low: '#2e7d32' };
  const bg     = { high: '#fce4ec', medium: '#fff3e0', low: '#e8f5e9' };
  return (
    <span style={{
      background: bg[risk] || '#f5f5f5', color: colors[risk] || '#555',
      borderRadius: 10, padding: '2px 9px', fontSize: '0.72rem', fontWeight: 700,
    }}>{risk}</span>
  );
}

// ─── Column-header filter dropdown ─────────────────────────────────────────────
function FilterHeader({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const active = value !== 'all';

  const openMenu = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: r.bottom + 4, left: r.left });
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const activeLabel = options.find(o => o.value === value)?.label || label;

  return (
    <th className={css.filterTh}>
      <button
        ref={btnRef}
        type="button"
        className={`${css.filterHeadBtn} ${active ? css.filterHeadBtnActive : ''}`}
        onClick={openMenu}
        title="Filter"
      >
        <span>{active ? activeLabel : label}</span>
        <span className={css.filterCaret}>▾</span>
      </button>
      {open && (
        <div
          ref={menuRef}
          className={css.filterMenu}
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {options.map(o => (
            <button
              key={o.value}
              type="button"
              className={`${css.filterMenuItem} ${o.value === value ? css.filterMenuItemActive : ''}`}
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onChange(o.value); setOpen(false); }}
            >
              <span className={css.filterMenuCheck}>{o.value === value ? '✓' : ''}</span>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </th>
  );
}

function TrendIcon({ declining, scores }) {
  if (!scores || scores.length < 2) return <span style={{ color: '#bbb' }}>—</span>;
  return declining
    ? <TrendingDownIcon fontSize="small" style={{ color: '#e53935', verticalAlign: 'middle' }} />
    : <TrendingUpIcon  fontSize="small" style={{ color: '#43a047', verticalAlign: 'middle' }} />;
}

// ─── Overview tab ─────────────────────────────────────────────────────────────
function OverviewTab({ teacher, school }) {
  const [stats, setStats] = React.useState(null);

  React.useEffect(() => {
    teacherFetch('/teacher/analytics/overview').then(({ ok, data }) => {
      if (ok) setStats(data);
    });
  }, []);

  const cards = [
    { label: 'Total Students', value: stats?.totalStudents ?? '—' },
    { label: 'Exams Created', value: stats?.totalExams ?? '—' },
    { label: 'Avg Score', value: stats?.avgPercentage != null ? `${stats.avgPercentage}%` : '—' },
    { label: 'Pass Rate', value: stats?.passRate != null ? `${stats.passRate}%` : '—' },
    { label: 'At-Risk Students', value: stats?.atRiskCount ?? '—' },
  ];

  return (
    <div>
      <h2 className={css.tabTitle}>Welcome back, Teacher {teacher?.firstName}!</h2>
      <p className={css.tabSub}>{school?.name}</p>
      <div className={css.statGrid}>
        {cards.map(c => (
          <div className={css.statCard} key={c.label}>
            <div className={css.statLabel}>{c.label}</div>
            <div className={css.statValue}>{c.value}</div>
          </div>
        ))}
      </div>

      {(teacher?.assignedSubjects || []).length > 0 && (
        <div className={css.profileSection}>
          <div className={css.profileLabel}>Subjects</div>
          <div className={css.pillRow}>
            {teacher.assignedSubjects.map(s => (
              <span key={s._id || s} className={css.pill}>{s.name || s}</span>
            ))}
          </div>
        </div>
      )}
      {(teacher?.assignedClasses || []).length > 0 && (
        <div className={css.profileSection}>
          <div className={css.profileLabel}>Classes</div>
          <div className={css.pillRow}>
            {teacher.assignedClasses.map(c => (
              <span key={c._id || c} className={css.pill}>{c.name || c}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Question picker row (reused inside exam editor) ─────────────────────────
function QuestionRow({ q, index, onChange, onRemove, equalMarks, autoScore, examId }) {
  const set = (k) => (e) => onChange(index, k, e.target.value);
  const [showScoreLock, setShowScoreLock] = React.useState(false);
  const [diagramUploadState, setDiagramUploadState] = React.useState('idle'); // 'idle' | 'compressing' | 'uploading' | 'error'
  const [diagramUploadError, setDiagramUploadError] = React.useState('');
  const [converting, setConverting] = React.useState(false);
  const [convertError, setConvertError] = React.useState('');
  // Cache converted versions so switching back/forth doesn't re-call AI
  const conversionCacheRef = React.useRef({ mcq: null, theory: null });
  const diagramFileRef = React.useRef(null);
  const qTextareaRef = React.useRef(null);
  const lastSelRef = React.useRef({ start: 0, end: 0 });
  const [symModalOpen, setSymModalOpen] = React.useState(false);
  const isTheory = q.questionType === 'theory';

  const handleTypeSwitch = async (targetType) => {
    if (targetType === q.questionType || converting) return;
    // Save current question as the cache for current type before switching
    conversionCacheRef.current[q.questionType] = { ...q };
    // If we already have a cached version for the target type, restore it
    const cached = conversionCacheRef.current[targetType];
    if (cached) {
      onChange(index, '__replace__', cached);
      return;
    }
    // If no question text, just switch type without AI conversion
    if (!q.question_text?.trim()) {
      onChange(index, 'questionType', targetType);
      if (targetType === 'theory') {
        onChange(index, 'options', []);
        onChange(index, 'correct_answer', '');
      } else {
        onChange(index, 'options', ['', '', '', '']);
        onChange(index, 'marking_guide', '');
        onChange(index, 'model_answer', '');
      }
      return;
    }
    // No cached version — call AI to convert
    if (!examId) {
      // Fallback: just switch type if exam not yet saved
      onChange(index, 'questionType', targetType);
      return;
    }
    setConverting(true);
    setConvertError('');
    try {
      const token = localStorage.getItem('school-token');
      const res = await fetch(`/teacher/exams/${examId}/convert-question`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-access-token': token || '' },
        body: JSON.stringify({ question: q, targetType }),
      });
      const data = await res.json();
      if (!res.ok || !data.question) {
        setConvertError(data.error || 'Conversion failed. Please try again.');
        return;
      }
      conversionCacheRef.current[targetType] = data.question;
      onChange(index, '__replace__', data.question);
    } catch {
      setConvertError('Connection error during conversion.');
    } finally {
      setConverting(false);
    }
  };

  const handleDiagramFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (diagramFileRef.current) diagramFileRef.current.value = '';
    if (!file) return;

    setDiagramUploadState('idle');
    setDiagramUploadError('');
    const validation = validateImageFile(file, 2);
    if (!validation.valid) {
      setDiagramUploadError(validation.error);
      return;
    }

    let finalFile = file;
    if (file.size > 2 * 1024 * 1024) {
      setDiagramUploadState('compressing');
      try {
        const result = await compressImageIfNeeded(file, 2);
        finalFile = result.file;
      } catch {
        setDiagramUploadState('error');
        setDiagramUploadError('Image compression failed. Try a smaller image.');
        return;
      }
    }

    setDiagramUploadState('uploading');
    try {
      const token = localStorage.getItem('school-token');
      const formData = new FormData();
      formData.append('file', finalFile);
      if (q.diagram) formData.append('oldUrl', q.diagram);

      const res = await fetch('/teacher/upload/question-diagram', {
        method: 'POST',
        headers: { 'x-access-token': token || '' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        setDiagramUploadState('error');
        setDiagramUploadError(data.error || 'Upload failed. Please try again.');
        return;
      }
      onChange(index, 'diagram', data.url);
      setDiagramUploadState('idle');
    } catch {
      setDiagramUploadState('error');
      setDiagramUploadError('Upload failed. Check your connection.');
    }
  };

  const handleDeleteDiagram = async () => {
    const url = q.diagram;
    onChange(index, 'diagram', '');
    setDiagramUploadError('');
    // Delete from S3 if it's a school-hosted asset
    if (url && url.includes('/school-assets?key=')) {
      try {
        const token = localStorage.getItem('school-token');
        fetch('/teacher/delete/question-diagram', {
          method: 'POST',
          headers: { 'x-access-token': token || '', 'Content-Type': 'application/json' },
          body: JSON.stringify({ url }),
        }).catch(() => {});
      } catch {} // fire-and-forget
    }
  };

  const isUploading = diagramUploadState === 'compressing' || diagramUploadState === 'uploading';

  return (
    <div className={css.questionCard}>
      <div className={css.questionHeader}>
        <span className={css.questionNum}>Q{index + 1}</span>
        <div className={css.qTypeToggle}>
          <button
            type="button"
            className={`${css.qTypeBtn} ${!isTheory ? css.qTypeBtnActive : ''}`}
            onClick={() => handleTypeSwitch('mcq')}
            disabled={converting}
          >MCQ</button>
          <button
            type="button"
            className={`${css.qTypeBtn} ${isTheory ? css.qTypeBtnActive : ''}`}
            onClick={() => handleTypeSwitch('theory')}
            disabled={converting}
          >{converting ? 'converting...' : 'Theory'}</button>
        </div>
        <button className={css.iconBtn} onClick={() => onRemove(index)} title="Remove question">
          <DeleteIcon fontSize="small" />
        </button>
      </div>
      {q.instructions ? (
        <div className={css.qInstructions}>{q.instructions}</div>
      ) : null}
      {q.diagram ? (
        <img src={q.diagram} alt="diagram" className={css.qDiagram} />
      ) : null}
      <textarea
        ref={qTextareaRef}
        className={css.qInput}
        placeholder="Question text"
        value={q.question_text || ''}
        onChange={set('question_text')}
        rows={2}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -6, marginBottom: 6 }}>
        <button
          type="button"
          className={css.symTriggerBtn}
          title="Insert special symbol"
          onMouseDown={() => {
            if (qTextareaRef.current) {
              lastSelRef.current = {
                start: qTextareaRef.current.selectionStart,
                end: qTextareaRef.current.selectionEnd,
              };
            }
          }}
          onClick={() => setSymModalOpen(true)}
        >
          Ω
        </button>
      </div>
      {symModalOpen && (
        <SpecialSymbolsModal
          onInsert={(symbol) => {
            const text = q.question_text || '';
            const { start, end } = lastSelRef.current;
            const newText = text.slice(0, start) + symbol + text.slice(end);
            onChange(index, 'question_text', newText);
            lastSelRef.current = { start: start + symbol.length, end: start + symbol.length };
            setTimeout(() => {
              if (qTextareaRef.current) {
                qTextareaRef.current.focus();
                qTextareaRef.current.setSelectionRange(start + symbol.length, start + symbol.length);
              }
            }, 0);
          }}
          onClose={() => setSymModalOpen(false)}
        />
      )}
      {!isTheory ? (
        <>
          <div className={css.optionGrid}>
            {[0, 1, 2, 3].map(i => (
              <input
                key={i}
                className={css.optInput}
                placeholder={`Option ${String.fromCharCode(65 + i)}`}
                value={(q.options || [])[i] || ''}
                onChange={e => {
                  const opts = [...(q.options || ['', '', '', ''])];
                  opts[i] = e.target.value;
                  onChange(index, 'options', opts);
                }}
              />
            ))}
          </div>
          <div className={css.qRow}>
            <label className={css.qLabel}>Correct Answer</label>
            <select className={css.qSelect} value={q.correct_answer || ''} onChange={set('correct_answer')}>
              <option value="">— select —</option>
              {(q.options || []).filter(Boolean).map((opt, i) => (
                <option key={i} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </>
      ) : (
        <>
          <div className={css.qRow}>
            <label className={css.qLabel}>Model Answer</label>
            <textarea className={css.qInput} placeholder="Full sample/model answer…" value={q.model_answer || q.correct_answer || ''}
              onChange={e => {
                onChange(index, 'model_answer', e.target.value);
                onChange(index, 'correct_answer', e.target.value);
              }} rows={3} />
          </div>
          <div className={css.qRow}>
            <label className={css.qLabel} style={{ color: '#7c3aed' }}>Marking Guide</label>
            <textarea className={css.qInput} placeholder="Numbered marking guide, e.g. 1. Definition (2 marks) 2. Example (3 marks)…"
              value={q.marking_guide || ''} onChange={e => { onChange(index, 'marking_guide', e.target.value); onChange(index, 'markingGuideEdited', true); }} rows={4}
              style={{ borderColor: q.marking_guide ? '#a78bfa' : undefined }} />
          </div>
        </>
      )}
      {converting && (
        <div style={{ fontSize: '0.78rem', color: '#7c3aed', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className={css.aiSpinner} style={{ width: 12, height: 12 }} />
          Converting question with AI…
        </div>
      )}
      {convertError && (
        <div style={{ fontSize: '0.78rem', color: '#e53935', padding: '2px 0' }}>{convertError}</div>
      )}
      <div className={css.qRow}>
        <label className={css.qLabel}>Topic</label>
        <input className={css.qInput2} placeholder="optional" value={q.topic || ''} onChange={set('topic')} />
        <label className={css.qLabel} style={{ marginLeft: 12 }}>Difficulty</label>
        <select className={css.qSelect} value={q.difficulty || ''} onChange={set('difficulty')}>
          <option value="">—</option>
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
      </div>
      {/* ── Diagram row: URL input + S3 upload button + delete ── */}
      <div className={css.qRow} style={{ alignItems: 'center', gap: 6 }}>
        <label className={css.qLabel}>Diagram</label>
        <input
          className={css.qInput2}
          placeholder="https://… or upload →"
          value={q.diagram || ''}
          onChange={set('diagram')}
          style={{ flex: 1, minWidth: 120 }}
        />
        {/* Hidden file input */}
        <input
          ref={diagramFileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          style={{ display: 'none' }}
          onChange={handleDiagramFileChange}
        />
        <button
          type="button"
          className={css.qTypeBtn}
          disabled={isUploading}
          onClick={() => { setDiagramUploadState('idle'); setDiagramUploadError(''); diagramFileRef.current?.click(); }}
          style={{ whiteSpace: 'nowrap', padding: '6px 10px', fontSize: '0.82rem', flexShrink: 0 }}
          title="Upload an image to use as the diagram"
        >
          {diagramUploadState === 'compressing' ? 'Compressing…'
            : diagramUploadState === 'uploading' ? 'Uploading…'
            : q.diagram ? '↑ Replace'
            : '↑ Upload'}
        </button>
        {q.diagram && !isUploading && (
          <button
            type="button"
            onClick={handleDeleteDiagram}
            title="Remove diagram"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: '#dc143c', display: 'flex', alignItems: 'center', flexShrink: 0 }}
          >
            <DeleteIcon fontSize="small" />
          </button>
        )}
      </div>
      {diagramUploadError && (
        <div style={{ color: '#e53935', fontSize: '0.78rem', marginTop: 2, marginLeft: 60 }}>
          {diagramUploadError}
        </div>
      )}
      <div className={css.qRow}>
        <label className={css.qLabel}>Marks</label>
        <div style={{ position: 'relative' }}>
          <input
            className={`${css.qInput2} ${equalMarks ? css.scoreFieldLocked : ''}`}
            type="number"
            min="0"
            step="0.5"
            value={equalMarks ? (autoScore ?? '') : (q.score ?? '')}
            readOnly={equalMarks}
            onChange={equalMarks ? undefined : set('score')}
            onFocus={() => { if (equalMarks) { setShowScoreLock(true); setTimeout(() => setShowScoreLock(false), 3500); } }}
            style={{ width: 80, cursor: equalMarks ? 'not-allowed' : 'auto' }}
          />
          {showScoreLock && (
            <div className={css.scoreLockTooltip}>
              Disable "Equal marks" to set individual scores
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Prebuilt exam catalog (imported from shared file) ─────────────────────
// To add a new exam here AND in the user mock dashboard, edit:
//   client/src/utils/prebuiltExamCatalog.js
const PREBUILT_EXAMS = PREBUILT_EXAM_CATALOG;

// ─── Prebuilt Exam Picker Modal ───────────────────────────────────────────────
function PrebuiltExamPickerModal({ onSelect, onClose }) {
  return (
    <div className={css.pickerOverlay} onClick={onClose}>
      <div className={`${css.pickerModal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <div className={css.pickerHeader}>
          <h2 className={css.pickerTitle}>Select Prebuilt Mock Exam</h2>
          <button className={css.closeBtn} onClick={onClose}>✕</button>
        </div>
        <p className={css.pickerSub}>
          Choose a platform mock exam. Students assigned this exam will be directed to that exam on the platform.
        </p>
        <div className={css.pickerGrid}>
          {PREBUILT_EXAMS.map(ex => (
            <div key={ex.slug} className={css.pickerCard}>
              <div className={css.pickerCardName}>{ex.examName}</div>
              <div className={css.pickerCardDesc}>{ex.description}</div>
              <button className={css.pickerSelectBtn} type="button" onClick={() => onSelect(ex)}>Select</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Custom Date-Time Picker ─────────────────────────────────────────────────
function DateTimePicker({ value, onChange, disabled, placeholder, min }) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(null);
  const [viewMonth, setViewMonth] = useState(null);
  const wrapRef = useRef(null);

  const parsed = value ? new Date(value) : null;
  const selYear  = parsed ? parsed.getFullYear() : null;
  const selMonth = parsed ? parsed.getMonth()    : null;
  const selDay   = parsed ? parsed.getDate()     : null;
  const selHour  = parsed ? String(parsed.getHours()).padStart(2, '0')   : '08';
  const selMin   = parsed ? String(parsed.getMinutes()).padStart(2, '0') : '00';

  // Earliest selectable instant (e.g. the exam's start date, when picking the end date)
  const parsedMin = min ? new Date(min) : null;
  const minDate = parsedMin && Number.isFinite(parsedMin.getTime()) ? new Date(parsedMin) : null;
  if (minDate) {
    const hasSubMinute = minDate.getSeconds() > 0 || minDate.getMilliseconds() > 0;
    const roundedMinute = Math.ceil((minDate.getMinutes() + (hasSubMinute ? 1 : 0)) / 5) * 5;
    minDate.setSeconds(0, 0);
    minDate.setMinutes(roundedMinute);
  }
  const isMinDaySelected = !!(minDate && selYear === minDate.getFullYear() && selMonth === minDate.getMonth() && selDay === minDate.getDate());
  const minHour   = isMinDaySelected ? minDate.getHours()   : -1;
  const minMinute = isMinDaySelected ? minDate.getMinutes() : -1;

  useEffect(() => {
    if (open) {
      const d = parsed || new Date();
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const DAY_NAMES = ['Su','Mo','Tu','We','Th','Fr','Sa'];

  const prevMonth = () => setViewMonth(m => {
    if (m === 0) { setViewYear(y => y - 1); return 11; }
    return m - 1;
  });
  const nextMonth = () => setViewMonth(m => {
    if (m === 11) { setViewYear(y => y + 1); return 0; }
    return m + 1;
  });

  const emit = (year, month, day, hh, mm) => {
    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${hh}:${mm}`;
    onChange({ target: { value: iso } });
  };

  // Rounds a time up to the next 5-minute mark so it lands on an available minute option
  const clampToMin = (hh, mm, dayMatchesMin) => {
    if (!minDate || !dayMatchesMin) return [hh, mm];
    const h = Number(hh), m = Number(mm);
    if (h > minDate.getHours() || (h === minDate.getHours() && m >= minDate.getMinutes())) return [hh, mm];
    let roundedMin = Math.ceil(minDate.getMinutes() / 5) * 5;
    let nextHour = minDate.getHours();
    if (roundedMin >= 60) { roundedMin = 0; nextHour += 1; }
    return [String(nextHour).padStart(2, '0'), String(roundedMin).padStart(2, '0')];
  };

  const handleDayClick = (day) => {
    if (minDate && new Date(viewYear, viewMonth, day, 23, 59, 59, 999) < minDate) return; // before the earliest allowed instant
    let hh = parsed ? String(parsed.getHours()).padStart(2, '0')   : '08';
    let mm = parsed ? String(parsed.getMinutes()).padStart(2, '0') : '00';
    const dayMatchesMin = !!(minDate && viewYear === minDate.getFullYear() && viewMonth === minDate.getMonth() && day === minDate.getDate());
    [hh, mm] = clampToMin(hh, mm, dayMatchesMin);
    emit(viewYear, viewMonth, day, hh, mm);
  };

  const handleHourChange = (e) => {
    if (!parsed) return;
    const [hh, mm] = clampToMin(e.target.value, String(parsed.getMinutes()).padStart(2, '0'), isMinDaySelected);
    emit(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), hh, mm);
  };

  const handleMinChange = (e) => {
    if (!parsed) return;
    const [hh, mm] = clampToMin(String(parsed.getHours()).padStart(2, '0'), e.target.value, isMinDaySelected);
    emit(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), hh, mm);
  };

  const today = new Date();
  const firstDay    = viewYear != null ? new Date(viewYear, viewMonth, 1).getDay()         : 0;
  const daysInMonth = viewYear != null ? new Date(viewYear, viewMonth + 1, 0).getDate()    : 0;
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isToday    = (day) => viewYear === today.getFullYear() && viewMonth === today.getMonth() && day === today.getDate();
  const isSelected = (day) => selYear === viewYear && selMonth === viewMonth && selDay === day;
  const isDayDisabled = (day) => !!(minDate && new Date(viewYear, viewMonth, day, 23, 59, 59, 999) < minDate);

  const formatDisplay = () => {
    if (!parsed) return placeholder || 'Select date & time';
    return parsed.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={css.dtpWrap} ref={wrapRef}>
      <button
        type="button"
        className={[css.dtpTrigger, disabled ? css.dtpTriggerDisabled : ''].filter(Boolean).join(' ')}
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
      >
        <span style={{ flex: 1 }} className={parsed ? undefined : css.dtpPlaceholder}>{formatDisplay()}</span>
        <span className={css.dtpIcon}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        </span>
      </button>
      {open && !disabled && (
        <div className={css.dtpPopup}>
          <div className={css.dtpHeader}>
            <button type="button" className={css.dtpNavBtn} onClick={prevMonth}>‹</button>
            <span className={css.dtpMonthLabel}>{MONTHS[viewMonth]} {viewYear}</span>
            <button type="button" className={css.dtpNavBtn} onClick={nextMonth}>›</button>
          </div>
          <div className={css.dtpDayLabels}>
            {DAY_NAMES.map(d => <span key={d}>{d}</span>)}
          </div>
          <div className={css.dtpGrid}>
            {cells.map((day, i) =>
              day === null
                ? <span key={`e${i}`} />
                : (
                  <button
                    type="button"
                    key={day}
                    className={[
                      css.dtpDay,
                      isSelected(day) ? css.dtpDaySelected : '',
                      isToday(day) && !isSelected(day) ? css.dtpDayToday : '',
                      isDayDisabled(day) ? css.dtpDayDisabled : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleDayClick(day)}
                    disabled={isDayDisabled(day)}
                  >
                    {day}
                  </button>
                )
            )}
          </div>
          <div className={css.dtpTime}>
            <span className={css.dtpTimeLabel}>Time</span>
            <select className={css.dtpTimeSelect} value={selHour} onChange={handleHourChange} disabled={!parsed}>
              {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => (
                <option key={h} value={h} disabled={isMinDaySelected && Number(h) < minHour}>{h}</option>
              ))}
            </select>
            <span className={css.dtpTimeSep}>:</span>
            <select className={css.dtpTimeSelect} value={selMin} onChange={handleMinChange} disabled={!parsed}>
              {['00','05','10','15','20','25','30','35','40','45','50','55'].map(m => (
                <option key={m} value={m} disabled={isMinDaySelected && Number(selHour) === minHour && Number(m) < minMinute}>{m}</option>
              ))}
            </select>
          </div>
          <div className={css.dtpFooter}>
            <button type="button" className={css.dtpDoneBtn} onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Create / Edit Exam Modal ─────────────────────────────────────────────────
// Converts a stored UTC ISO timestamp into a "YYYY-MM-DDTHH:mm" string that
// represents the SAME instant in the current user's local timezone, matching
// the format the DateTimePicker widget reads/writes (which is built purely
// from local Date getters). Using toISOString() here would return the UTC
// wall-clock instead of the local one, shifting displayed times by the
// viewer's UTC offset (e.g. showing 1 hour early for GMT+1 users).
function toDatetimeLocal(d) {
  if (!d) return '';
  try {
    const date = new Date(d);
    if (isNaN(date)) return '';
    const pad = n => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  } catch { return ''; }
}

// Converts a local "YYYY-MM-DDTHH:mm" string (as produced by the
// DateTimePicker, with no timezone offset) into an absolute UTC ISO string
// for the server. `new Date(localStr)` parses the string using the browser's
// local timezone, so this preserves the exact instant the user picked
// regardless of server timezone.
function localDatetimeToISO(localStr) {
  if (!localStr) return null;
  const date = new Date(localStr);
  return isNaN(date) ? null : date.toISOString();
}

function ExamModal({ exam, subjects, classes, aiEnabled, school, onClose, onSaved, onDraftSaved, initialExamMode = 'cbt' }) {
  const isEdit = !!exam;
  const isMultiSubject = !!exam?.isMultiSubject;
  const initialMode = exam?.examMode || initialExamMode;
  const isManualMode = ['manual', 'manual_scores'].includes(initialMode);
  const isScheduled = isEdit && (exam?.status === 'scheduled' || exam?.status === 'closed');
  const hasStarted = isEdit && isScheduled && exam?.status !== 'closed' &&
    exam?.settings?.startDate && new Date(exam.settings.startDate) <= new Date();
  // Autosave only writes drafts; scheduled and pending exams are saved explicitly.
  const autosaveEnabled = !isEdit || exam?.status === 'draft';
  const requiresApproval = !!school?.controlSettings?.questionsApprovalRequired;
  const scheduleLabel = requiresApproval ? 'Submit for Approval' : isManualMode ? 'Add Exam' : 'Schedule Exam';
  const schedulingLabel = requiresApproval ? 'Submitting…' : isManualMode ? 'Adding…' : 'Scheduling…';

  // Ref for scrolling to the bottom of the question list after adding questions
  const questionsEndRef = React.useRef(null);
  const [scrollToBottom, setScrollToBottom] = React.useState(false);

  React.useEffect(() => {
    if (scrollToBottom && questionsEndRef.current) {
      questionsEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setScrollToBottom(false);
    }
  }, [scrollToBottom]);

  const [form, setForm] = useState({
    title: exam?.title || '',
    subjectId: exam?.subjectId?._id || exam?.subjectId || '',
    classId: exam?.classId?._id || exam?.classId || '',
    classArm: exam?.classArm || '',
    instructions: exam?.instructions || '',
    settings: {
      duration: exam?.settings?.duration || 60,
      passmark: exam?.settings?.passmark || 50,
      totalScore: exam?.settings?.totalScore || '',
      shuffleQuestions: exam?.settings?.shuffleQuestions || false,
      shuffleOptions: exam?.settings?.shuffleOptions || false,
      maxAttempts: exam?.settings?.maxAttempts || 1,
      resultVisibility: exam?.settings?.resultVisibility || 'immediate',
      startDate: toDatetimeLocal(exam?.settings?.startDate),
      endDate: toDatetimeLocal(exam?.settings?.endDate),
      term: exam?.settings?.term || school?.currentTerm || '',
      session: exam?.settings?.session || school?.academicSession || '',
      proctoring: {
        enabled:              exam?.settings?.proctoring?.enabled !== false,  // default true
        requireFullscreen:    exam?.settings?.proctoring?.requireFullscreen    ?? true,
        maxViolations:        exam?.settings?.proctoring?.maxViolations        ?? 20,
        blockOnMaxViolations: exam?.settings?.proctoring?.blockOnMaxViolations ?? true,
      },
    },
    source: exam?.source || 'internal',
    prebuiltConfig: exam?.prebuiltConfig || null,
    questions: exam?.questions || [],
    examCategory: isMultiSubject ? 'test' : (exam?.examCategory || 'test'),
    examMode: exam?.examMode || initialExamMode,
  });
  // Equal marks: on by default; restored from saved settings when editing
  const [equalMarks, setEqualMarks] = useState(() => exam?.settings?.equalMarks !== false);
  const [saving, setSaving] = useState(false);
  const [autosaveState, setAutosaveState] = useState('idle');
  const [error, setError] = useState('');
  const [activePane, setActivePane] = useState('details');
  const [aiTopic, setAiTopic] = useState('');
  const [aiCount, setAiCount] = useState(5);
  const [aiDiff, setAiDiff] = useState('medium');
  const [aiQType, setAiQType] = useState('mcq');
  const [aiMultiSelect, setAiMultiSelect] = useState(false);
  const [showAllTopics, setShowAllTopics] = useState(false);
  const [recentlyPickedTopic, setRecentlyPickedTopic] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  // Multi-topic state (used in AI panel multi-select mode)
  const [currGenSelectedTopics, setCurrGenSelectedTopics] = useState([]);
  const [currGenCount, setCurrGenCount] = useState(2);
  const [aiError, setAiError] = useState('');
  const [aiSuccess, setAiSuccess] = useState('');
  const [showPrebuiltPicker, setShowPrebuiltPicker] = useState(false);
  // Prebuilt generate flow
  const [savedExamId, setSavedExamIdState] = useState(isEdit ? exam._id : null);
  const savedExamIdRef = useRef(isEdit ? exam._id : null);
  const setSavedExamId = id => { savedExamIdRef.current = id; setSavedExamIdState(id); };
  const autosaveTimerRef = useRef(null);
  const persistedRef = useRef(false);
  const autosaveQueueRef = useRef(Promise.resolve());
  const lastAutosaveSnapshotRef = useRef(null);
  const latestAutosaveRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [genStatus, setGenStatus] = useState('');
  const [prebuiltSubject, setPrebuiltSubject] = useState(exam?.prebuiltConfig?.subject || '');
  const [questionsReady, setQuestionsReady] = useState(isEdit && !!(exam?.questions?.length));
  // Upload questions from document
  const [uploadQModal, setUploadQModal] = useState(false);
  const [uploadQLoading, setUploadQLoading] = useState(false);
  const [uploadQError, setUploadQError] = useState('');
  const [uploadQType, setUploadQType] = useState('mcq');
  const [uploadQFileName, setUploadQFileName] = useState('');
  const uploadQFileRef = React.useRef(null);
  const uploadQFileDataRef = React.useRef(null);

  // Computed
  const isPrebuilt = form.source === 'external' && !!form.prebuiltConfig;
  const prebuiltEntry = isPrebuilt ? PREBUILT_EXAMS.find(e => e.slug === form.prebuiltConfig?.slug) : null;
  const [curriculumCoverage, setCurriculumCoverage] = useState(null);
  const [curriculumCoverageLoading, setCurriculumCoverageLoading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!form.subjectId || !form.classId) {
      setCurriculumCoverage(null);
      return undefined;
    }
    setCurriculumCoverageLoading(true);
    teacherFetch(`/teacher/curriculum/coverage/${form.subjectId}/${form.classId}`)
      .then(({ ok, data }) => {
        if (active) setCurriculumCoverage(ok ? data.coverage : null);
      })
      .catch(() => {
        if (active) setCurriculumCoverage(null);
      })
      .finally(() => {
        if (active) setCurriculumCoverageLoading(false);
      });
    return () => { active = false; };
  }, [form.subjectId, form.classId]);

  const setField = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: val }));
  };
  const setSetting = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, settings: { ...f.settings, [k]: val } }));
  };
  // Keeps the end date valid whenever the start date moves past it, instead of
  // silently leaving an impossible date range for the student to be caught by later
  const handleStartDateChange = (e) => {
    const newStart = e.target.value;
    setForm(f => {
      const currentEnd = f.settings.endDate;
      const needsBump = newStart && currentEnd && new Date(currentEnd) <= new Date(newStart);
      const nextEnd = needsBump ? toDatetimeLocal(new Date(new Date(newStart).getTime() + 60 * 60 * 1000)) : currentEnd;
      return { ...f, settings: { ...f.settings, startDate: newStart, endDate: nextEnd } };
    });
  };
  const setProctoringField = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({
      ...f,
      settings: {
        ...f.settings,
        proctoring: { ...f.settings.proctoring, [k]: val },
      },
    }));
  };

  const addBlankQuestion = () => {
    setForm(f => ({
      ...f,
      questions: [...f.questions, { question_text: '', options: ['', '', '', ''], correct_answer: '', topic: '', difficulty: '', instructions: '', questionType: 'mcq', score: 1, marking_guide: '', model_answer: '' }],
    }));
    setAiSuccess('Blank question added successfully.');
    setAiError('');
    setScrollToBottom(true);
    setTimeout(() => setAiSuccess(''), 3000);
  };

  const handleUploadQuestions = async () => {
    const file = uploadQFileDataRef.current;
    if (!file) { setUploadQError('Please select a file first.'); return; }
    if (!form.title.trim()) { setUploadQError('Enter an exam title first, then upload questions.'); return; }
    if (!(await flushDraft())) { setUploadQError('Could not save the draft before uploading questions.'); return; }
    setUploadQLoading(true);
    setUploadQError('');
    try {
      let examId = savedExamIdRef.current || (isEdit ? exam._id : null);
      if (!examId) {
        if (!form.title.trim()) { setUploadQError('Enter an exam title first, then upload questions.'); setUploadQLoading(false); return; }
        const initPayload = { ...buildPayload(), questions: buildPayload().questions.filter(q => q.question_text?.trim()) };
        const { ok: initOk, data: initData } = await teacherFetch('/teacher/exams', { method: 'POST', body: JSON.stringify(initPayload) });
        if (!initOk) { setUploadQError(initData.error || 'Could not save exam before uploading.'); setUploadQLoading(false); return; }
        examId = initData.exam._id;
        setSavedExamId(examId);
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('questionType', uploadQType);
      const token = localStorage.getItem('school-token');
      const res = await fetch(`/teacher/exams/${examId}/upload-questions`, {
        method: 'POST',
        headers: { 'x-access-token': token || '' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok || !data.questions) {
        setUploadQError(data.error || 'Upload failed. Please try again.');
        return;
      }
      setForm(f => ({ ...f, questions: [...f.questions, ...data.questions] }));
      setActivePane('questions');
      setUploadQModal(false);
      setUploadQFileName('');
      uploadQFileDataRef.current = null;
      setAiSuccess(`${data.questions.length} question${data.questions.length !== 1 ? 's' : ''} extracted and added.`);
      setScrollToBottom(true);
      setTimeout(() => setAiSuccess(''), 5000);
    } catch { setUploadQError('Connection error. Please try again.'); }
    finally { setUploadQLoading(false); }
  };

  // ── Score helpers ──────────────────────────────────────────────────────────
  const totalScoreNum = Number(form.settings.totalScore) || 0;
  // Per-question auto mark when equal marks is ON
  const autoScore = totalScoreNum > 0 && form.questions.length > 0
    ? Math.round((totalScoreNum / form.questions.length) * 100) / 100
    : 0;
  // Running sum of manually-set question scores (when equal marks is OFF)
  const scoreSumManual = form.questions.reduce((s, q) => s + (Number(q.score) || 0), 0);
  const scoreSumManualRounded = Math.round(scoreSumManual * 100) / 100;

  // Keep each theory question's displayed marking_guide numbers summing to
  // its actual score (equal-marks auto-score, or its manual score) so AI-
  // generated guides look correct immediately in the builder, not only after
  // a save+reload round-trip (backend reconciles again on save as a safety net).
  const questionsScoreSig = form.questions.map(q => q.score).join(',');
  useEffect(() => {
    setForm(f => {
      const reconciled = reconcileQuestionsForDisplay(f.questions, (i) => (
        equalMarks
          ? (i === 0 ? Math.round((autoScore + Math.round((totalScoreNum - autoScore * f.questions.length) * 100) / 100) * 100) / 100 : autoScore)
          : (Number(f.questions[i]?.score) || 0)
      ));
      return reconciled === f.questions ? f : { ...f, questions: reconciled };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equalMarks, totalScoreNum, form.questions.length, questionsScoreSig]);

  // Toggle handler: pre-fill per-question scores when switching to manual mode
  const handleEqualMarksToggle = (checked) => {
    if (!checked && totalScoreNum > 0 && form.questions.length > 0) {
      const base = Math.round((totalScoreNum / form.questions.length) * 100) / 100;
      const remainder = Math.round((totalScoreNum - base * form.questions.length) * 100) / 100;
      setForm(f => ({
        ...f,
        questions: f.questions.map((q, i) => ({
          ...q,
          score: i === 0 ? Math.round((base + remainder) * 100) / 100 : base,
        })),
      }));
    }
    setEqualMarks(checked);
  };

  // Build the payload to send to the server, embedding computed scores into questions
  const buildPayload = (extraOverrides = {}) => {
    // A scheduled exam never receives blank rows; equal marks are rebalanced over the rows sent.
    const sendable = isScheduled ? form.questions.filter(q => q.question_text?.trim()) : form.questions;
    const sendableScore = totalScoreNum > 0 && sendable.length > 0 ? Math.round((totalScoreNum / sendable.length) * 100) / 100 : 0;
    const questionsWithScores = sendable.map((q, i) => ({
      ...q,
      score: equalMarks
        ? (i === 0
          ? Math.round((sendableScore + Math.round((totalScoreNum - sendableScore * sendable.length) * 100) / 100) * 100) / 100
          : sendableScore)
        : (Number(q.score) || 0),
    }));
    return {
      ...form,
      questions: questionsWithScores,
      settings: {
        ...form.settings,
        proctoring: {
          ...form.settings.proctoring,
          enabled: form.examMode === 'cbt' ? form.settings.proctoring.enabled : false,
        },
        equalMarks,
        startDate: localDatetimeToISO(form.settings.startDate),
        endDate: localDatetimeToISO(form.settings.endDate),
      },
      examCategory: isMultiSubject ? 'test' : form.examCategory,
      examMode: ['manual', 'manual_scores'].includes(form.examMode) ? form.examMode : 'cbt',
      ...extraOverrides,
    };
  };

  const queueDraftSave = (payload, snapshot) => {
    const save = async () => {
      if (!payload.title?.trim() && !savedExamIdRef.current) {
        setAutosaveState('error');
        setError('Add an exam title to save this draft.');
        return { ok: false };
      }
      setAutosaveState('saving');
      const examId = savedExamIdRef.current || (isEdit ? exam._id : null);
      const savePayload = payload;
      try {
        const { ok, data } = await teacherFetch(examId ? `/teacher/exams/${examId}` : '/teacher/exams', {
          method: examId ? 'PATCH' : 'POST',
          headers: { 'x-draft-autosave': '1' },
          body: JSON.stringify(savePayload),
        });
        if (!ok) {
          setAutosaveState('error');
          setError(data?.error || 'Draft could not be saved. Your changes are still here.');
          return { ok: false };
        }
        if (!examId) setSavedExamId(data.exam._id);
        persistedRef.current = true;
        lastAutosaveSnapshotRef.current = snapshot;
        setAutosaveState('saved');
        setError('');
        if (!examId) onDraftSaved?.();
        return { ok: true, exam: data.exam };
      } catch {
        setAutosaveState('error');
        setError('Draft could not be saved. Check your connection and retry.');
        return { ok: false };
      }
    };
    const queuedSave = autosaveQueueRef.current.catch(() => {}).then(save);
    autosaveQueueRef.current = queuedSave;
    return queuedSave;
  };

  useEffect(() => {
    const payload = buildPayload({ prebuiltConfig: form.prebuiltConfig ? { ...form.prebuiltConfig, subject: prebuiltSubject || undefined } : null });
    const snapshot = JSON.stringify(payload);
    latestAutosaveRef.current = { payload, snapshot };
    if (lastAutosaveSnapshotRef.current === null) {
      lastAutosaveSnapshotRef.current = snapshot;
      return undefined;
    }
    if (!autosaveEnabled || snapshot === lastAutosaveSnapshotRef.current) return undefined;
    clearTimeout(autosaveTimerRef.current);
    setAutosaveState('pending');
    autosaveTimerRef.current = setTimeout(() => queueDraftSave(payload, snapshot), 900);
    return () => clearTimeout(autosaveTimerRef.current);
    // buildPayload intentionally snapshots form, score, and prebuilt state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, equalMarks, prebuiltSubject, autosaveEnabled]);

  useEffect(() => () => clearTimeout(autosaveTimerRef.current), []);

  const flushDraft = async () => {
    clearTimeout(autosaveTimerRef.current);
    if (!autosaveEnabled) return true;
    const payload = buildPayload({ prebuiltConfig: form.prebuiltConfig ? { ...form.prebuiltConfig, subject: prebuiltSubject || undefined } : null });
    const snapshot = JSON.stringify(payload);
    latestAutosaveRef.current = { payload, snapshot };
    if (snapshot !== lastAutosaveSnapshotRef.current) {
      const result = await queueDraftSave(payload, snapshot);
      return result.ok;
    }
    await autosaveQueueRef.current.catch(() => {});
    return lastAutosaveSnapshotRef.current === snapshot;
  };

  const handleClose = async () => {
    if (!autosaveEnabled) {
      const readOnly = hasStarted || exam?.status === 'closed';
      const payload = buildPayload({ prebuiltConfig: form.prebuiltConfig ? { ...form.prebuiltConfig, subject: prebuiltSubject || undefined } : null });
      const hasUnsavedChanges = !readOnly && JSON.stringify(payload) !== lastAutosaveSnapshotRef.current;
      if (hasUnsavedChanges && !window.confirm('You have unsaved changes. Discard them and close?')) return;
      onClose();
      return;
    }
    if (!savedExamIdRef.current && !form.title.trim()) {
      const payload = buildPayload({ prebuiltConfig: form.prebuiltConfig ? { ...form.prebuiltConfig, subject: prebuiltSubject || undefined } : null });
      const hasUnsavedChanges = JSON.stringify(payload) !== lastAutosaveSnapshotRef.current;
      if (!hasUnsavedChanges || window.confirm('A title is required to save this draft. Discard these unsaved changes?')) onClose();
      return;
    }
    if (!(await flushDraft()) && !window.confirm('Your latest changes could not be saved. Close and discard them?')) return;
    if (persistedRef.current) onDraftSaved?.();
    onClose();
  };

  const changeQuestion = (i, key, val) => {
    setForm(f => {
      const qs = [...f.questions];
      if (key === '__replace__') {
        // Full question replacement (used by MCQ/Theory converter)
        qs[i] = { ...qs[i], ...val, question_number: qs[i].question_number };
      } else {
        qs[i] = { ...qs[i], [key]: val };
      }
      return { ...f, questions: qs };
    });
  };

  const removeQuestion = (i) => {
    setForm(f => ({ ...f, questions: f.questions.filter((_, idx) => idx !== i) }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.settings.totalScore || Number(form.settings.totalScore) <= 0) { setError('Total score is required'); return; }
    if (!(await flushDraft())) return;
    setSaving(true); setError('');
    try {
      const existingId = savedExamIdRef.current || (isEdit ? exam._id : null);
      const method = existingId ? 'PATCH' : 'POST';
      const url = existingId ? `/teacher/exams/${existingId}` : '/teacher/exams';
      const payload = buildPayload();
      const { ok, data } = await teacherFetch(url, { method, body: JSON.stringify(payload) });
      if (ok) { onSaved(data.exam); }
      else setError(data.error || 'Failed to save exam');
    } catch { setError('Connection error'); }
    finally { setSaving(false); }
  };

  // ── Generate exam from prebuilt ──────────────────────────────────────────
  const handleGenerate = async (regenerate = false) => {
    if (!form.title.trim()) { setGenError('Title is required'); return; }
    if (prebuiltEntry?.subjects?.length > 0 && !prebuiltSubject) {
      setGenError(`Please select a subject for ${form.prebuiltConfig?.examName} before generating.`);
      return;
    }
    if (regenerate && !window.confirm(`Replace the ${form.questions.length} existing question(s) with a freshly generated set?`)) return;
    if (!(await flushDraft())) { setGenError('Could not save the draft before generating questions.'); return; }
    setGenerating(true); setGenError(''); setError('');
    // Cycle through progress messages while the request is in-flight
    const _progressMsgs = [
      'Loading exam bank…',
      'Selecting questions…',
      'Preparing question set…',
      'Formatting questions…',
      'Almost done…',
    ];
    let _msgIdx = 0;
    setGenStatus(_progressMsgs[0]);
    const _progressTimer = setInterval(() => {
      _msgIdx = Math.min(_msgIdx + 1, _progressMsgs.length - 1);
      setGenStatus(_progressMsgs[_msgIdx]);
    }, 1800);
    try {
      let examId = savedExamIdRef.current;
      const payload = buildPayload({ prebuiltConfig: { ...form.prebuiltConfig, subject: prebuiltSubject || undefined } });
      if (!examId) {
        const { ok, data } = await teacherFetch('/teacher/exams', { method: 'POST', body: JSON.stringify(payload) });
        if (!ok) { setGenError(data.error || 'Failed to save exam'); return; }
        examId = data.exam._id;
        setSavedExamId(examId);
      } else {
        await teacherFetch(`/teacher/exams/${examId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      }
      const { ok: aiOk, data: aiData } = await teacherFetch(`/teacher/exams/${examId}/prebuilt-generate`, {
        method: 'POST',
        body: JSON.stringify({
          examName: form.prebuiltConfig.examName,
          subject: prebuiltSubject,
          questionCount: 40,
        }),
      });
      if (!aiOk) { setGenError(aiData.error || 'Generation failed — check your school plan or try again'); return; }
      setForm(f => ({ ...f, questions: aiData.questions || [] }));
      setQuestionsReady(true);
      setActivePane('questions');
    } catch { setGenError('Connection error'); }
    finally { clearInterval(_progressTimer); setGenStatus(''); setGenerating(false); }
  };

  const handleSaveDraft = async () => {
    if (!savedExamId) return;
    setSaving(true); setError('');
    try {
      const payload = buildPayload({ prebuiltConfig: form.prebuiltConfig ? { ...form.prebuiltConfig, subject: prebuiltSubject || undefined } : null });
      const { ok, data } = await teacherFetch(`/teacher/exams/${savedExamId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      if (ok) onSaved(data.exam);
      else setError(data.error || 'Save failed');
    } catch { setError('Connection error'); }
    finally { setSaving(false); }
  };

  // Schedule = publish + assign. Used from both Details pane and Prebuilt questions pane.
  const handleSchedule = async (overrideId) => {
    if (!form.title.trim())           { setError('Title is required');           return; }
    if (!form.subjectId)              { setError('Subject is required');         return; }
    if (!form.classId)                { setError('Class is required');           return; }
    if (!form.settings.startDate) { setError('Start date is required'); return; }
    if (!form.settings.endDate)   { setError('End date is required'); return; }
    if (form.settings.startDate && form.settings.endDate &&
        new Date(form.settings.endDate) <= new Date(form.settings.startDate)) {
      setError('End date must be after the start date'); return;
    }
    if (!form.settings.totalScore || Number(form.settings.totalScore) <= 0) {
      setError('Total score is required'); return;
    }
    if (!(await flushDraft())) return;
    setSaving(true); setError('');
    try {
      let examId = overrideId || savedExamIdRef.current || (isEdit ? exam._id : null);
      const payload = buildPayload({ prebuiltConfig: form.prebuiltConfig ? { ...form.prebuiltConfig, subject: prebuiltSubject || undefined } : null });
      // 1. Save / update exam
      if (!examId) {
        const { ok, data } = await teacherFetch('/teacher/exams', { method: 'POST', body: JSON.stringify(payload) });
        if (!ok) { setError(data.error || 'Failed to save exam'); return; }
        examId = data.exam._id;
        setSavedExamId(examId);
      } else {
        const { ok, data } = await teacherFetch(`/teacher/exams/${examId}`, { method: 'PATCH', body: JSON.stringify(payload) });
        if (!ok) { setError(data.error || 'Failed to save exam'); return; }
      }
      // 2. Schedule (sets status → 'scheduled', startDate, endDate)
      const { ok: schOk, data: schData } = await teacherFetch(`/teacher/exams/${examId}/publish`, {
        method: 'POST',
        body: JSON.stringify({
          scheduledAt: localDatetimeToISO(form.settings.startDate),
          endDate: localDatetimeToISO(form.settings.endDate),
        }),
      });
      if (!schOk) { setError(schData.error || 'Scheduling failed'); return; }
      if (schData.exam.status === 'pending_approval') {
        onSaved(schData.exam);
        return;
      }
      // 3. Assign students (send emails); failure is non-fatal
      await teacherFetch(`/teacher/exams/${examId}/assign`, {
        method: 'POST',
        body: JSON.stringify({ classId: form.classId, classArm: form.classArm }),
      }).catch(e => console.warn('[schedule] assign error:', e));
      onSaved(schData.exam);
    } catch { setError('Connection error'); }
    finally { setSaving(false); }
  };

  const handleAISuggest = async () => {
    if (!aiEnabled) return;
    const isMulti       = aiMultiSelect;
    const isSingleMixed = !aiMultiSelect && aiQType === 'mixed';
    const useMultiEndpoint = isMulti || isSingleMixed;

    if (isMulti) {
      if (currGenSelectedTopics.length === 0) { setAiError('Click topics below to select at least one.'); return; }
    } else if (!aiTopic.trim()) {
      setAiError('Enter a topic first'); return;
    }
    if (!form.title.trim()) { setAiError('Enter an exam title first, then generate questions.'); return; }
    if (!(await flushDraft())) { setAiError('Could not save the draft before generating questions.'); return; }

    setAiLoading(true); setAiError(''); setAiSuccess('');
    try {
      let examId = savedExamIdRef.current || (isEdit ? exam._id : null);
      if (!examId) {
        if (!form.title.trim()) { setAiError('Enter an exam title first, then generate questions.'); return; }
        const initPayload = {
          ...buildPayload(),
          questions: buildPayload().questions.filter(q => q.question_text?.trim()),
        };
        const { ok, data } = await teacherFetch('/teacher/exams', { method: 'POST', body: JSON.stringify(initPayload) });
        if (!ok) { setAiError(data.error || 'Could not save exam — check the title and try again'); return; }
        examId = data.exam._id;
        setSavedExamId(examId);
      }

      let ok, data;
      if (useMultiEndpoint) {
        const topics       = isMulti ? currGenSelectedTopics : [aiTopic.trim()];
        const countPerTopic = isMulti ? Number(currGenCount) : Number(aiCount);
        ({ ok, data } = await teacherFetch(`/teacher/exams/${examId}/curriculum-generate`, {
          method: 'POST',
          body: JSON.stringify({ topics, countPerTopic, questionType: aiQType }),
        }));
      } else {
        ({ ok, data } = await teacherFetch(`/teacher/exams/${examId}/ai-suggest`, {
          method: 'POST',
          body: JSON.stringify({ topic: aiTopic, count: Number(aiCount), difficulty: aiDiff, questionType: aiQType }),
        }));
      }

      if (ok && Array.isArray(data.questions)) {
        // For theory questions AI returns model_answer/marking_guide; normalise correct_answer too
        const normalised = data.questions.map(q =>
          q.questionType === 'theory'
            ? { ...q, correct_answer: q.model_answer || q.correct_answer || '', marking_guide: ensureMarkingGuideMarks(q.marking_guide || '') }
            : { ...q, marking_guide: '', model_answer: '' }
        );
        setForm(f => ({ ...f, questions: [...f.questions, ...normalised] }));
        setActivePane('questions');
        setAiSuccess(`${data.questions.length} question${data.questions.length !== 1 ? 's' : ''} added successfully.`);
        setScrollToBottom(true);
        setTimeout(() => setAiSuccess(''), 4000);
      } else {
        setAiError(data.error || 'AI generation failed');
      }
    } catch { setAiError('Connection error'); }
    finally { setAiLoading(false); }
  };

  return (
    <>
    <div className={css.modalOverlay} onClick={event => { if (event.target === event.currentTarget) handleClose(); }}>
      <div className={`${css.modalLarge} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <div className={css.modalHeader}>
          <div className={css.modalTitleRow}>
            <h2 className={css.modalTitle}>{isEdit ? 'Edit Exam' : 'Create New Exam'}</h2>
            <span className={css.examModeBadge}>
              {form.examMode === 'manual_scores' ? 'Manual Score Entry' : form.examMode === 'manual' ? 'Manual + AI Script Grading' : 'Computer-Based (CBT)'}
            </span>
          </div>
          <button className={css.closeBtn} onClick={handleClose} aria-label="Close exam editor">✕</button>
        </div>

        <div className={css.modalTabs}>
          <button
            className={`${css.modalTab} ${activePane === 'details' ? css.modalTabActive : ''}`}
            onClick={() => setActivePane('details')}
          >
            Details &amp; Settings
            {isScheduled && <span className={css.tabLockBadge}>🔒 read-only</span>}
          </button>
          <button
            className={`${css.modalTab} ${activePane === 'questions' ? css.modalTabActive : ''}`}
            onClick={() => { if (isPrebuilt && !questionsReady) return; setActivePane('questions'); }}
            style={isPrebuilt && !questionsReady ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            title={isPrebuilt && !questionsReady ? 'Generate the exam first to view questions' : ''}
          >
            Questions ({form.questions.length})
            {isPrebuilt && !questionsReady && <span style={{ fontSize: '0.7rem', marginLeft: 5 }}>🔒</span>}
          </button>
        </div>

        {error && <div className={css.inlineError}>{error}</div>}

        {activePane === 'details' && (
          <div className={css.modalBody}>
            {isScheduled && (
              <div className={css.scheduledBanner}>
                {hasStarted
                  ? '🔴 This exam is currently live — all editing is locked.'
                  : '✓ This exam is scheduled. Settings are locked — only questions can be edited before the start time.'}
              </div>
            )}
            {questionsReady && !isScheduled && (
              <div className={css.genBanner}>
                ✓ {form.questions.length} questions generated — edits here are preserved. Click <strong>View Questions</strong> when ready.
              </div>
            )}
            <div className={css.formRow}>
              <label>Title *</label>
              <input className={css.formInput} value={form.title} onChange={setField('title')} placeholder="e.g. Mid-Term Chemistry Test" disabled={isScheduled} />
            </div>
            <div className={css.formGrid2}>
              <div className={css.formRow}>
                <label>Subject *</label>
                <select className={css.formSelect} value={form.subjectId} onChange={setField('subjectId')} disabled={isScheduled}>
                  <option value="">— Select subject —</option>
                  {subjects.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
              </div>
              <div className={css.formRow}>
                <label>Class *</label>
                <select className={css.formSelect} value={form.classId} onChange={setField('classId')} disabled={isScheduled}>
                  <option value="">— Select class —</option>
                  {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className={css.formRow}>
              <label>Class Arm / Stream *</label>
              <input className={css.formInput} value={form.classArm} onChange={setField('classArm')} placeholder="e.g. A, B, Science" disabled={isScheduled} />
            </div>
            <div className={css.formGrid2}>
              <div className={css.formRow}>
                <label>Start Date &amp; Time *</label>
                <DateTimePicker
                  value={form.settings.startDate}
                  onChange={handleStartDateChange}
                  disabled={isScheduled}
                  placeholder="Pick start date & time"
                  min={new Date()}
                />
              </div>
              <div className={css.formRow}>
                <label>End Date &amp; Time *</label>
                <DateTimePicker
                  value={form.settings.endDate}
                  onChange={setSetting('endDate')}
                  disabled={isScheduled}
                  placeholder="Pick end date & time"
                  min={form.settings.startDate ? new Date(new Date(form.settings.startDate).getTime() + 1) : new Date()}
                />
              </div>
            </div>
            <p className={css.hintText} style={{ marginBottom: 12 }}>
              {isManualMode ? 'Students will be added to this exam without schedule or missed-exam notifications.' : 'Students in the selected class will be assigned and notified by email when you schedule the exam.'}
            </p>
            <div className={css.formRow}>
              <label>Instructions</label>
              <textarea className={css.formTextarea} rows={3} value={form.instructions} onChange={setField('instructions')} placeholder="Instructions shown to students..." disabled={isScheduled} />
            </div>

            {/* Multi-subject exams are always Test. */}
            {!isMultiSubject && <div className={css.formRow}>
              <label>Exam Category</label>
              <div style={{ display: 'flex', gap: 14, marginTop: 6, flexWrap: 'wrap' }}>
                {[
                  { value: 'test', label: 'Test', desc: 'Informal test, not counted in report cards' },
                  { value: 'ca',   label: 'CA (Continuous Assessment)', desc: 'Counted in term report card' },
                  { value: 'exam', label: 'End-of-Term Exam', desc: 'Main exam for term report card' },
                ].map(opt => (
                  <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, cursor: (isMultiSubject && opt.value !== 'test') || isScheduled ? 'not-allowed' : 'pointer', fontSize: '0.82rem', opacity: ((isMultiSubject && opt.value !== 'test') || isScheduled) ? 0.55 : 1 }}>
                    <input
                      className="schoolRadio"
                      type="radio"
                      value={opt.value}
                      checked={form.examCategory === opt.value}
                      onChange={() => !isScheduled && !isMultiSubject && setForm(f => ({ ...f, examCategory: opt.value }))}
                      disabled={(isMultiSubject && opt.value !== 'test') || isScheduled}
                      style={{ marginTop: 2 }}
                    />
                    <span>
                      <strong>{opt.label}</strong><br />
                      <span style={{ color: '#90a4ae', fontSize: '0.75rem' }}>{opt.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>}

            {/* ── Prebuilt exam picker ── */}
            {isMultiSubject && <p className={css.hintText} style={{ margin: '4px 0 12px' }}>Multi-subject exams are set to Test by default. You cannot change their exam category for now.</p>}
            <div className={css.prebuiltPanel}>
              <div className={css.prebuiltHeader}>
                <SchoolIcon fontSize="small" style={{ color: '#5fbec5' }} />
                <span className={css.prebuiltTitle}>Assign Prebuilt Mock Exam</span>
              </div>
              <p className={css.prebuiltSub}>
                Link this assignment to one of the platform&apos;s prebuilt mock exams (UTME, WAEC, etc.). Generated exam will exactly mimic the selected exam.
              </p>
              {form.source === 'external' && form.prebuiltConfig ? (
                <div className={css.selectedPrebuilt}>
                  <div className={css.selectedPrebuiltInfo}>
                    <CheckCircleIcon fontSize="small" style={{ color: '#5fbec5', flexShrink: 0 }} />
                    <div>
                      <div className={css.selectedPrebuiltName}>{form.prebuiltConfig.examName}</div>
                      <div className={css.selectedPrebuiltDesc}>{form.prebuiltConfig.description}</div>
                    </div>
                  </div>
                  {!isScheduled && (
                    <div className={css.prebuiltActions}>
                      <button className={css.prebuiltChangeBtn} type="button" onClick={() => setShowPrebuiltPicker(true)}>Change</button>
                      <button className={css.prebuiltRemoveBtn} type="button" onClick={() => {
                        setForm(f => ({ ...f, source: 'internal', prebuiltConfig: null }));
                        setPrebuiltSubject('');
                        setQuestionsReady(false);
                      }}>Remove</button>
                    </div>
                  )}
                </div>
              ) : !isScheduled ? (
                <button className={css.prebuiltBtn} type="button" onClick={() => setShowPrebuiltPicker(true)}>
                  Browse &amp; Select Exam
                </button>
              ) : null}

              {/* ── Subject / paper picker ── */}
              {isPrebuilt && (
                <div style={{ marginTop: 14 }}>
                  <label className={css.prebuiltSubjectLabel}>
                    Exam Subject / Paper
                    {prebuiltEntry?.subjects?.length > 0 && (
                      <span style={{ color: '#e53935', marginLeft: 3 }}>*</span>
                    )}
                  </label>
                  {prebuiltEntry?.subjects?.length > 0 ? (
                    <select
                      className={css.formSelect}
                      value={prebuiltSubject}
                      onChange={e => setPrebuiltSubject(e.target.value)}
                      disabled={isScheduled}
                    >
                      <option value="">— Select a subject —</option>
                      {prebuiltEntry.subjects.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className={css.formInput}
                      value={prebuiltSubject}
                      onChange={e => setPrebuiltSubject(e.target.value)}
                      placeholder="e.g. Financial Accounting (optional)"
                      disabled={isScheduled}
                    />
                  )}
                  <p className={css.hintText} style={{ marginTop: 4 }}>
                    {prebuiltEntry?.subjects?.length > 0
                      ? `Select the subject to generate targeted ${form.prebuiltConfig?.examName} questions for your students.`
                      : `Optionally narrow questions to a specific topic within ${form.prebuiltConfig?.examName}.`}
                  </p>
                </div>
              )}
              {genError && <div className={css.inlineError} style={{ marginTop: 10 }}>{genError}</div>}
              {isPrebuilt && !aiEnabled && (
                <div className={css.premiumLockBanner}>
                  🔒 Prebuilt exam generation is a <strong>Premium</strong> feature. Contact your administrator to upgrade your school plan.
                </div>
              )}
            </div>

            <div className={css.sectionLabel}>Settings</div>
            <div className={css.formGrid3}>
              <div className={css.formRow}>
                <label>Duration (minutes)</label>
                <input className={css.formInput} type="number" min="1" value={form.settings.duration} onChange={setSetting('duration')} disabled={isScheduled} />
              </div>
              <div className={css.formRow}>
                <label>Pass Mark (%)</label>
                <input className={css.formInput} type="number" min="0" max="100" value={form.settings.passmark} onChange={setSetting('passmark')} disabled={isScheduled} />
              </div>
              <div className={css.formRow}>
                <label>Total Score *</label>
                <input className={css.formInput} type="number" min="1" value={form.settings.totalScore} onChange={setSetting('totalScore')} disabled={isScheduled} placeholder="e.g. 100" required />
              </div>
            </div>
            <div className={css.formGrid2}>
              <div className={css.formRow}>
                <label>Max Attempts</label>
                <input className={css.formInput} type="number" min="1" value={form.settings.maxAttempts} onChange={setSetting('maxAttempts')} disabled={isScheduled} />
              </div>
              <div className={css.formRow}>
                <label>Result Visibility</label>
                <select className={css.formSelect} value={form.settings.resultVisibility} onChange={setSetting('resultVisibility')} disabled={isScheduled}>
                  <option value="immediate">Immediately after submit</option>
                  <option value="after_deadline">After exam closes</option>
                  <option value="manual">Manual (teacher releases)</option>
                </select>
              </div>
            </div>
            <div className={css.checkRow}>
              <label><input className="schoolCheckbox" type="checkbox" checked={form.settings.shuffleQuestions} onChange={setSetting('shuffleQuestions')} disabled={isScheduled} /> Shuffle questions</label>
              <label><input className="schoolCheckbox" type="checkbox" checked={form.settings.shuffleOptions} onChange={setSetting('shuffleOptions')} disabled={isScheduled} /> Shuffle options</label>
            </div>

            {/* ── Proctoring ── */}
            {form.examMode === 'cbt' && <>
            <div className={css.sectionLabel} style={{ marginTop: 18 }}>Proctoring</div>
            <div style={{
              border: '1.5px solid',
              borderColor: form.settings.proctoring.enabled ? '#5fbec5' : '#e0eaec',
              borderRadius: 10,
              padding: '14px 16px',
              background: form.settings.proctoring.enabled ? 'rgba(95,190,197,0.04)' : '#fafcfc',
              transition: 'all .2s',
            }}>
              {/* Master toggle */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: isScheduled ? 'not-allowed' : 'pointer', marginBottom: 2 }}>
                <input
                  className="schoolCheckbox"
                  type="checkbox"
                  checked={form.settings.proctoring.enabled}
                  onChange={setProctoringField('enabled')}
                  disabled={isScheduled}
                  style={{ width: 16, height: 16, accentColor: '#5fbec5' }}
                />
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0c1e28' }}>
                  Enable proctoring for this exam
                </span>
              </label>
              <p style={{ margin: '2px 0 10px 26px', fontSize: '0.78rem', color: '#90a4ae', lineHeight: 1.5 }}>
                When enabled, students are monitored for suspicious activity (tab switches, copy attempts, dev tools, etc.). Violations are logged and visible to you in the exam analytics.
              </p>

              {/* Sub-options — only shown when enabled */}
              {form.settings.proctoring.enabled && (
                <div style={{ marginLeft: 26, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: isScheduled ? 'not-allowed' : 'pointer', fontSize: '0.85rem', opacity: isScheduled ? 0.55 : 1 }}>
                    <input
                      className="schoolCheckbox"
                      type="checkbox"
                      checked={form.settings.proctoring.requireFullscreen}
                      onChange={setProctoringField('requireFullscreen')}
                      disabled={isScheduled}
                      style={{ marginTop: 2, accentColor: '#5fbec5' }}
                    />
                    <span>
                      <strong style={{ color: '#0c1e28' }}>Require fullscreen mode</strong><br />
                      <span style={{ color: '#90a4ae', fontSize: '0.75rem' }}>Students must enter fullscreen before questions appear. Exiting fullscreen is logged as a violation.</span>
                    </span>
                  </label>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0c1e28', whiteSpace: 'nowrap' }}>
                      Max violations before flagging:
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      style={{ width: 64, padding: '4px 8px', borderRadius: 6, border: '1.5px solid #e0eaec', fontSize: '0.85rem' }}
                      value={form.settings.proctoring.maxViolations}
                      onChange={setProctoringField('maxViolations')}
                      disabled={isScheduled}
                    />
                  </div>

                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: isScheduled ? 'not-allowed' : 'pointer', fontSize: '0.85rem', opacity: isScheduled ? 0.55 : 1 }}>
                    <input
                      className="schoolCheckbox schoolCheckboxDanger"
                      type="checkbox"
                      checked={form.settings.proctoring.blockOnMaxViolations}
                      onChange={setProctoringField('blockOnMaxViolations')}
                      disabled={isScheduled}
                      style={{ marginTop: 2, accentColor: '#dc2626' }}
                    />
                    <span>
                      <strong style={{ color: '#dc2626' }}>Auto-submit when max violations are reached</strong><br />
                      <span style={{ color: '#90a4ae', fontSize: '0.75rem' }}>The exam will be automatically submitted if a student exceeds the limit above. Use with caution.</span>
                    </span>
                  </label>
                </div>
              )}
            </div>
            </>}


          </div>
        )}

        {activePane === 'questions' && (
          <div className={css.modalBody}>
            {/* Hard lock — exam has started */}
            {hasStarted && (
              <div className={css.startedLockBanner}>
                🔒 <strong>This exam is live.</strong> Questions cannot be edited once the start time has passed. Students are currently taking this exam.
              </div>
            )}
            {!hasStarted && (
            <>
            {/* AI Question Suggestions — single topic or multi-topic (curriculum) */}
            {(() => {
              const selectedSubject = subjects.find(s => String(s._id) === String(form.subjectId));
              const classCurriculum = (Array.isArray(selectedSubject?.curriculum) ? selectedSubject.curriculum : []).find(c => String(c.classId) === String(form.classId));
              const hasCurriculum   = !!(classCurriculum?.key);
              const currTopics      = classCurriculum?.topics || [];
              const VISIBLE = 5;
              const visibleTopics   = showAllTopics ? currTopics : currTopics.slice(0, VISIBLE);
              const selectedTopicNames = aiMultiSelect ? currGenSelectedTopics : (aiTopic ? [aiTopic] : []);
              const coveredSelectedTopics = selectedTopicNames.filter(topic => curriculumCoverage?.topics?.some(item => item.name === topic && item.covered));

              const handlePickTopic = (t) => {
                if (aiMultiSelect) {
                  setCurrGenSelectedTopics(prev =>
                    prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]
                  );
                } else {
                  setAiTopic(t);
                  setRecentlyPickedTopic(t);
                  setTimeout(() => setRecentlyPickedTopic(''), 1200);
                }
              };

              return (
                <div className={css.aiPanel}>
                  <div className={css.aiPanelHeader}>
                    {aiEnabled ? <AutoFixHighIcon fontSize="small" style={{ color: '#5fbec5' }} /> : <LockIcon fontSize="small" style={{ color: '#90a4ae' }} />}
                    <span className={css.aiPanelTitle}>AI Question Generator {!aiEnabled && <span className={css.aiLock}>(not enabled on your plan)</span>}</span>
                    {hasCurriculum && <span className={css.aiCurriculumTag}>✓ Curriculum-Grounded</span>}
                  </div>
                  {aiEnabled && (
                    <>
                      {currTopics.length > 0 && (
                        <div className={css.aiTopicsWrap}>
                          <div className={css.aiTopicsLabelRow}>
                            <span className={css.aiTopicsLabel}>From your curriculum:</span>
                            {!aiMultiSelect ? (
                              <button
                                className={css.aiTopicsMultiLink}
                                type="button"
                                onClick={() => { setAiMultiSelect(true); setCurrGenSelectedTopics([]); }}
                              >+ Select multiple</button>
                            ) : (
                              <>
                                <button
                                  className={css.aiTopicsMultiLink}
                                  type="button"
                                  onClick={() => { setAiMultiSelect(false); setCurrGenSelectedTopics([]); }}
                                >− Single topic</button>
                                {currGenSelectedTopics.length > 0 && (
                                  <button
                                    className={css.aiTopicsClearLink}
                                    type="button"
                                    onClick={() => setCurrGenSelectedTopics([])}
                                  >Clear</button>
                                )}
                              </>
                            )}
                          </div>
                          <div className={`${css.aiTopicsChips} ${showAllTopics ? css.aiTopicsChipsExpanded : ''}`}>
                            {visibleTopics.map((t, i) => {
                              const isActiveSingle = !aiMultiSelect && aiTopic === t;
                              const isActiveMulti  = aiMultiSelect && currGenSelectedTopics.includes(t);
                              const coverageTopic = curriculumCoverage?.topics?.find(item => item.name === t);
                              return (
                                <button
                                  key={i}
                                  className={`${css.aiTopicChip} ${(isActiveSingle || isActiveMulti) ? css.aiTopicChipActive : ''} ${recentlyPickedTopic === t ? css.aiTopicChipFlash : ''}`}
                                  onClick={() => handlePickTopic(t)}
                                  type="button"
                                >
                                  <span className={`${css.coverageTopicChip} ${coverageTopic?.covered ? css.coverageTopicChipCovered : css.coverageTopicChipUncovered}`}>
                                    {isActiveMulti ? '✓ ' : ''}{coverageTopic ? (coverageTopic.covered ? '✓' : '○') : (curriculumCoverageLoading ? '…' : '○')} {t}
                                    {coverageTopic?.covered && coverageTopic.questionCount > 0 && (
                                      <span className={css.coverageTopicCount}>{coverageTopic.questionCount}</span>
                                    )}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                          {currTopics.length > VISIBLE && (
                            <button className={css.aiTopicsMoreBtn} onClick={() => setShowAllTopics(v => !v)} type="button">
                              {showAllTopics ? '▲ Show less' : `▼ +${currTopics.length - VISIBLE} more topics`}
                            </button>
                          )}
                          {coveredSelectedTopics.length > 0 && (
                            <div className={css.coverageNotice} role="status">
                              {coveredSelectedTopics.length === 1 ? 'This topic' : `${coveredSelectedTopics.length} selected topics`} already {coveredSelectedTopics.length === 1 ? 'has' : 'have'} exams generated previously. This is just a heads up even though you can still generate.
                            </div>
                          )}
                        </div>
                      )}
                      <div className={css.aiInputRow}>
                        {aiMultiSelect ? (
                          <div className={css.aiMultiTopicInput}>
                            {currGenSelectedTopics.map((t, i) => (
                              <span key={i} className={css.aiMultiTopicChip}>
                                {t}
                                <button
                                  type="button"
                                  className={css.aiMultiTopicRemove}
                                  onClick={() => setCurrGenSelectedTopics(prev => prev.filter(x => x !== t))}
                                >×</button>
                              </span>
                            ))}
                            {currGenSelectedTopics.length === 0 && (
                              <span className={css.aiMultiTopicPlaceholder}>Click topics above to add…</span>
                            )}
                          </div>
                        ) : (
                          <input
                            className={css.aiInput}
                            placeholder="Topic (e.g. Photosynthesis)"
                            value={aiTopic}
                            onChange={e => setAiTopic(e.target.value)}
                          />
                        )}
                        <select className={css.aiSelect} value={aiQType} onChange={e => setAiQType(e.target.value)}>
                          <option value="mcq">MCQ</option>
                          <option value="theory">Theory</option>
                          <option value="mixed">Mixed</option>
                        </select>
                        {aiMultiSelect ? (
                          <>
                            <span className={css.aiSelectLabel}>per topic</span>
                            <select className={css.aiSelect} value={currGenCount} onChange={e => setCurrGenCount(e.target.value)}>
                              {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} Q</option>)}
                            </select>
                          </>
                        ) : (
                          <>
                            <select className={css.aiSelect} value={aiCount} onChange={e => setAiCount(e.target.value)}>
                              {[1, 2, 3, 5, 10].map(n => <option key={n} value={n}>{n} Q{n !== 1 ? 's' : ''}</option>)}
                            </select>
                            <select className={css.aiSelect} value={aiDiff} onChange={e => setAiDiff(e.target.value)}>
                              <option value="easy">Easy</option>
                              <option value="medium">Medium</option>
                              <option value="hard">Hard</option>
                            </select>
                          </>
                        )}
                        <button className={css.aiBtn} onClick={handleAISuggest} disabled={aiLoading}>
                          {aiLoading ? 'Generating…' : 'Generate'}
                        </button>
                      </div>
                      {aiMultiSelect && currGenSelectedTopics.length > 0 && (
                        <div className={css.aiMultiCount}>
                          {currGenSelectedTopics.length} topic{currGenSelectedTopics.length !== 1 ? 's' : ''} selected
                          {' · '}{currGenSelectedTopics.length * Number(currGenCount)} questions total
                        </div>
                      )}
                    </>
                  )}
                  {aiError && <div className={css.aiError}>{aiError}</div>}
                  {aiSuccess && <div className={css.aiSuccess}>{aiSuccess}</div>}
                </div>
              );
            })()}

            <div className={css.qListHeader}>
              <span>{form.questions.length} question(s)</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className={css.addQBtn} onClick={() => { setUploadQModal(true); setUploadQError(''); setUploadQFileName(''); uploadQFileDataRef.current = null; }}>
                  <UploadFileIcon fontSize="small" /> Upload questions
                </button>
                <button className={css.addQBtn} onClick={addBlankQuestion}><AddIcon fontSize="small" /> Add blank</button>
              </div>
            </div>

            {/* Equal marks toggle bar */}
            <div className={css.equalMarksBar}>
              <label className={css.toggleSwitch}>
                <input className="schoolCheckbox" type="checkbox" checked={equalMarks} onChange={e => handleEqualMarksToggle(e.target.checked)} />
                <span className={css.toggleSlider} />
              </label>
              <div>
                <div className={css.equalMarksLabel}>Equal marks per question</div>
                <div className={css.equalMarksSub}>
                  {equalMarks
                    ? `Each question: ${form.questions.length > 0 && totalScoreNum > 0 ? (totalScoreNum / form.questions.length).toFixed(2) : '—'} marks`
                    : 'Set individual marks per question below'}
                </div>
              </div>
              {!equalMarks && (
                <div className={css.scoreSumBadge} style={{ color: Math.abs(scoreSumManualRounded - totalScoreNum) < 0.01 ? '#43a047' : '#e53935' }}>
                  {scoreSumManualRounded} / {totalScoreNum} marks
                </div>
              )}
            </div>

            {form.questions.length === 0
              ? <EmptyState message="No questions yet. Add blanks or use AI above." />
              : form.questions.map((q, i) => (
                <QuestionRow
                  key={i} q={q} index={i}
                  onChange={changeQuestion} onRemove={removeQuestion}
                  equalMarks={equalMarks} autoScore={autoScore}
                  examId={savedExamId || (isEdit ? exam._id : null)}
                />
              ))
            }
            <div ref={questionsEndRef} />
            </>
            )}
          </div>
        )}

        {/* ── Upload Questions Modal ── */}
        {uploadQModal && (
          <div className={css.modalOverlay} onClick={() => setUploadQModal(false)} style={{ zIndex: 1400 }}>
            <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
              <div className={css.modalHeader}>
                <h2 className={css.modalTitle}>Upload Questions from File</h2>
                <button className={css.closeBtn} onClick={() => setUploadQModal(false)}>✕</button>
              </div>
              <p style={{ fontSize: '0.82rem', color: '#607d8b', margin: '0 0 14px' }}>
                Upload a PDF, Word document, spreadsheet, image, or plain text file containing questions.
                Text PDFs and structured spreadsheets are read directly; scans and images use AI vision.
              </p>
              <div
                style={{
                  border: '2px dashed #b2d8da', borderRadius: 10, padding: '22px 16px',
                  textAlign: 'center', cursor: 'pointer', background: '#f0fafb', marginBottom: 14,
                }}
                onClick={() => uploadQFileRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  const f = e.dataTransfer.files[0];
                  if (f) { uploadQFileDataRef.current = f; setUploadQFileName(f.name); }
                }}
              >
                <input
                  ref={uploadQFileRef}
                  type="file"
                  accept=".pdf,.txt,.docx,.doc,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tif,.tiff"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const f = e.target.files[0];
                    if (f) { uploadQFileDataRef.current = f; setUploadQFileName(f.name); }
                  }}
                />
                <UploadFileIcon style={{ fontSize: '2rem', color: '#5fbec5', marginBottom: 6 }} />
                {uploadQFileName
                  ? <div style={{ fontWeight: 700, color: '#0c7c84', fontSize: '0.88rem' }}>{uploadQFileName}</div>
                  : <><div style={{ fontWeight: 600, color: '#607d8b', fontSize: '0.88rem' }}>Click or drag &amp; drop</div>
                    <div style={{ fontSize: '0.75rem', color: '#90a4ae' }}>PDF, Word, Excel, CSV, image, or TXT - max 10 MB</div></>
                }
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#546e7a', display: 'block', marginBottom: 5 }}>Extract as</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['mcq', 'theory'].map(t => (
                    <button key={t} type="button"
                      onClick={() => setUploadQType(t)}
                      style={{
                        padding: '6px 18px', borderRadius: 8, border: '1.5px solid',
                        borderColor: uploadQType === t ? '#5fbec5' : '#dde4e8',
                        background: uploadQType === t ? 'rgba(95,190,197,0.12)' : '#fff',
                        color: uploadQType === t ? '#0c7c84' : '#607d8b',
                        fontWeight: uploadQType === t ? 700 : 500, fontSize: '0.84rem', cursor: 'pointer',
                      }}
                    >
                      {t === 'mcq' ? 'MCQ' : 'Theory'}
                    </button>
                  ))}
                </div>
              </div>
              {uploadQError && <div className={css.aiError} style={{ marginBottom: 10 }}>{uploadQError}</div>}
              <div className={css.modalFooter}>
                <button className={css.cancelBtn} onClick={() => setUploadQModal(false)}>Cancel</button>
                <button
                  className={css.saveBtn}
                  onClick={handleUploadQuestions}
                  disabled={uploadQLoading || !uploadQFileName}
                >
                  {uploadQLoading ? <><span className={css.aiSpinner} /> Extracting…</> : 'Extract Questions'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={css.modalFooter}>
          <span role="status" aria-live="polite" style={{ marginRight: 'auto', color: autosaveState === 'error' ? '#b42318' : '#607d8b', fontSize: '0.78rem' }}>
            {autosaveState === 'saving' || autosaveState === 'pending' ? 'Saving draft…' : autosaveState === 'saved' ? 'Draft saved' : autosaveState === 'error' ? 'Draft not saved' : ''}
          </span>
          {autosaveState === 'error' && <button type="button" className={css.draftBtn} onClick={() => {
            const latest = latestAutosaveRef.current || { payload: buildPayload(), snapshot: JSON.stringify(buildPayload()) };
            queueDraftSave(latest.payload, latest.snapshot);
          }} disabled={saving}>Retry</button>}
          <button className={css.cancelBtn} onClick={handleClose}>{isScheduled ? 'Close' : 'Cancel'}</button>

          {/* ── Scheduled exam: only questions can be saved ── */}
          {isScheduled ? (
            activePane === 'questions' && !hasStarted && (
              <button className={css.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Questions'}
              </button>
            )
          ) : activePane === 'details' && isPrebuilt ? (
            /* ── Prebuilt: details pane ── */
            !aiEnabled ? (
              <div className={css.premiumLockFooter}>
                🔒 <strong>Premium plan required</strong> to generate prebuilt exams.
              </div>
            ) : questionsReady ? (
              <>
                <button className={css.regenBtn} onClick={() => handleGenerate(true)} disabled={generating || saving}>
                  {generating ? genStatus || 'Regenerating…' : '↺ Regenerate'}
                </button>
                <button className={css.saveBtn} onClick={() => setActivePane('questions')}>
                  View Questions →
                </button>
              </>
            ) : (
              <button className={css.saveBtn} onClick={() => handleGenerate(false)} disabled={generating || saving}>
                {generating ? genStatus || 'Generating exam…' : '⚡ Generate Exam'}
              </button>
            )
          ) : activePane === 'questions' && isPrebuilt && savedExamId ? (
            /* ── Prebuilt: questions pane ── */
            <>
              <button className={css.draftBtn} onClick={handleSaveDraft} disabled={saving}>
                {saving ? 'Saving…' : 'Save as Draft'}
              </button>
              <button className={css.publishBtn} onClick={() => handleSchedule(savedExamId)} disabled={saving}>
                {saving ? schedulingLabel : scheduleLabel}
              </button>
            </>
          ) : (
            /* ── Normal (non-prebuilt, non-scheduled) ── */
            <>
              <button className={css.draftBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save as Draft'}
              </button>
              <button className={css.publishBtn} onClick={() => handleSchedule(null)} disabled={saving}>
                {saving ? schedulingLabel : scheduleLabel}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
    {showPrebuiltPicker && (
      <PrebuiltExamPickerModal
        onSelect={(picked) => {
          setForm(f => ({
            ...f,
            source: 'external',
            prebuiltConfig: picked,
            settings: {
              ...f.settings,
              ...(picked.duration != null ? { duration: picked.duration } : {}),
              ...(picked.passmark != null ? { passmark: picked.passmark } : {}),
            },
          }));
          setPrebuiltSubject('');
          setQuestionsReady(false);
          setGenError('');
          setShowPrebuiltPicker(false);
        }}
        onClose={() => setShowPrebuiltPicker(false)}
      />
    )}
    </>
  );
}

// ─── Student Exam Detail Drawer ───────────────────────────────────────────────
// ─── Violation type labels & colours ─────────────────────────────────────────
const VIOLATION_META = {
  tab_switch:      { label: 'Tab switch',       color: '#e53935', bg: '#fce4ec' },
  window_blur:     { label: 'Window blur',       color: '#f57c00', bg: '#fff3e0' },
  copy_attempt:    { label: 'Copy attempt',      color: '#6d4c41', bg: '#efebe9' },
  paste_attempt:   { label: 'Paste attempt',     color: '#6d4c41', bg: '#efebe9' },
  dev_tools:       { label: 'Dev tools opened',  color: '#c62828', bg: '#ffcdd2' },
  right_click:     { label: 'Right click',       color: '#78909c', bg: '#eceff1' },
  fullscreen_exit: { label: 'Fullscreen exit',   color: '#ad1457', bg: '#fce4ec' },
  mouse_leave:     { label: 'Mouse left window', color: '#00838f', bg: '#e0f7fa' },
  auto_submit:     { label: 'Auto-submitted',    color: '#b71c1c', bg: '#ffebee' },
};

function TeacherRetakeQuestionEditor({ examId, questions, onChange, onRemove, onAdd, equalMarks, autoScore, uploadButton }) {
  return (
    <>
      <div className={css.qListHeader}>
        <span>{questions.length} question(s)</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {uploadButton}
          <button type="button" className={css.addQBtn} onClick={onAdd}><AddIcon fontSize="small" /> Add Question</button>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {questions.map((question, index) => (
          <QuestionRow
            key={index}
            q={question}
            index={index}
            onChange={onChange}
            onRemove={onRemove}
            equalMarks={equalMarks}
            autoScore={autoScore}
            examId={examId}
          />
        ))}
      </div>
    </>
  );
}

function StudentExamDetailDrawer({ examId, membershipId, student, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studyPlanOpen, setStudyPlanOpen] = useState(false);
  const [proctoringOpen, setProctoringOpen] = useState(true);
  const [tModal, setTModal] = useState({ type: null, task: null, loading: false, content: null, error: null });
  const [spSubjectIdx, setSpSubjectIdx] = useState('all');
  const [editingScore, setEditingScore] = useState(null); // { question, newScore, reason, submitting, error }
  const [viewingScoreEdit, setViewingScoreEdit] = useState(null); // last scoreEdit record being viewed
  const [showRetakeModal, setShowRetakeModal] = useState(false);

  const loadData = React.useCallback(async () => {
    setLoading(true);
    const { ok, data: d } = await teacherFetch(`/teacher/analytics/exam/${examId}/student/${membershipId}`);
    if (ok) setData(d);
    setLoading(false);
  }, [examId, membershipId]);

  useEffect(() => { loadData(); }, [loadData]);

  const openScoreEdit = (q) => setEditingScore({ question: q, newScore: String(q.scoreAwarded ?? 0), reason: '', submitting: false, error: null });

  const submitScoreEdit = async () => {
    if (!editingScore) return;
    const reason = editingScore.reason.trim();
    if (!reason) { setEditingScore(m => ({ ...m, error: 'A reason is required.' })); return; }
    setEditingScore(m => ({ ...m, submitting: true, error: null }));
    const { ok, data: resp } = await teacherFetch(`/teacher/analytics/exam/${examId}/student/${membershipId}/question-score`, {
      method: 'PATCH',
      body: JSON.stringify({ questionNumber: editingScore.question.number, newScore: editingScore.newScore, reason }),
    });
    if (ok) {
      setEditingScore(null);
      await loadData();
    } else {
      setEditingScore(m => ({ ...m, submitting: false, error: resp?.error || 'Could not save the score.' }));
    }
  };

  const fmt = (s) => s ? new Date(s).toLocaleString() : '—';
  const fmtTime = (secs) => {
    if (!secs) return '—';
    const m = Math.floor(secs / 60), s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  // ── Study-plan task modal handlers ─────────────────────────────────────────
  const handleTaskClick = async (task) => {
    const rType = (typeof task.resource === 'string' ? '' : (task.resource?.resourceType || task.resource?.type || '')).toLowerCase();
    const type = rType.includes('video') ? 'video' : rType.includes('note') ? 'note' : rType.includes('mock') ? 'mock' : null;
    if (!type || !data) return;
    setTModal({ type, task, loading: true, content: null, error: null });
    if (type === 'video') {
      const query = task.resource?.title || task.title;
      const { ok, data: d } = await teacherFetch(`/teacher/analytics/study-plan/video?q=${encodeURIComponent(query)}`);
      setTModal(m => ({ ...m, loading: false, content: ok ? d.video : null, error: ok ? null : 'Failed to load video' }));
    } else if (type === 'note') {
      // Use resource.title as primary key (matches how student saves the note), fall back to task.title
      const noteTitle = task.resource?.title || task.title;
      const resourceTitle = task.resource?.title || '';
      const { ok, data: d } = await teacherFetch(
        `/teacher/analytics/study-plan/note?userId=${data.student.studentId}&examObjectId=${data.examObjectId}&title=${encodeURIComponent(noteTitle)}&resourceTitle=${encodeURIComponent(resourceTitle)}`
      );
      setTModal(m => ({ ...m, loading: false, content: ok ? d.note : null, error: ok ? null : 'Failed to load note' }));
    } else if (type === 'mock') {
      // Use resource.title as topic key (matches how student saves the mock result)
      const topic = task.resource?.title || task.title;
      const { ok, data: d } = await teacherFetch(
        `/teacher/analytics/study-plan/mock-result?userId=${data.student.studentId}&schoolExamId=${examId}&topic=${encodeURIComponent(topic)}`
      );
      setTModal(m => ({ ...m, loading: false, content: ok ? d.result : null, error: ok ? null : 'Failed to load practice result' }));
    }
  };

  // ── Export handlers ────────────────────────────────────────────────────────
  const handleExcelExport = () => {
    if (!data) return;
    const sName = `${data.student.firstName} ${data.student.lastName}`;
    const eTitle = data.exam?.title || 'Exam';
    const wb = XLSX.utils.book_new();

    // ── Summary sheet ──
    const summaryRows = [
      ['Student', sName],
      ['Class', `${data.student.className} ${data.student.classArm}`],
      ['Exam', eTitle],
      ['Score', data.attempt?.score ?? '—'],
      ['Percentage', `${data.attempt?.percentage ?? 0}%`],
      ['Result', data.attempt?.passed ? 'Pass' : 'Fail'],
      ['Time Taken', fmtTime(data.attempt?.timeTaken)],
      ['Started', fmt(data.attempt?.startedAt)],
      ['Submitted', fmt(data.attempt?.submittedAt)],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary');

    // ── Questions sheet ──
    const qRows = [['#', 'Question', 'Student Answer', 'Correct Answer', 'Result', 'Topic', 'Time']];
    (data.questions || []).forEach(q => {
      qRows.push([q.number, q.questionText, q.chosenAnswer || '', q.correctAnswer || '', q.isCorrect ? 'Correct' : 'Incorrect', q.topic || '', fmtTime(q.timeTaken)]);
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(qRows), 'Questions');

    // ── Study Plan sheet ──
    if (data.studyPlan?.schedule) {
      const spRows = [['Date', 'Task', 'Type', 'Duration (min)', 'Priority', 'Status']];
      data.studyPlan.schedule.forEach(day => {
        (day.tasks || []).forEach(task => {
          const rType = (task.resource?.resourceType || task.resource?.type || '');
          spRows.push([day.date, task.title, rType, task.timeAllocation, task.importance >= 4 ? 'High' : 'Normal', task.done ? 'Done' : 'Pending']);
        });
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(spRows), 'Study Plan');
    }

    XLSX.writeFile(wb, `${sName.replace(/\s+/g, '_')}_${eTitle.replace(/\s+/g, '_')}_report.xlsx`);
  };

  const handlePDFExport = () => {
    if (!data) return;
    const sName = `${data.student.firstName} ${data.student.lastName}`;
    const eTitle = data.exam?.title || 'Exam';
    const qRows = (data.questions || []).map((q, i) =>
      `<tr style="background:${q.isCorrect ? '#f0fff4' : '#fff5f5'}">
        <td>${q.number}</td>
        <td style="max-width:300px">${q.questionText || ''}</td>
        <td>${q.chosenAnswer || '<em>No answer</em>'}</td>
        <td>${q.isCorrect === true ? '✅' : q.isCorrect === false ? '❌' : '—'}</td>
        <td>${q.isCorrect ? '—' : (q.correctAnswer || '—')}</td>
        <td>${q.topic || ''}</td>
      </tr>`
    ).join('');
    const spRows = (data.studyPlan?.schedule || []).flatMap(day =>
      (day.tasks || []).map(t =>
        `<tr><td>${day.date}</td><td>${t.title}</td><td>${t.resource?.resourceType || t.resource?.type || ''}</td><td>${t.timeAllocation} min</td><td style="color:${t.done ? '#2e7d32' : '#f59e0b'}">${t.done ? 'Done' : 'Pending'}</td></tr>`
      )
    ).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${sName} – ${eTitle}</title>
<style>body{font-family:sans-serif;font-size:13px;color:#1a1a2e;padding:24px}h1{font-size:18px;margin:0 0 4px}h2{font-size:14px;color:#1a6e73;margin:20px 0 8px;border-bottom:1px solid #e0f4f5;padding-bottom:4px}table{width:100%;border-collapse:collapse;margin-bottom:12px}th,td{border:1px solid #e0e0e0;padding:6px 8px;text-align:left;font-size:12px}th{background:#e0f4f5;font-weight:600}.stat{display:inline-block;margin:4px 12px 4px 0;font-weight:600}@media print{body{padding:12px}}</style>
</head><body>
<h1>${sName}</h1>
<p style="margin:0 0 12px;color:#607d8b">${data.student.className} ${data.student.classArm} · ${eTitle}</p>
<h2>Exam Summary</h2>
<span class="stat">Score: ${data.attempt?.score ?? '—'}</span>
<span class="stat">Percentage: ${data.attempt?.percentage ?? 0}%</span>
<span class="stat" style="color:${data.attempt?.passed ? '#2e7d32' : '#c62828'}">${data.attempt?.passed ? '✓ Pass' : '✗ Fail'}</span>
<span class="stat">Time: ${fmtTime(data.attempt?.timeTaken)}</span>
<h2>Question Analysis</h2>
<table><thead><tr><th>#</th><th>Question</th><th>Student Answer</th><th>Result</th><th>Correct Answer</th><th>Topic</th></tr></thead><tbody>${qRows}</tbody></table>
${data.studyPlan?.schedule?.length ? `<h2>Study Plan</h2><table><thead><tr><th>Date</th><th>Task</th><th>Type</th><th>Duration</th><th>Status</th></tr></thead><tbody>${spRows}</tbody></table>` : ''}
</body></html>`;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  // Group topic breakdown
  const topicGroups = React.useMemo(() => {
    if (!data?.questions) return {};
    return data.questions.reduce((acc, q) => {
      const t = q.topic || 'General';
      if (!acc[t]) acc[t] = { correct: 0, total: 0 };
      acc[t].total++;
      if (q.isCorrect) acc[t].correct++;
      return acc;
    }, {});
  }, [data]);

  return (
    <div className={css.drawerOverlay} onClick={onClose}>
      <div className={css.drawer} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={css.drawerHeader}>
          <div>
            <div className={css.drawerTitle}>
              {student ? `${student.firstName} ${student.lastName}` : (data?.student ? `${data.student.firstName} ${data.student.lastName}` : 'Student')}
            </div>
            {data && (
              <div className={css.drawerSubtitle}>
                {data.student.className} {data.student.classArm} · {data.exam.title}
              </div>
            )}
          </div>
          <button className={css.closeBtn} onClick={onClose}><CloseIcon /></button>
        </div>

        <div className={css.drawerBody}>
          {loading ? (
            <div className={css.drawerLoading}>Loading performance data…</div>
          ) : !data ? (
            <div className={css.drawerLoading} style={{ color: '#ef9a9a' }}>Failed to load data.</div>
          ) : (
            <>
              {/* ── Attempt not found ── */}
              {!data.attempt ? (
                <div className={css.emptyState} style={{ marginTop: 32 }}>
                  <AssignmentIcon style={{ fontSize: 44, opacity: 0.3, marginBottom: 8 }} />
                  <p>This student has not attempted the exam yet.</p>
                </div>
              ) : (
                <>
                  {/* ── Score Cards ── */}
                  <div className={css.perfCardRow}>
                    <div className={css.perfCard}>
                      <div className={css.perfCardLabel}>Score</div>
                      <div className={css.perfCardValue}>{data.attempt.score ?? '—'}</div>
                    </div>
                    <div className={css.perfCard}>
                      <div className={css.perfCardLabel}>Percentage</div>
                      <div className={css.perfCardValue} style={{
                        color: data.attempt.percentage >= 50 ? '#43a047' : '#e53935',
                      }}>{data.attempt.percentage ?? 0}%</div>
                    </div>
                    <div className={css.perfCard}>
                      <div className={css.perfCardLabel}>Time Taken</div>
                      <div className={css.perfCardValue}>{fmtTime(data.attempt.timeTaken)}</div>
                    </div>
                    <div className={css.perfCard} style={{
                      background: data.attempt.passed ? 'linear-gradient(135deg,#e8f5e9,#f1f8e9)' : 'linear-gradient(135deg,#fce4ec,#fef9fa)',
                      borderColor: data.attempt.passed ? '#a5d6a7' : '#ef9a9a',
                    }}>
                      <div className={css.perfCardLabel}>Result</div>
                      <div className={css.perfCardValue} style={{ color: data.attempt.passed ? '#2e7d32' : '#c62828' }}>
                        {data.attempt.passed ? '✓ Pass' : '✗ Fail'}
                      </div>
                    </div>
                  </div>

                  <div className={css.drawerSection}>
                    <div className={css.drawerSectionTitle}>Retakes</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <RetakeBadge count={data.attempt.retakeCount} retake={data.attempt.retake} />
                      {data.exam?.canAssignRetake && data.attempt.retakeEligibility?.eligible && <button type="button" className={css.btnSecondary} onClick={() => setShowRetakeModal(true)}>Allow retake</button>}
                    </div>
                    <RetakeHistoryList history={data.attempt.retakeHistory || []} />
                  </div>

                  {/* ── Topic Breakdown Pills ── */}
                  {Object.keys(topicGroups).length > 0 && (
                    <div className={css.drawerSection}>
                      <div className={css.drawerSectionTitle}>Topic Breakdown</div>
                      <div className={css.topicPillRow}>
                        {Object.entries(topicGroups).map(([topic, { correct, total }]) => {
                          const pct = Math.round((correct / total) * 100);
                          const color = pct >= 70 ? '#43a047' : pct >= 40 ? '#fb8c00' : '#e53935';
                          return (
                            <div key={topic} className={css.topicPill} style={{ borderColor: color }}>
                              <span className={css.topicPillName}>{topic}</span>
                              <span className={css.topicPillStat} style={{ color }}>
                                {correct}/{total} ({pct}%)
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* ── Question-by-question ── */}
                  <div className={css.drawerSection}>
                    <div className={css.drawerSectionTitle}>Question Analysis ({data.questions.length} questions)</div>
                    <div className={css.tableWrap}>
                      <table className={css.table}>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Question</th>
                            <th>Student Answer</th>
                            <th>Result</th>
                            <th>Correct Answer</th>
                            <th>Score</th>
                            <th>Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.questions.map(q => (
                            <tr key={q.number} className={q.isCorrect === true ? css.qRowCorrect : q.isCorrect === false ? css.qRowIncorrect : ''}>
                              <td>{q.number}</td>
                              <td className={css.qText}>{q.questionText ? q.questionText.slice(0, 80) + (q.questionText.length > 80 ? '…' : '') : '—'}</td>
                              <td>
                                {q.chosenAnswer ? (
                                  <span className={css.answerPill} style={{
                                    background: q.isCorrect ? '#e8f5e9' : '#fce4ec',
                                    color: q.isCorrect ? '#2e7d32' : '#c62828',
                                  }}>{q.chosenAnswer}</span>
                                ) : <span style={{ color: '#90a4ae' }}>No answer</span>}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {q.isCorrect === true ? '✅' : q.isCorrect === false ? '❌' : '—'}
                              </td>
                              <td>
                                {!q.isCorrect && q.correctAnswer ? (
                                  <span className={css.answerPill} style={{ background: '#e8f5e9', color: '#2e7d32' }}>
                                    {q.correctAnswer}
                                  </span>
                                ) : q.isCorrect ? <span style={{ color: '#90a4ae' }}>—</span> : '—'}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {q.scoreAwarded != null ? `${q.scoreAwarded}/${q.maxScore ?? '—'}` : '—'}
                                {q.lastScoreEdit && (
                                  <button
                                    type="button"
                                    onClick={() => setViewingScoreEdit(q.lastScoreEdit)}
                                    title="Corrected score — tap to view details"
                                    style={{ marginLeft: 4, border: 'none', background: 'none', color: '#f57c00', cursor: 'pointer', fontSize: '0.85rem', padding: '2px 4px' }}
                                  >✎</button>
                                )}
                                {data.exam?.canEditScores && (
                                  <button
                                    type="button"
                                    onClick={() => openScoreEdit(q)}
                                    disabled={['pending', 'in_progress', 'grading'].includes(data.attempt.retake?.status)}
                                    title={data.attempt.retake?.status ? 'Score corrections are unavailable while a retake is active' : 'Edit score'}
                                    style={{ marginLeft: 6, border: 'none', background: 'none', color: '#1a6e73', cursor: 'pointer', fontSize: '0.75rem', textDecoration: 'underline' }}
                                  >Edit</button>
                                )}
                              </td>
                              <td>{fmtTime(q.timeTaken)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ── Proctoring / Integrity ── */}
                  {['manual', 'manual_scores'].includes(data.exam?.examMode) ? (
                    <div className={css.drawerSection}>
                      <div className={css.drawerSectionTitle}>🔍 Integrity Report</div>
                      <p style={{ color: '#607d8b', fontSize: '0.82rem', margin: 0 }}>Manual exam taken; no integrity report is available.</p>
                    </div>
                  ) : data.attempt.proctoring && (
                    (() => {
                      const p = data.attempt.proctoring;
                      if (p.locked) {
                        return (
                          <div className={css.drawerSection}>
                            <div className={css.drawerSectionTitle}>🔍 Integrity Report</div>
                            <div className={css.lockedBanner}>🔒 {p.message || 'Locked by Admin'}</div>
                          </div>
                        );
                      }
                      const totalViolations =
                        (p.tabSwitchCount || 0) + (p.windowBlurCount || 0) +
                        (p.copyAttemptCount || 0) + (p.pasteAttemptCount || 0) +
                        (p.devToolsCount || 0) + (p.rightClickCount || 0) +
                        (p.fullscreenExitCount || 0) + (p.mouseLeaveCount || 0);
                      const riskBand =
                        p.riskScore >= 40 ? { label: 'High Risk',   color: '#c62828', bg: '#fce4ec' } :
                        p.riskScore >= 20 ? { label: 'Medium Risk',  color: '#f57c00', bg: '#fff3e0' } :
                                            { label: 'Low Risk',     color: '#2e7d32', bg: '#e8f5e9' };
                      const countRows = [
                        { key: 'tab_switch',      value: p.tabSwitchCount },
                        { key: 'window_blur',     value: p.windowBlurCount },
                        { key: 'copy_attempt',    value: p.copyAttemptCount },
                        { key: 'paste_attempt',   value: p.pasteAttemptCount },
                        { key: 'dev_tools',       value: p.devToolsCount },
                        { key: 'fullscreen_exit', value: p.fullscreenExitCount },
                        { key: 'mouse_leave',     value: p.mouseLeaveCount },
                      ].filter(r => r.value > 0);

                      return (
                        <div className={css.drawerSection}>
                          <div
                            className={css.drawerSectionTitle}
                            style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            onClick={() => setProctoringOpen(o => !o)}
                          >
                            <span>
                              🔍 Integrity Report
                              {p.flaggedForReview && (
                                <span style={{
                                  marginLeft: 8, fontSize: '0.7rem', fontWeight: 700,
                                  background: '#fce4ec', color: '#c62828',
                                  borderRadius: 6, padding: '2px 7px', verticalAlign: 'middle',
                                }}>FLAGGED</span>
                              )}
                            </span>
                            <span style={{ fontSize: '0.8rem', color: '#90a4ae' }}>{proctoringOpen ? '▲' : '▼'}</span>
                          </div>

                          {proctoringOpen && (
                            <>
                              {/* Summary stat cards */}
                              <div className={css.perfCardRow} style={{ marginBottom: 16 }}>
                                <div className={css.perfCard} style={{ borderTopColor: riskBand.color, background: riskBand.bg }}>
                                  <div className={css.perfCardLabel}>Risk Score</div>
                                  <div className={css.perfCardValue} style={{ color: riskBand.color, fontSize: '1.3rem' }}>
                                    {p.riskScore ?? 0}<span style={{ fontSize: '0.75rem', fontWeight: 500, color: riskBand.color }}>/100</span>
                                  </div>
                                  <div style={{ fontSize: '0.7rem', fontWeight: 700, color: riskBand.color, marginTop: 2 }}>{riskBand.label}</div>
                                </div>
                                <div className={css.perfCard}>
                                  <div className={css.perfCardLabel}>Total Events</div>
                                  <div className={css.perfCardValue} style={{ fontSize: '1.3rem' }}>{totalViolations}</div>
                                </div>
                                <div className={css.perfCard}>
                                  <div className={css.perfCardLabel}>Timeline Entries</div>
                                  <div className={css.perfCardValue} style={{ fontSize: '1.3rem' }}>{(p.violations || []).length}</div>
                                </div>
                              </div>

                              {/* Per-type breakdown pills */}
                              {countRows.length > 0 && (
                                <div className={css.topicPillRow} style={{ marginBottom: 16 }}>
                                  {countRows.map(({ key, value }) => {
                                    const meta = VIOLATION_META[key] || { label: key, color: '#555', bg: '#f5f5f5' };
                                    return (
                                      <div key={key} className={css.topicPill} style={{ borderColor: meta.color, background: meta.bg }}>
                                        <span className={css.topicPillName} style={{ color: meta.color }}>{meta.label}</span>
                                        <span className={css.topicPillStat} style={{ color: meta.color }}>×{value}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Violation timeline */}
                              {(p.violations || []).length > 0 ? (
                                <div className={css.tableWrap}>
                                  <table className={css.table}>
                                    <thead>
                                      <tr>
                                        <th>#</th>
                                        <th>Event</th>
                                        <th>Time</th>
                                        <th>Detail</th>
                                        <th>Screenshot</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {p.violations.map((v, i) => {
                                        const meta = VIOLATION_META[v.type] || { label: v.type, color: '#555', bg: '#f5f5f5' };
                                        return (
                                          <tr key={i}>
                                            <td style={{ color: '#90a4ae', fontSize: '0.78rem' }}>{i + 1}</td>
                                            <td>
                                              <span className={css.answerPill} style={{ background: meta.bg, color: meta.color }}>
                                                {meta.label}
                                              </span>
                                            </td>
                                            <td style={{ fontSize: '0.78rem', color: '#546e7a', whiteSpace: 'nowrap' }}>
                                              {v.timestamp ? new Date(v.timestamp).toLocaleTimeString() : '—'}
                                            </td>
                                            <td style={{ fontSize: '0.78rem', color: '#78909c' }}>{v.detail || '—'}</td>
                                            <td>
                                              {v.screenshotUrl ? (
                                                <button
                                                  className={css.iconActionBtn}
                                                  title="View screenshot"
                                                  style={{ color: '#5fbec5', borderColor: '#b2e0e3', fontSize: '0.75rem', padding: '3px 8px' }}
                                                  onClick={() => setTModal({ type: 'screenshot', content: v.screenshotUrl, loading: false, task: null, error: null })}
                                                >
                                                  📷 View
                                                </button>
                                              ) : (
                                                <span style={{ color: '#90a4ae', fontSize: '0.75rem' }}>—</span>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p style={{ fontSize: '0.82rem', color: '#90a4ae', fontStyle: 'italic', margin: 0 }}>
                                  No detailed violation timeline recorded.
                                </p>
                              )}

                              {totalViolations === 0 && (
                                <p style={{ fontSize: '0.82rem', color: '#43a047', fontStyle: 'italic', margin: '8px 0 0' }}>
                                  ✓ No integrity events recorded for this attempt.
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })()
                  )}

                  {/* ── Study Plan ── */}
                  {data.studyPlan && (
                    <div className={css.studyPlanSection}>
                      <div className={css.studyPlanToggle} onClick={() => setStudyPlanOpen(o => !o)}>
                        <span>📚 Assigned Study Plan</span>
                        <span>{studyPlanOpen ? '▲' : '▼'}</span>
                      </div>
                      {studyPlanOpen && (
                        <div className={css.studyPlanBody}>
                          {(() => {
                            const plan = data.studyPlan;
                            const schedule = Array.isArray(plan?.schedule) ? plan.schedule : null;
                            if (!schedule) {
                              return (
                                <p style={{ fontSize: '0.85rem', color: '#607d8b', margin: 0 }}>
                                  Study plan data unavailable.
                                </p>
                              );
                            }
                            const allTasks = schedule.flatMap(d => d.tasks || []);
                            const subjects = Array.from(new Map(
                              allTasks
                                .filter(t => typeof t.subjectIdx === 'number')
                                .map(t => [t.subjectIdx, t.subjectName || `Subject ${t.subjectIdx + 1}`])
                            ).entries()).sort((a, b) => a[0] - b[0]);
                            const isMultiPlan = data.exam?.isMultiSubject && subjects.length > 1;
                            const selectedSpSubjectIdx = isMultiPlan && spSubjectIdx === 'all' ? subjects[0][0] : spSubjectIdx;
                            const visibleTasks = isMultiPlan
                              ? allTasks.filter(t => t.subjectIdx === selectedSpSubjectIdx)
                              : allTasks;
                            const totalTasks = visibleTasks.length;
                            const doneTasks  = visibleTasks.filter(t => t.done).length;
                            const pct = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;
                            return (
                              <>
                                {isMultiPlan && (
                                  <div className={css.subjectTabRow}>
                                    {subjects.map(([idx, name]) => (
                                      <button
                                        key={idx}
                                        className={`${css.subjectTabPill} ${selectedSpSubjectIdx === idx ? css.subjectTabPillActive : ''}`}
                                        onClick={() => setSpSubjectIdx(idx)}
                                      >
                                        {name}
                                      </button>
                                    ))}
                                  </div>
                                )}
                                {/* Progress summary */}
                                <div className={css.spTeacherProgress}>
                                  <div className={css.spTeacherProgressBar}>
                                    <div className={css.spTeacherProgressFill} style={{ width: `${pct}%` }} />
                                  </div>
                                  <span className={css.spTeacherProgressLabel}>{doneTasks}/{totalTasks} tasks completed ({pct}%)</span>
                                </div>

                                {/* Day-by-day task list */}
                                {schedule.map((day, di) => {
                                  const dayTasks = (day.tasks || []).filter(task =>
                                    !isMultiPlan || task.subjectIdx === selectedSpSubjectIdx
                                  );
                                  if (dayTasks.length === 0) return null;
                                  return <div key={di} className={css.spTeacherDay}>
                                    <p className={css.spTeacherDayLabel}>{day.date}</p>
                                    {dayTasks.map((task, ti) => {
                                      const rType = (typeof task.resource === 'string' ? '' : (task.resource?.resourceType || task.resource?.type || '')).toLowerCase();
                                      const typeTag = rType.includes('video') ? 'Video' : rType.includes('note') ? 'Notes' : rType.includes('mock') ? 'Mock' : null;
                                      const isClickable = rType.includes('video') || rType.includes('note') || rType.includes('mock');
                                      return (
                                        <div
                                          key={ti}
                                          className={`${css.spTeacherTask} ${task.done ? css.spTeacherTaskDone : ''} ${isClickable ? css.spTeacherTaskClickable : ''}`}
                                          onClick={isClickable ? () => handleTaskClick(task) : undefined}
                                          style={isClickable ? { cursor: 'pointer' } : undefined}
                                        >
                                          <span className={`${css.spTeacherDot} ${task.done ? css.spTeacherDotDone : ''}`} />
                                          <div className={css.spTeacherTaskInfo}>
                                            <span className={css.spTeacherTaskTitle}>{task.title}</span>
                                            <span className={css.spTeacherTaskMeta}>
                                              {task.timeAllocation} min
                                              {typeTag && <span className={css.spTeacherTypeTag}>{typeTag}</span>}
                                              {task.importance >= 4 && <span className={css.spTeacherStarTag}>★ Priority</span>}
                                              {isClickable && <span className={css.spTeacherViewHint}>View ›</span>}
                                            </span>
                                          </div>
                                          <span className={`${css.spTeacherStatus} ${task.done ? css.spTeacherStatusDone : css.spTeacherStatusPending}`}>
                                            {task.done ? 'Done' : 'Pending'}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                })}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  <div className={css.drawerMeta}>
                    <span>Started: {fmt(data.attempt.startedAt)}</span>
                    <span>Submitted: {fmt(data.attempt.submittedAt)}</span>
                  </div>

                  {/* ── Export buttons ── */}
                  <div className={css.drawerExportRow}>
                    <button className={css.drawerExportBtn} onClick={handlePDFExport}>
                      🖨 Export PDF
                    </button>
                    <button className={css.drawerExportBtn} style={{ background: 'linear-gradient(135deg,#1a6e73,#2a9d8f)' }} onClick={handleExcelExport}>
                      📊 Export Excel
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Teacher study-plan task modal ── */}
      {tModal.type && (
        <div className={css.tModalOverlay} onClick={() => setTModal({ type: null, task: null, loading: false, content: null, error: null })}>
          <div className={`${css.tModalBox} BubblePopIn`} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className={css.tModalHeader}>
              <div>
                <span className={css.tModalBadge}>
                  {tModal.type === 'video' ? '▶ Video' : tModal.type === 'note' ? '📄 Study Notes' : tModal.type === 'screenshot' ? '📷 Screenshot' : '🎯 Practice Mock'}
                </span>
                <h3 className={css.tModalTitle}>{tModal.type === 'screenshot' ? 'Violation Screenshot' : tModal.task?.title}</h3>
              </div>
              <button className={css.tModalClose} onClick={() => setTModal({ type: null, task: null, loading: false, content: null, error: null })}>✕</button>
            </div>

            {/* Body */}
            <div className={css.tModalBody}>
              {tModal.loading && (
                <div className={css.tModalLoading}>
                  <span className={css.tModalSpinner} />
                  <p>Loading…</p>
                </div>
              )}
              {!tModal.loading && tModal.error && (
                <p className={css.tModalError}>{tModal.error}</p>
              )}

              {/* VIDEO */}
              {!tModal.loading && !tModal.error && tModal.type === 'video' && (
                tModal.content ? (
                  <div>
                    <div className={css.tModalVideoWrap}>
                      <iframe
                        src={`https://www.youtube.com/embed/${tModal.content.videoId}?rel=0`}
                        title={tModal.content.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className={css.tModalVideoFrame}
                      />
                    </div>
                    <p className={css.tModalVideoTitle}>{tModal.content.title}</p>
                    <p className={css.tModalVideoChannel}>{tModal.content.channelTitle}</p>
                    <p className={css.tModalHint}>Read-only view — this is the video available for this study topic.</p>
                  </div>
                ) : (
                  <p className={css.tModalEmpty}>No video found for this topic.</p>
                )
              )}

              {/* NOTE */}
              {!tModal.loading && !tModal.error && tModal.type === 'note' && (
                tModal.content ? (
                  <div>
                    <div className={css.tModalNoteBody}>
                      {renderMarkdownNote(tModal.content.content)}
                    </div>
                    <p className={css.tModalHint}>Generated: {tModal.content.createdAt ? new Date(tModal.content.createdAt).toLocaleDateString() : '—'}</p>
                  </div>
                ) : (
                  <p className={css.tModalEmpty}>The student has not generated notes for this task yet.</p>
                )
              )}

              {/* MOCK */}
              {!tModal.loading && !tModal.error && tModal.type === 'mock' && (
                tModal.content ? (
                  <div>
                    <div className={css.tModalScoreCard} style={{ borderColor: tModal.content.passed ? '#a5d6a7' : '#ef9a9a', background: tModal.content.passed ? 'linear-gradient(135deg,#e8f5e9,#f1f8e9)' : 'linear-gradient(135deg,#fce4ec,#fef9fa)' }}>
                      <span className={css.tModalScorePct} style={{ color: tModal.content.passed ? '#2e7d32' : '#c62828' }}>{tModal.content.pct}%</span>
                      <span className={css.tModalScoreSub}>{tModal.content.score}/{tModal.content.total} correct · {tModal.content.passed ? 'Pass' : 'Needs Work'}</span>
                      {tModal.content.timeTaken > 0 && (
                        <span className={css.tModalScoreSub}>Time: {fmtTime(tModal.content.timeTaken)}</span>
                      )}
                    </div>
                    {(tModal.content.questions || []).length > 0 && (
                      <div className={css.tModalQList}>
                        {tModal.content.questions.map((q, i) => (
                          <div key={i} className={`${css.tModalQItem} ${q.isCorrect ? css.tModalQCorrect : css.tModalQWrong}`}>
                            <div className={css.tModalQHeader}>
                              <span className={css.tModalQBadge}>{q.isCorrect ? '✓' : '✗'}</span>
                              <p className={css.tModalQText}><strong>Q{i + 1}.</strong> {q.questionText}</p>
                            </div>
                            <div className={css.tModalQAnswers}>
                              {!q.isCorrect && q.chosen && <span className={css.tModalQYours}>Student: {q.chosen}</span>}
                              <span className={css.tModalQCorrect2}>Correct: {q.correct}</span>
                            </div>
                            {q.explanation && <p className={css.tModalQExplanation}>{q.explanation}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className={css.tModalEmpty}>
                    {tModal.task?.done
                      ? 'Practice was completed but no result data is available (may have been done before tracking was enabled).'
                      : 'This student has not completed the practice mock for this task yet.'}
                  </p>
                )
              )}

              {/* SCREENSHOT */}
              {!tModal.loading && !tModal.error && tModal.type === 'screenshot' && (
                tModal.content ? (
                  <div style={{ textAlign: 'center' }}>
                    <div
                      id="violation-screenshot-wrap"
                      style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #e0eaec', marginBottom: 12 }}
                    >
                      <img
                        src={tModal.content}
                        alt="Violation screenshot"
                        style={{ maxWidth: '100%', display: 'block' }}
                      />
                    </div>
                    <button
                      className={css.primaryBtn}
                      style={{ fontSize: '0.82rem' }}
                      onClick={() => {
                        const wrap = document.getElementById('violation-screenshot-wrap');
                        if (wrap?.requestFullscreen) wrap.requestFullscreen();
                      }}
                    >
                      ⛶ Fullscreen
                    </button>
                  </div>
                ) : (
                  <p className={css.tModalEmpty}>No screenshot available for this violation.</p>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Score correction modal (mandatory reason) ── */}
      {editingScore && (
        <div className={css.modalOverlay} onClick={() => !editingScore.submitting && setEditingScore(null)}>
          <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <h2 className={css.modalTitle}>Correct Score — Q{editingScore.question.number}</h2>
            <p style={{ fontSize: '0.82rem', color: '#78909c', margin: '-6px 0 16px' }}>
              This changes the marks awarded for this question only — the student's total score,
              percentage and pass/fail result are recalculated automatically. The change and your
              reason are logged and visible to the school admin; the student and parent only see
              the corrected score.
            </p>
            {editingScore.error && <div className={css.inlineError}>{editingScore.error}</div>}
            <div className={css.formRow}>
              <label>Score (max {editingScore.question.maxScore ?? '—'})</label>
              <input
                className={css.formInput}
                type="number"
                min="0"
                max={editingScore.question.maxScore ?? undefined}
                step="0.5"
                value={editingScore.newScore}
                onChange={e => setEditingScore(m => ({ ...m, newScore: e.target.value }))}
              />
            </div>
            <div className={css.formRow}>
              <label>Reason *</label>
              <textarea
                className={css.formTextarea}
                rows={3}
                value={editingScore.reason}
                onChange={e => setEditingScore(m => ({ ...m, reason: e.target.value }))}
                placeholder="e.g. Re-marked against the marking guide, the AI missed a valid method."
              />
            </div>
            <div className={css.modalFooter}>
              <button className={css.cancelBtn} onClick={() => setEditingScore(null)} disabled={editingScore.submitting}>Cancel</button>
              <button
                className={css.saveBtn}
                disabled={editingScore.submitting || !editingScore.reason.trim()}
                onClick={submitScoreEdit}
              >
                {editingScore.submitting ? 'Saving…' : 'Save Correction'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Score edit detail (tap/click-friendly — works on mobile too) ── */}
      {viewingScoreEdit && (
        <div className={css.modalOverlay} onClick={() => setViewingScoreEdit(null)}>
          <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <h2 className={css.modalTitle}>Score Correction Details</h2>
            <p style={{ fontSize: '0.85rem', color: '#1a2a36', margin: '12px 0 4px' }}>
              <strong>{viewingScoreEdit.editedBy?.name || 'Staff'}</strong>
              {' '}({viewingScoreEdit.editedBy?.actorType === 'school_admin' ? 'Admin' : 'Teacher'}) changed this
              question's score from {viewingScoreEdit.previousScoreAwarded} to {viewingScoreEdit.newScoreAwarded}
              {viewingScoreEdit.editedAt ? ` on ${new Date(viewingScoreEdit.editedAt).toLocaleString()}` : ''}.
            </p>
            <p style={{ fontSize: '0.85rem', color: '#546e7a', margin: '8px 0 0' }}>
              <strong>Reason:</strong> {viewingScoreEdit.reason}
            </p>
            <div className={css.modalFooter}>
              <button className={css.cancelBtn} onClick={() => setViewingScoreEdit(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
      {showRetakeModal && data?.student && (
        <RetakeAssignmentModal
          exam={{ _id: examId, title: data.exam?.title }}
          selectedStudents={[data.student]}
          actor="teacher"
          fetchFn={teacherFetch}
          dateTimePicker={DateTimePicker}
          classes={css}
          reportCardReleased={data.exam?.reportCardReleased}
          renderQuestionEditor={editor => <TeacherRetakeQuestionEditor {...editor} examId={examId} />}
          onClose={() => setShowRetakeModal(false)}
          onComplete={() => loadData()}
        />
      )}
    </div>
  );
}

// ─── Score distribution buckets (constant — defined outside component) ────────
const SCORE_BUCKETS = [
  { label: '0–19%',   min: 0,  max: 20,  color: '#ef5350' },
  { label: '20–39%',  min: 20, max: 40,  color: '#ff8a65' },
  { label: '40–59%',  min: 40, max: 60,  color: '#ffb300' },
  { label: '60–79%',  min: 60, max: 80,  color: '#26a69a' },
  { label: '80–100%', min: 80, max: 101, color: '#43a047' },
];

// ─── Exam Performance Modal ────────────────────────────────────────────────────
function ExamPerformanceModal({ exam, onClose, onChanged }) {
  const [perfData, setPerfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null); // { membershipId, firstName, lastName }
  const [manualMember, setManualMember] = useState(null);
  const [selectedRetakeIds, setSelectedRetakeIds] = useState([]);
  const [showRetakeModal, setShowRetakeModal] = useState(false);
  const [qAnalysisOpen, setQAnalysisOpen] = useState(false);
  const [activeSubjectIdx, setActiveSubjectIdx] = useState('all');
  const isManualExam = ['manual', 'manual_scores'].includes(exam.examMode);
  const isPendingManualApproval = isManualExam && (perfData?.exam?.status || exam.status) === 'pending_approval';

  // Student Performance table filters
  const [resultFilter, setResultFilter]   = useState('all');
  const [riskFilter, setRiskFilter]        = useState('all');
  const [flaggedFilter, setFlaggedFilter]  = useState('all');

  const now = new Date();
  const startDate = exam.settings?.startDate ? new Date(exam.settings.startDate) : null;
  const endDate   = exam.settings?.endDate   ? new Date(exam.settings.endDate)   : null;
  const examIsLive = !isPendingManualApproval && startDate && startDate <= now && (!endDate || endDate > now);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { ok, data } = await teacherFetch(`/teacher/analytics/exam/${exam._id}`);
      if (ok) setPerfData(data);
      setLoading(false);
    })();
  }, [exam._id]);

  // ── Export handlers ────────────────────────────────────────────────────────
  const handleExcelExport = () => {
    if (!perfData) return;
    const eTitle = exam.title || 'Exam';
    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryRows = [
      ['Exam', eTitle],
      ['Subject', exam.subjectName || '—'],
      ['Class', `${exam.classId?.name || ''} ${exam.classArm ? `(${exam.classArm})` : ''}`.trim()],
      ['Assigned', perfData.summary?.totalAssigned ?? '—'],
      ['Submitted', perfData.summary?.totalSubmitted ?? '—'],
      ['Avg Score', perfData.summary?.avgPercentage != null ? `${perfData.summary.avgPercentage}%` : '—'],
      ['Pass Rate', perfData.summary?.passRate != null ? `${perfData.summary.passRate}%` : '—'],
      ['Not Attempted', (perfData.summary?.totalAssigned ?? 0) - (perfData.summary?.totalSubmitted ?? 0)],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary');

    // Students sheet
    const studentRows = [['Student', 'Score', '%', 'Retakes', 'Result', 'Risk', 'Flagged', 'Time']];
    (perfData.students || []).forEach(s => {
      const mins = s.timeTaken ? Math.floor(s.timeTaken / 60) : 0;
      const secs = s.timeTaken ? s.timeTaken % 60 : 0;
      const timeLabel = s.timeTaken ? (mins > 0 ? `${mins}m ${secs > 0 ? `${secs}s` : ''}`.trim() : `${secs}s`) : '—';
      studentRows.push([
        `${s.firstName} ${s.lastName}`,
        s.score ?? '—',
        `${s.percentage ?? 0}%`,
        s.retakeCount || 0,
        s.passed ? 'Pass' : 'Fail',
        s.riskLevel || 'low',
        s.proctoring?.flaggedForReview ? 'Flagged' : 'Clear',
        timeLabel,
      ]);
    });
    (perfData.notAttempted || []).forEach(s => {
      studentRows.push([`${s.firstName} ${s.lastName}`, '—', '—', 0, 'Not Attempted', '—', '—', '—']);
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(studentRows), 'Students');

    // Topic breakdown sheet
    if (perfData.topicBreakdown?.length) {
      const topicRows = [['Topic', 'Correct Rate']];
      perfData.topicBreakdown.forEach(t => topicRows.push([t.topic, `${t.avgCorrectRate ?? 0}%`]));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(topicRows), 'Topics');
    }

    XLSX.writeFile(wb, `${eTitle.replace(/\s+/g, '_')}_results.xlsx`);
  };

  const handlePDFExport = () => {
    if (!perfData) return;
    const eTitle = exam.title || 'Exam';
    const subjectLine = [exam.subjectName, exam.classId?.name, exam.classArm ? `(${exam.classArm})` : ''].filter(Boolean).join(' · ');

    const studentRows = (perfData.students || []).map(s =>
      `<tr>
        <td>${s.firstName} ${s.lastName}</td>
        <td>${s.retakeCount > 0 ? `Retaken ×${s.retakeCount}` : '—'}</td>
        <td>${s.score ?? '—'}</td>
        <td style="color:${(s.percentage ?? 0) >= 50 ? '#2e7d32' : '#c62828'};font-weight:700">${s.percentage ?? 0}%</td>
        <td style="color:${s.passed ? '#2e7d32' : '#c62828'}">${s.passed ? 'Pass' : 'Fail'}</td>
        <td>${s.riskLevel || 'low'}</td>
        <td>${s.proctoring?.flaggedForReview ? '<span style="color:#c62828">⚑ Flagged</span>' : '—'}</td>
      </tr>`
    ).join('');

    const notAttemptedRows = (perfData.notAttempted || []).map(s =>
      `<tr style="opacity:0.6"><td>${s.firstName} ${s.lastName}</td><td>—</td><td colspan="5" style="font-style:italic;color:#90a4ae">Not attempted</td></tr>`
    ).join('');

    const topicRows = (perfData.topicBreakdown || []).map(t => {
      const pct = t.avgCorrectRate ?? 0;
      const color = pct >= 70 ? '#2e7d32' : pct >= 40 ? '#f57c00' : '#c62828';
      return `<tr><td>${t.topic}</td><td style="color:${color};font-weight:700">${pct}%</td></tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${eTitle} – Results</title>
<style>body{font-family:sans-serif;font-size:13px;color:#1a1a2e;padding:24px}h1{font-size:18px;margin:0 0 4px}h2{font-size:14px;color:#1a6e73;margin:20px 0 8px;border-bottom:1px solid #e0f4f5;padding-bottom:4px}table{width:100%;border-collapse:collapse;margin-bottom:12px}th,td{border:1px solid #e0e0e0;padding:6px 8px;text-align:left;font-size:12px}th{background:#e0f4f5;font-weight:600}.stat{display:inline-block;margin:4px 16px 4px 0;font-weight:600}@media print{body{padding:12px}}</style>
</head><body>
<h1>${eTitle}</h1>
<p style="margin:0 0 12px;color:#607d8b">${subjectLine}</p>
<h2>Summary</h2>
<span class="stat">Assigned: ${perfData.summary?.totalAssigned ?? '—'}</span>
<span class="stat">Submitted: ${perfData.summary?.totalSubmitted ?? '—'}</span>
<span class="stat">Avg Score: ${perfData.summary?.avgPercentage != null ? `${perfData.summary.avgPercentage}%` : '—'}</span>
<span class="stat">Pass Rate: ${perfData.summary?.passRate != null ? `${perfData.summary.passRate}%` : '—'}</span>
<h2>Student Performance</h2>
<table><thead><tr><th>Student</th><th>Retakes</th><th>Score</th><th>%</th><th>Result</th><th>Risk</th><th>Flagged</th></tr></thead><tbody>${studentRows}${notAttemptedRows}</tbody></table>
${topicRows ? `<h2>Topic Breakdown</h2><table><thead><tr><th>Topic</th><th>Correct Rate</th></tr></thead><tbody>${topicRows}</tbody></table>` : ''}
</body></html>`;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  // SCORE_BUCKETS used directly in useMemo and JSX (module-level constant)

  // Multi-subject support — per-subject tabs mirroring the admin dashboard's
  // exam performance modal, so topic/question analysis can be viewed per subject.
  const isMulti = perfData?.exam?.isMultiSubject && (perfData?.subjectAnalytics?.length ?? 0) > 1;
  const currentSubjectData = isMulti && activeSubjectIdx !== 'all'
    ? (perfData.subjectAnalytics || []).find(s => s.subjectIdx === activeSubjectIdx) ?? null
    : null;
  const scoreDistribution = currentSubjectData?.scoreDistribution ?? perfData?.overallScoreDistribution ?? [];
  const topicData = currentSubjectData?.topicBreakdown ?? perfData?.topicBreakdown ?? [];
  const questionData = currentSubjectData?.questionAnalysis ?? perfData?.questionAnalysis ?? [];

  const bucketCounts = scoreDistribution.length
    ? SCORE_BUCKETS.map(b => scoreDistribution.find(d => d.label === b.label)?.count ?? 0)
    : SCORE_BUCKETS.map(() => 0);

  const maxBucket = Math.max(...bucketCounts, 1);

  // Apply Student Performance filters
  const filteredStudents = React.useMemo(() => {
    let list = perfData?.students || [];
    if (resultFilter !== 'all') list = list.filter(s => (s.passed ? 'pass' : 'fail') === resultFilter);
    if (riskFilter !== 'all')   list = list.filter(s => (s.riskLevel || 'low') === riskFilter);
    if (flaggedFilter !== 'all') {
      const want = flaggedFilter === 'flagged';
      list = list.filter(s => !!s.proctoring?.flaggedForReview === want);
    }
    return list;
  }, [perfData, resultFilter, riskFilter, flaggedFilter]);

  const anyFilterActive = resultFilter !== 'all' || riskFilter !== 'all' || flaggedFilter !== 'all';
  const resetFilters = () => { setResultFilter('all'); setRiskFilter('all'); setFlaggedFilter('all'); };
  const canManageRetakes = !!perfData?.canAssignRetake;
  const selectableStudents = canManageRetakes ? filteredStudents.filter(student => student.retakeEligibility?.eligible) : [];
  const selectedRetakeStudents = (perfData?.students || []).filter(student => selectedRetakeIds.includes(String(student.membershipId)));
  const allVisibleSelected = selectableStudents.length > 0 && selectableStudents.every(student => selectedRetakeIds.includes(String(student.membershipId)));
  const toggleAllVisible = checked => setSelectedRetakeIds(current => checked
    ? [...new Set([...current, ...selectableStudents.map(student => String(student.membershipId))])]
    : current.filter(id => !selectableStudents.some(student => String(student.membershipId) === id)));
  const reloadAnalytics = () => {
    setLoading(true);
    teacherFetch(`/teacher/analytics/exam/${exam._id}`).then(result => {
      if (result.ok) setPerfData(result.data);
      setLoading(false);
    });
  };
  const cancelRetake = async membershipId => {
    const result = await teacherFetch(`/teacher/exams/${exam._id}/retakes/${membershipId}`, { method: 'DELETE' });
    if (result.ok) reloadAnalytics();
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modalXL} BubblePopIn`} onClick={e => e.stopPropagation()}>
        {/* ── Header ── */}
        <div className={css.perfHeader}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1.2rem', fontWeight: 800, color: '#fff' }}>{exam.title}</span>
              {!loading && !examIsLive && perfData && (() => {
                const flaggedCount = (perfData.students || []).filter(s => s.proctoring?.flaggedForReview).length;
                return flaggedCount > 0 ? (
                  <span style={{
                    background: '#fce4ec', color: '#c62828',
                    fontSize: '0.72rem', fontWeight: 800, borderRadius: 8,
                    padding: '3px 10px', letterSpacing: '0.04em',
                    border: '1.5px solid #ef9a9a', whiteSpace: 'nowrap',
                  }}>
                    ⚠ {flaggedCount} student{flaggedCount !== 1 ? 's' : ''} flagged
                  </span>
                ) : null;
              })()}
            </div>
            <div style={{ fontSize: '0.82rem', color: '#90a4ae', marginTop: 4 }}>
              {exam.subjectName} · {exam.classId?.name || ''} {exam.classArm ? `(${exam.classArm})` : ''}
            </div>
          </div>
          <button className={css.closeBtn} style={{ color: '#fff' }} onClick={onClose}><CloseIcon /></button>
        </div>

        <div style={{ padding: '24px 28px', overflowY: 'auto' }}>
          {loading ? (
            <SkeletonTableRows rows={5} cols={6} />
          ) : examIsLive ? (
            <div style={{ textAlign: 'center', padding: '48px 24px' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>⏳</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', marginBottom: 8 }}>
                Exam in Progress
              </div>
              <div style={{ fontSize: '0.88rem', color: '#90a4ae', maxWidth: 340, margin: '0 auto' }}>
                This exam is currently live. Results and analytics will be available once the exam ends
                {endDate ? ` on ${endDate.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}.
              </div>
            </div>
          ) : !perfData ? (
            <div className={css.emptyState}>Failed to load performance data.</div>
          ) : (
            <>
              {/* ── Summary Stats ── */}
              <div className={css.perfCardRow} style={{ marginBottom: 24 }}>
                {[
                  { label: 'Assigned', value: perfData.summary?.totalAssigned ?? '—', color: '#1a6e73' },
                  { label: 'Submitted', value: perfData.summary?.totalSubmitted ?? '—', color: '#5fbec5' },
                  { label: 'Avg Score', value: perfData.summary?.avgPercentage != null ? `${perfData.summary.avgPercentage}%` : '—', color: '#fb8c00' },
                  { label: 'Pass Rate', value: perfData.summary?.passRate != null ? `${perfData.summary.passRate}%` : '—', color: '#43a047' },
                  { label: 'Not Attempted', value: (perfData.summary?.totalAssigned ?? 0) - (perfData.summary?.totalSubmitted ?? 0), color: '#e53935' },
                  { label: 'Retakes Pending', value: perfData.summary?.retakesPending ?? 0, color: '#d97706' },
                  { label: 'Retakes Active', value: perfData.summary?.retakesInProgress ?? 0, color: '#0f766e' },
                  { label: 'Total Score', value: exam.settings?.totalScore != null ? exam.settings.totalScore : '—', color: '#6d4c41' },
                ].map(({ label, value, color }) => (
                  <div key={label} className={css.perfCard} style={{ borderTopColor: color }}>
                    <div className={css.perfCardLabel}>{label}</div>
                    <div className={css.perfCardValue} style={{ color }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* ── Subject tabs (multi-subject exams only) ── */}
              {isMulti && (
                <div className={css.subjectTabRow}>
                  <button className={`${css.subjectTabPill} ${activeSubjectIdx === 'all' ? css.subjectTabPillActive : ''}`} onClick={() => setActiveSubjectIdx('all')}>All Subjects</button>
                  {perfData.subjectAnalytics.map(s => (
                    <button key={s.subjectIdx} className={`${css.subjectTabPill} ${activeSubjectIdx === s.subjectIdx ? css.subjectTabPillActive : ''}`} onClick={() => setActiveSubjectIdx(s.subjectIdx)}>{s.subjectName}</button>
                  ))}
                </div>
              )}

              {/* ── Score Distribution ── */}
              {perfData.students?.length > 0 && (
                <div className={css.distSection}>
                  <div className={css.drawerSectionTitle} style={{ marginBottom: 12 }}>
                    Score Distribution{currentSubjectData ? ` — ${currentSubjectData.subjectName}` : ''}
                  </div>
                  <div className={css.scoreDistRow}>
                    {SCORE_BUCKETS.map((b, i) => (
                      <div key={b.label} className={css.scoreDistBucket}>
                        <span style={{ fontWeight: 700, fontSize: '0.82rem', color: b.color }}>
                          {bucketCounts[i]}
                        </span>
                        <div
                          className={css.scoreDistBar}
                          style={{
                            background: b.color,
                            height: `${Math.max(4, Math.round((bucketCounts[i] / maxBucket) * 60))}px`,
                          }}
                        />
                        <span style={{ fontSize: '0.7rem', color: '#78909c' }}>{b.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Student Performance Table ── */}
              <div className={css.drawerSection}>
                <div className={css.perfTableHead}>
                  <div className={css.drawerSectionTitle} style={{ margin: 0 }}>
                    Student Performance
                    {anyFilterActive && (
                      <span className={css.perfFilterCount}>
                        {filteredStudents.length} of {perfData.students?.length || 0}
                      </span>
                    )}
                  </div>
                  {anyFilterActive && (
                    <button type="button" className={css.filterResetLink} onClick={resetFilters}>
                      Reset filters
                    </button>
                  )}
                </div>
                {canManageRetakes && selectedRetakeIds.length > 0 && (
                  <div style={{ position: 'sticky', top: 0, zIndex: 2, display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', marginBottom: 8, border: '1px solid #b7d8d5', borderRadius: 6, background: '#f0faf8' }}>
                    <strong style={{ fontSize: '0.8rem', color: '#185c57' }}>{selectedRetakeIds.length} selected</strong>
                    <button type="button" className={css.btnPrimary} onClick={() => setShowRetakeModal(true)}>Allow retake</button>
                    <button type="button" className={css.filterResetLink} onClick={() => setSelectedRetakeIds([])}>Clear</button>
                  </div>
                )}
                {isPendingManualApproval ? (
                  <div className={css.emptyState} role="status">
                    <p>This exam is pending school admin approval.</p>
                    <p>{exam.examMode === 'manual_scores'
                      ? 'Students will appear here for score recording once the school admin approves this exam.'
                      : 'Students will appear here for script upload and grading once the school admin approves this exam.'}</p>
                  </div>
                ) : !perfData.students?.length ? (
                  <div className={css.emptyState}>No submissions yet.</div>
                ) : (
                  <div className={css.tableWrap}>
                    <table className={css.table}>
                      <thead>
                        <tr>
                          {canManageRetakes && <th><input type="checkbox" aria-label="Select all eligible students currently shown" checked={allVisibleSelected} disabled={!selectableStudents.length} onChange={event => toggleAllVisible(event.target.checked)} /></th>}
                          <th>Student</th>
                          <th>Score</th>
                          <th>%</th>
                          <th>Time Spent</th>
                          <FilterHeader
                            label="Result"
                            value={resultFilter}
                            onChange={setResultFilter}
                            options={[
                              { value: 'all',  label: 'All' },
                              { value: 'pass', label: 'Pass' },
                              { value: 'fail', label: 'Fail' },
                            ]}
                          />
                          <FilterHeader
                            label="Risk"
                            value={riskFilter}
                            onChange={setRiskFilter}
                            options={[
                              { value: 'all',    label: 'All' },
                              { value: 'high',   label: 'High' },
                              { value: 'medium', label: 'Medium' },
                              { value: 'low',    label: 'Low' },
                            ]}
                          />
                          <FilterHeader
                            label="Flagged"
                            value={flaggedFilter}
                            onChange={setFlaggedFilter}
                            options={[
                              { value: 'all',         label: 'All' },
                              { value: 'flagged',     label: 'Flagged' },
                              { value: 'not_flagged', label: 'Not Flagged' },
                            ]}
                          />
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.length === 0 ? (
                            <tr>
                              <td colSpan={canManageRetakes ? 9 : 8} style={{ textAlign: 'center', padding: '28px 12px', color: '#90a4ae' }}>
                              No students match the selected filters.
                            </td>
                          </tr>
                        ) : filteredStudents.map(s => {
                          const rowExamMode = perfData.exam?.examMode || exam.examMode;
                          const manualNeedsUpload = s.attemptStatus !== 'missed' && rowExamMode === 'manual' && s.manualSubmission?.status !== 'confirmed';
                          const manualScoreNeedsEntry = s.attemptStatus !== 'missed' && rowExamMode === 'manual_scores' && s.manualScoreEntry?.status !== 'confirmed';
                          const mins = s.timeTaken ? Math.floor(s.timeTaken / 60) : 0;
                          const secs = s.timeTaken ? s.timeTaken % 60 : 0;
                          const timeLabel = s.timeTaken
                            ? (mins > 0 ? `${mins}m ${secs > 0 ? `${secs}s` : ''}`.trim() : `${secs}s`)
                            : '—';
                          const isFlagged = s.proctoring?.flaggedForReview;
                          return (
                            <tr
                              key={s.membershipId}
                              className={css.clickableRow}
                              title={manualNeedsUpload ? 'Upload handwritten script' : manualScoreNeedsEntry ? 'Enter student scores' : 'Click to view detailed performance'}
                              onClick={() => {
                                if (manualNeedsUpload || manualScoreNeedsEntry) {
                                  setSelectedMember(null);
                                  setManualMember({ membershipId: s.membershipId, firstName: s.firstName, lastName: s.lastName, className: s.className, classArm: s.classArm, scoreEntry: manualScoreNeedsEntry });
                                } else {
                                  setManualMember(null);
                                  setSelectedMember({ membershipId: s.membershipId, firstName: s.firstName, lastName: s.lastName });
                                }
                              }}
                            >
                              {canManageRetakes && <td onClick={event => event.stopPropagation()}><input type="checkbox" aria-label={`Select ${s.firstName} ${s.lastName} for retake`} checked={selectedRetakeIds.includes(String(s.membershipId))} disabled={!s.retakeEligibility?.eligible} title={s.retakeEligibility?.reason || ''} onChange={event => setSelectedRetakeIds(current => event.target.checked ? [...new Set([...current, String(s.membershipId)])] : current.filter(id => id !== String(s.membershipId)))} /></td>}
                              <td className={css.bold}>{s.firstName} {s.lastName} <RetakeBadge count={s.retakeCount} retake={s.retake} /></td>
                              <td>{s.score ?? '—'}</td>
                              <td style={{ color: (s.percentage ?? 0) >= 50 ? '#43a047' : '#e53935', fontWeight: 700 }}>
                                {s.percentage ?? 0}%
                              </td>
                              <td>{timeLabel}</td>
                              <td onClick={event => event.stopPropagation()}>
                                <StatusChip status={s.passed ? 'pass' : 'fail'} />
                              </td>
                              <td><RiskBadge risk={s.riskLevel} /></td>
                              <td>
                                {isFlagged ? (
                                  <span style={{
                                    background: '#fce4ec', color: '#c62828',
                                    fontSize: '0.7rem', fontWeight: 700,
                                    borderRadius: 6, padding: '2px 8px',
                                    border: '1px solid #ef9a9a', whiteSpace: 'nowrap',
                                  }}>⚑ Flagged</span>
                                ) : (
                                  <span style={{ color: '#b0bec5', fontSize: '0.75rem' }}>—</span>
                                )}
                              </td>
                              <td>
                                <div className={css.performanceRowActions}>
                                {manualNeedsUpload || manualScoreNeedsEntry ? (
                                  <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }} onClick={event => event.stopPropagation()}>
                                    <button type="button" className={adminCss.btnSecondary} style={{ padding: '3px 9px', fontSize: '0.72rem', lineHeight: 1.4 }} onClick={() => {
                                      setSelectedMember(null);
                                      setManualMember({ membershipId: s.membershipId, firstName: s.firstName, lastName: s.lastName, className: s.className, classArm: s.classArm, scoreEntry: manualScoreNeedsEntry });
                                    }}>{manualScoreNeedsEntry ? 'Add Score' : 'Add Script'}</button>
                                    <ManualExamMissedAction
                                      css={css}
                                      actionCss={adminCss}
                                      actor="teacher"
                                      examMode={rowExamMode}
                                      examId={exam._id}
                                      examTitle={exam.title}
                                      membershipId={s.membershipId}
                                      student={s}
                                      compact
                                      onMarked={() => {
                                        setLoading(true);
                                        teacherFetch(`/teacher/analytics/exam/${exam._id}`).then(result => {
                                          if (result.ok) setPerfData(result.data);
                                          setLoading(false);
                                        });
                                      }}
                                    />
                                  </div>
                                ) : rowExamMode !== 'cbt' && s.attemptStatus === 'missed' ? (
                                  <span style={{ background: '#fce4ec', color: '#c62828', fontSize: '0.7rem', fontWeight: 700, borderRadius: 6, padding: '2px 8px', border: '1px solid #ef9a9a', whiteSpace: 'nowrap' }}>Missed</span>
                                ) : <span className={css.rowCue}>View ›</span>}
                                {canManageRetakes && s.retakeEligibility?.eligible && <button type="button" className={adminCss.btnSecondary} style={{ marginLeft: 6, padding: '3px 8px', fontSize: '0.72rem', cursor: 'pointer' }} onClick={event => { event.stopPropagation(); setSelectedRetakeIds([String(s.membershipId)]); setShowRetakeModal(true); }}>Retake</button>}
                                {canManageRetakes && s.retake?.status === 'pending' && <button type="button" className={adminCss.btnDanger} style={{ marginLeft: 6, padding: '3px 8px', fontSize: '0.72rem', cursor: 'pointer' }} onClick={event => { event.stopPropagation(); cancelRetake(s.membershipId); }}>Cancel retake</button>}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {/* Show not-attempted students */}
                        {!anyFilterActive && (perfData.notAttempted || []).map(s => (
                          <tr key={s.membershipId} style={{ opacity: 0.55 }}>
                            <td>{s.firstName} {s.lastName}</td>
                            <td colSpan={canManageRetakes ? 7 : 6} style={{ color: '#90a4ae', fontStyle: 'italic' }}>Not attempted</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* ── Topic Performance ── */}
              {/* Hidden for the "All Subjects" pill on multi-subject exams — only
                  meaningful once a specific subject is selected (or the exam is single-subject). */}
              {topicData.length > 0 && !(isMulti && activeSubjectIdx === 'all') && (
                <div className={css.drawerSection}>
                  <div className={css.drawerSectionTitle}>Topic Performance{currentSubjectData ? ` — ${currentSubjectData.subjectName}` : ''}</div>
                  <div className={css.tableWrap}>
                    <table className={css.table}>
                      <thead>
                        <tr>
                          <th>Topic</th>
                          <th>Correct Rate</th>
                          <th>Progress</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topicData.map(t => {
                          const pct = t.avgCorrectRate ?? 0;
                          const color = pct >= 70 ? '#43a047' : pct >= 40 ? '#fb8c00' : '#e53935';
                          return (
                            <tr key={t.topic}>
                              <td>{t.topic}</td>
                              <td><span style={{ color, fontWeight: 700 }}>{pct}%</span></td>
                              <td style={{ minWidth: 120 }}>
                                <div style={{ height: 6, borderRadius: 3, background: '#eceff1', overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3 }} />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── Question Analysis (collapsible) ── */}
              {questionData.length > 0 && (
                <div className={css.drawerSection}>
                  <div className={css.drawerSectionTitle} style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', justifyContent: 'space-between' }} onClick={() => setQAnalysisOpen(o => !o)}>
                    <span>Question Analysis ({questionData.length} questions){currentSubjectData ? ` — ${currentSubjectData.subjectName}` : ''}</span>
                    <span>{qAnalysisOpen ? '▲' : '▼'}</span>
                  </div>
                  {qAnalysisOpen && (
                    <div className={css.tableWrap}>
                      <table className={css.table}>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Topic</th>
                            <th>Difficulty</th>
                            <th>Correct Rate</th>
                            <th>Avg Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {questionData.map((q, i) => {
                            const pct = q.correctRate ?? 0;
                            const color = pct >= 70 ? '#43a047' : pct >= 40 ? '#fb8c00' : '#e53935';
                            return (
                              <tr key={i}>
                                <td>{q.questionNumber ?? i + 1}</td>
                                <td>{q.topic || '—'}</td>
                                <td><span className={css.chip}>{q.difficulty || '—'}</span></td>
                                <td><span style={{ color, fontWeight: 700 }}>{pct}%</span></td>
                                <td>{q.avgTime ? `${q.avgTime}s` : '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ── Export buttons ── */}
              {!loading && perfData && !examIsLive && (
                <div className={css.drawerExportRow}>
                  <button className={css.drawerExportBtn} onClick={handlePDFExport}>
                    🖨 Export PDF
                  </button>
                  <button className={css.drawerExportBtn} style={{ background: 'linear-gradient(135deg,#1a6e73,#2a9d8f)' }} onClick={handleExcelExport}>
                    📊 Export Excel
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Per-student drill-down ── */}
      {selectedMember && (
        <StudentExamDetailDrawer
          examId={exam._id}
          membershipId={selectedMember.membershipId}
          student={selectedMember}
          onClose={() => setSelectedMember(null)}
        />
      )}
      {manualMember && (
        manualMember.scoreEntry ? <ManualScoreEntryDrawer
          css={adminCss}
          actionCss={adminCss}
          actor="teacher"
          examId={exam._id}
          examTitle={exam.title}
          membershipId={manualMember.membershipId}
          student={manualMember}
          onClose={() => setManualMember(null)}
          onConfirmed={() => {
            setManualMember(null);
            setLoading(true);
            onChanged?.();
            teacherFetch(`/teacher/analytics/exam/${exam._id}`).then(result => {
              if (result.ok) setPerfData(result.data);
              setLoading(false);
            });
          }}
          onMissed={() => {
            setManualMember(null);
            setLoading(true);
            onChanged?.();
            teacherFetch(`/teacher/analytics/exam/${exam._id}`).then(result => {
              if (result.ok) setPerfData(result.data);
              setLoading(false);
            });
          }}
        /> : <ManualScriptUploadDrawer
          css={css}
          uploadCss={adminCss}
          actionCss={adminCss}
          actor="teacher"
          examId={exam._id}
          examTitle={exam.title}
          membershipId={manualMember.membershipId}
          student={manualMember}
          onClose={() => setManualMember(null)}
          onConfirmed={() => {
            setManualMember(null);
            setLoading(true);
            teacherFetch(`/teacher/analytics/exam/${exam._id}`).then(result => {
              if (result.ok) setPerfData(result.data);
              setLoading(false);
            });
          }}
          onMissed={() => {
            setManualMember(null);
            setLoading(true);
            teacherFetch(`/teacher/analytics/exam/${exam._id}`).then(result => {
              if (result.ok) setPerfData(result.data);
              setLoading(false);
            });
          }}
        />
      )}
      {showRetakeModal && selectedRetakeStudents.length > 0 && (
        <RetakeAssignmentModal
          exam={exam}
          selectedStudents={selectedRetakeStudents}
          actor="teacher"
          fetchFn={teacherFetch}
          dateTimePicker={DateTimePicker}
          classes={css}
          reportCardReleased={perfData?.exam?.reportCardReleased}
          renderQuestionEditor={editor => <TeacherRetakeQuestionEditor {...editor} examId={exam._id} />}
          onClose={() => { setShowRetakeModal(false); setSelectedRetakeIds([]); }}
          onComplete={() => { setSelectedRetakeIds([]); reloadAnalytics(); onChanged?.(); }}
        />
      )}
    </div>
  );
}

// ─── Exams Tab ────────────────────────────────────────────────────────────────
function ExamsTab({ subjects, classes, aiEnabled, school }) {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterTerm, setFilterTerm] = useState('');
  const [filterSession, setFilterSession] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [showModeChoice, setShowModeChoice] = useState(false);
  const [createExamMode, setCreateExamMode] = useState('cbt');
  const [editExam, setEditExam] = useState(null);
  const [viewResultsExam, setViewResultsExam] = useState(null);
  const [pendingCategory, setPendingCategory] = useState(null); // { examId, category, label }
  // Delete confirm modal state
  const [deleteConfirmExam, setDeleteConfirmExam] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  // Duplicate state
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [loadError, setLoadError] = useState('');
  const examListRequestRef = useRef(0);

  const examHasStarted = (exam) => exam.settings?.startDate && new Date(exam.settings.startDate) <= new Date();

  const loadExams = useCallback(async () => {
    const requestId = ++examListRequestRef.current;
    setLoading(true);
    setLoadError('');
    let requestedPage = page;
    try {
      while (true) {
        const params = new URLSearchParams({ page: requestedPage, limit: 15 });
        if (filterStatus) params.set('status', filterStatus);
        if (filterSubject) params.set('subjectId', filterSubject);
        if (filterTerm) params.set('term', filterTerm);
        if (filterSession) params.set('session', filterSession);
        const { ok, data } = await teacherFetch(`/teacher/exams?${params}`);
        if (requestId !== examListRequestRef.current) return;
        if (!ok) {
          setLoadError(data.error || 'Failed to reload exams. Please retry.');
          return;
        }
        if (!Array.isArray(data.exams) || !data.pagination) {
          setLoadError('Could not load the exam list. Please retry.');
          return;
        }
        const lastPage = Math.max(1, data.pagination.pages || 0);
        if (requestedPage > lastPage) {
          requestedPage = lastPage;
          continue;
        }
        setExams(data.exams);
        setPagination(data.pagination);
        if (requestedPage !== page) setPage(requestedPage);
        return;
      }
    } catch {
      if (requestId === examListRequestRef.current) setLoadError('Connection error. Could not reload exams. Please retry.');
    } finally {
      if (requestId === examListRequestRef.current) setLoading(false);
    }
  }, [page, filterStatus, filterSubject, filterTerm, filterSession]);

  useEffect(() => { loadExams(); }, [loadExams]);

  useSchoolExamSync(loadExams, id => {
    ++examListRequestRef.current;
    setExams(current => current.filter(exam => String(exam._id) !== id));
    setEditExam(current => String(current?._id) === id ? null : current);
    setViewResultsExam(current => String(current?._id) === id ? null : current);
    setDeleteConfirmExam(current => String(current?._id) === id ? null : current);
  });

  const handleDelete = (exam) => {
    setDeletePassword('');
    setDeleteError('');
    setDeleteConfirmExam(exam);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmExam) return;
    const isDraft = deleteConfirmExam.status === 'draft';
    if (!isDraft && !deletePassword.trim()) {
      setDeleteError('Please enter your password to confirm deletion.');
      return;
    }
    setIsDeleting(true);
    setDeleteError('');
    const body = isDraft ? {} : { password: deletePassword };
    const { ok, data } = await teacherFetch(`/teacher/exams/${deleteConfirmExam._id}`, {
      method: 'DELETE',
      body: JSON.stringify(body),
    });
    setIsDeleting(false);
    if (ok) {
      setDeleteConfirmExam(null);
      setDeletePassword('');
      setExams(current => current.filter(exam => exam._id !== deleteConfirmExam._id));
      await loadExams();
    } else {
      setDeleteError(data.error || 'Delete failed');
    }
  };

  const handleDuplicate = async (exam) => {
    setDuplicatingId(exam._id);
    const { ok, data } = await teacherFetch(`/teacher/exams/${exam._id}/duplicate`, { method: 'POST' });
    setDuplicatingId(null);
    if (ok && data.exam) {
      setExams(prev => [data.exam, ...prev]);
    } else {
      alert(data.error || 'Duplicate failed');
    }
  };

  const handleSetCategory = async (id, category) => {
    const { ok, data } = await teacherFetch(`/teacher/exams/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ examCategory: category }),
    });
    if (ok) loadExams();
    else alert(data?.error || 'Update failed');
  };

  const handleCategoryClick = (exam, category, label) => {
    if (examHasStarted(exam)) return; // already guarded by button disabled, but safety net
    setPendingCategory({ examId: exam._id, category, label });
  };

  const confirmCategorySwitch = async () => {
    if (!pendingCategory) return;
    const { examId, category } = pendingCategory;
    setPendingCategory(null);
    await handleSetCategory(examId, category);
  };

  return (
    <div>
      <div className={css.tabBar}>
        <h2 className={css.tabTitle}>My Exams</h2>
        <div className={css.tabBarRight}>
          <select className={css.filterSelect} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="closed">Closed</option>
          </select>
          <select className={css.filterSelect} value={filterSubject} onChange={e => { setFilterSubject(e.target.value); setPage(1); }}>
            <option value="">All Subjects</option>
            {(subjects || []).map(s => (
              <option key={s._id} value={s._id}>{s.name}</option>
            ))}
          </select>
          <select className={css.filterSelect} value={filterTerm} onChange={e => { setFilterTerm(e.target.value); setPage(1); }}>
            <option value="">All Terms</option>
            <option value="first">First Term</option>
            <option value="second">Second Term</option>
            <option value="third">Third Term</option>
          </select>
          <input
            className={css.filterSelect}
            style={{ width: 120 }}
            type="text"
            name="examSessionFilter"
            autoComplete="off"
            placeholder="Session e.g. 2025/2026"
            value={filterSession}
            onChange={e => { setFilterSession(e.target.value); setPage(1); }}
          />
          <button className={css.primaryBtn} onClick={() => setShowModeChoice(true)}><AddIcon fontSize="small" /> New Exam</button>
        </div>
      </div>

      {/* Active term context — so teachers know which term new exams will be stamped with */}
      {school?.currentTerm && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#f0fafb', border: '1px solid #b2e0e3', borderRadius: 9,
          padding: '8px 14px', marginBottom: 12, fontSize: '0.83rem', color: '#1a6e73',
        }}>
          <span style={{ fontWeight: 700 }}>Active Term:</span>
          <span>
            {school.currentTerm === 'first' ? 'First' : school.currentTerm === 'second' ? 'Second' : 'Third'} Term
            {school.academicSession ? ` · ${school.academicSession}` : ''}
          </span>
          <span style={{ color: '#90a4ae', fontSize: '0.75rem', marginLeft: 4 }}>
            (New exams will be tagged to this term automatically)
          </span>
        </div>
      )}

      {loadError && (
        <div className={css.inlineError} role="alert">
          {loadError}
          <button type="button" className={css.pageBtn} onClick={loadExams}>Retry</button>
        </div>
      )}
      {loading ? (
        <SkeletonTableRows rows={5} cols={10} />
      ) : exams.length === 0 ? (
        !loadError && <EmptyState message="No exams yet. Create your first one!" />
      ) : (
        <div className={css.tableWrap}>
          <table className={`${css.table} ${css.examsTable}`}>
            <thead>
              <tr>
                <th>Title</th>
                <th>Subject</th>
                <th>Class</th>
                <th>Term</th>
                <th>Session</th>
                <th>Questions</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {exams.map(exam => (
                <tr key={exam._id}>
                  <td className={css.bold}>
                    {exam.title}
                    {exam.status === 'draft' && exam.approval?.rejectionReason && (
                      <div
                        style={{
                          marginTop: 6, display: 'flex', alignItems: 'flex-start', gap: 6,
                          background: '#fff2f2', border: '1px solid #fca5a5', borderRadius: 8,
                          padding: '6px 10px', fontSize: '0.74rem', color: '#c62828', fontWeight: 500, maxWidth: 260,
                        }}
                        title={exam.approval.rejectionReason}
                      >
                        <span style={{ flexShrink: 0 }}>⚠ Rejected:</span>
                        <span style={{ fontWeight: 400 }}>{exam.approval.rejectionReason}</span>
                      </div>
                    )}
                  </td>
                  <td>
                    {exam.isMultiSubject ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                        <span
                          className={css.chip}
                          style={{ background: 'rgba(95,190,197,0.14)', color: '#0f3135', border: '1px solid rgba(95,190,197,0.4)' }}
                        >
                          Multi-Subject
                        </span>
                        <span style={{ fontSize: '0.78rem', color: '#546e7a' }}>
                          {(exam.subjects || [])
                            .filter(s => (subjects || []).some(ts => String(ts._id) === String(s.subjectId)))
                            .map(s => s.subjectName)
                            .join(', ') || '—'}
                        </span>
                      </div>
                    ) : (
                      exam.subjectName || '—'
                    )}
                  </td>
                  <td>{exam.classId?.name || '—'} {exam.classArm ? `(${exam.classArm})` : ''}</td>
                  <td>{exam.settings?.term ? (exam.settings.term === 'first' ? 'First' : exam.settings.term === 'second' ? 'Second' : 'Third') : '—'}</td>
                  <td>{exam.settings?.session || '—'}</td>
                  <td>{(exam.questions || []).length}</td>
                  <td className={css.dateCell}>
                    {renderExamDate(exam.settings?.startDate)}
                  </td>
                  <td className={css.dateCell}>
                    {renderExamDate(exam.settings?.endDate)}
                  </td>
                  <td><StatusChip status={examDisplayStatus(exam)} /></td>
                  <td>
                    <ExamActionsMenu
                      css={css}
                      actions={[
                        exam.status !== 'closed' && (exam.status === 'draft' || !examHasStarted(exam)) && {
                          key: 'edit', label: 'Edit exam', icon: <EditIcon fontSize="small" />, onClick: () => setEditExam(exam),
                        },
                        (exam.status === 'draft' || (exam.status === 'scheduled' && !examHasStarted(exam))) && {
                          key: 'duplicate', label: 'Duplicate exam', icon: <ContentCopyIcon fontSize="small" />, accent: true,
                          onClick: () => handleDuplicate(exam), disabled: duplicatingId === exam._id,
                        },
                        ['draft', 'scheduled', 'pending_approval'].includes(exam.status) && (exam.status === 'draft' || (
                          exam.settings?.term === school?.currentTerm && exam.settings?.session === school?.academicSession
                        )) && {
                          key: 'delete', label: 'Delete exam', icon: <DeleteIcon fontSize="small" />, danger: true,
                          onClick: () => handleDelete(exam), disabled: isDeleting && deleteConfirmExam?._id === exam._id,
                        },
                        exam.status !== 'draft' && {
                          key: 'results', label: exam.status === 'pending_approval' ? 'View results' : exam.examMode === 'manual_scores' ? 'Record/View Scores' : exam.examMode === 'manual' && exam.status === 'closed' && exam.manualGradingStatus === 'ready' ? 'Upload Scripts to Grade' : 'View results', icon: <BarChartIcon fontSize="small" />, accent: true,
                          onClick: () => setViewResultsExam(exam),
                        },
                        exam.status !== 'draft' && exam.settings?.resultVisibility === 'manual' && !exam.resultsReleasedAt && {
                          key: 'release', label: 'Release results to students', icon: <LockIcon fontSize="small" />,
                          onClick: async () => {
                            if (!window.confirm(`Release results to all students who submitted this exam?\n\nThey will be notified and can view their grades immediately.`)) return;
                            const { ok, data } = await teacherFetch(`/teacher/exams/${exam._id}/release-results`, { method: 'POST' });
                            if (ok) {
                              alert(data.message || 'Results released successfully!');
                              loadExams();
                            } else {
                              alert(data.error || 'Failed to release results');
                            }
                          },
                        },
                      ].filter(Boolean)}
                      releasedAt={exam.resultsReleasedAt}
                      categories={['ca', 'exam', 'test'].map(key => {
                        const labels = { ca: 'CA', exam: 'Exam', test: 'Test' };
                        const active = (exam.isMultiSubject ? 'test' : (exam.examCategory || 'test')) === key;
                        const started = examHasStarted(exam);
                        const multiSubjectLocked = exam.isMultiSubject && key !== 'test';
                        return {
                          key, label: labels[key], active, disabled: active || started || multiSubjectLocked,
                          title: multiSubjectLocked ? 'Multi-subject exams must use the Test category' : active ? `Currently set as ${labels[key]}` : started ? 'Cannot change category — exam has already started' : `Switch to ${labels[key]}`,
                          onClick: () => handleCategoryClick(exam, key, labels[key]),
                        };
                      })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination.pages > 1 && (
        <div className={css.pagination}>
          <button className={css.pageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
          <span className={css.pageInfo}>{page} / {pagination.pages}</span>
          <button className={css.pageBtn} disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>Next ›</button>
        </div>
      )}

      {showModeChoice && (
        <ExamModeChoiceModal
          css={css}
          onClose={() => setShowModeChoice(false)}
          onSelect={mode => { setCreateExamMode(mode); setShowModeChoice(false); setShowCreate(true); }}
        />
      )}
      {showCreate && (
        <ExamModal
          exam={null}
          subjects={subjects}
          classes={classes}
          aiEnabled={aiEnabled}
          school={school}
          initialExamMode={createExamMode}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); loadExams(); }}
          onDraftSaved={loadExams}
        />
      )}
      {editExam && (
        <ExamModal
          exam={editExam}
          subjects={subjects}
          classes={classes}
          aiEnabled={aiEnabled}
          school={school}
          onClose={() => setEditExam(null)}
          onSaved={() => { setEditExam(null); loadExams(); }}
          onDraftSaved={loadExams}
        />
      )}
      {viewResultsExam && (
        <ExamPerformanceModal exam={viewResultsExam} onClose={() => setViewResultsExam(null)} onChanged={loadExams} />
      )}
      {pendingCategory && (
        <div className={css.modalOverlay} onClick={() => setPendingCategory(null)}>
          <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h2 className={css.modalTitle}>Change Exam Category</h2>
              <button className={css.closeBtn} onClick={() => setPendingCategory(null)}>✕</button>
            </div>
            <div className={css.modalBody}>
              <p style={{ margin: 0, color: '#455a64', fontSize: '0.92rem' }}>
                Are you sure you want to switch this exam's category to <strong>{pendingCategory.label}</strong>?
                This will affect how it is counted in term reports.
              </p>
            </div>
            <div className={css.modalFooter}>
              <button className={css.cancelBtn} onClick={() => setPendingCategory(null)}>Cancel</button>
              <button className={css.saveBtn} onClick={confirmCategorySwitch}>Confirm</button>
            </div>
          </div>
        </div>
      )}
      {/* ── Delete confirm modal ── */}
      {deleteConfirmExam && (
        <div className={css.modalOverlay} onClick={() => { if (!isDeleting) { setDeleteConfirmExam(null); setDeletePassword(''); setDeleteError(''); } }}>
          <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h2 className={css.modalTitle}>Delete Exam</h2>
              <button className={css.closeBtn} onClick={() => { setDeleteConfirmExam(null); setDeletePassword(''); setDeleteError(''); }} disabled={isDeleting}>✕</button>
            </div>
            <div className={css.modalBody}>
              <p style={{ margin: '0 0 12px', color: '#455a64', fontSize: '0.92rem' }}>
                Permanently delete <strong>&ldquo;{deleteConfirmExam.title}&rdquo;</strong>? This removes its assigned attempts, current and archived results, uploaded scripts, linked calendar event, and related inbox notifications. This cannot be undone.
              </p>
              {deleteConfirmExam.status !== 'draft' && (
                <>
                  <p style={{ margin: '0 0 10px', color: '#b91c1c', fontSize: '0.88rem', fontWeight: 600 }}>
                    This exam has already been published. Enter your password to confirm deletion.
                  </p>
                  <SchoolPasswordInput
                    className={css.formInput}
                    name="deleteExamConfirmPassword"
                    autoComplete="new-password"
                    placeholder="Your password"
                    value={deletePassword}
                    onChange={e => { setDeletePassword(e.target.value); setDeleteError(''); }}
                    autoFocus
                    onKeyDown={e => e.key === 'Enter' && handleDeleteConfirm()}
                  />
                </>
              )}
              {deleteError && (
                <p style={{ margin: '8px 0 0', color: '#dc2626', fontSize: '0.85rem' }}>{deleteError}</p>
              )}
            </div>
            <div className={css.modalFooter}>
              <button className={css.cancelBtn} onClick={() => { setDeleteConfirmExam(null); setDeletePassword(''); setDeleteError(''); }} disabled={isDeleting}>Cancel</button>
              <button
                className={css.saveBtn}
                style={{ background: '#dc2626' }}
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Add Question to Bank Modal ───────────────────────────────────────────────
function AddQBankModal({ subjects, onClose, onAdded }) {
  const [form, setForm] = useState({ question_text: '', correct_answer: '', options: ['', '', '', ''], subjectId: '', topic: '', difficulty: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.question_text || !form.correct_answer) { setError('Question text and correct answer are required'); return; }
    setLoading(true); setError('');
    try {
      const { ok, data } = await teacherFetch('/teacher/question-bank', { method: 'POST', body: JSON.stringify(form) });
      if (ok) { onAdded(data.question); }
      else setError(data.error || 'Failed to save');
    } catch { setError('Connection error'); }
    finally { setLoading(false); }
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Add to Question Bank</h2>
        {error && <div className={css.inlineError}>{error}</div>}
        <div className={css.formRow}>
          <label>Question *</label>
          <textarea className={css.formTextarea} rows={3} value={form.question_text} onChange={set('question_text')} placeholder="Enter question text…" />
        </div>
        <div className={css.optionGrid}>
          {[0, 1, 2, 3].map(i => (
            <input
              key={i}
              className={css.optInput}
              placeholder={`Option ${String.fromCharCode(65 + i)}`}
              value={form.options[i] || ''}
              onChange={e => {
                const opts = [...form.options];
                opts[i] = e.target.value;
                setForm(f => ({ ...f, options: opts }));
              }}
            />
          ))}
        </div>
        <div className={css.formRow}>
          <label>Correct Answer *</label>
          <select className={css.formSelect} value={form.correct_answer} onChange={set('correct_answer')}>
            <option value="">— select —</option>
            {form.options.filter(Boolean).map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
          </select>
        </div>
        <div className={css.formGrid2}>
          <div className={css.formRow}>
            <label>Subject</label>
            <select className={css.formSelect} value={form.subjectId} onChange={set('subjectId')}>
              <option value="">—</option>
              {subjects.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
          </div>
          <div className={css.formRow}>
            <label>Topic</label>
            <input className={css.formInput} value={form.topic} onChange={set('topic')} placeholder="e.g. Photosynthesis" />
          </div>
          <div className={css.formRow}>
            <label>Difficulty</label>
            <select className={css.formSelect} value={form.difficulty} onChange={set('difficulty')}>
              <option value="">—</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
        </div>
        <div className={css.modalFooter}>
          <button className={css.cancelBtn} onClick={onClose}>Cancel</button>
          <button className={css.saveBtn} onClick={handleSave} disabled={loading}>{loading ? 'Saving…' : 'Add Question'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Question Bank Tab ────────────────────────────────────────────────────────
function QuestionBankTab({ subjects }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subjectId, setSubjectId] = useState('');
  const [topic, setTopic] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 20 });
    if (subjectId) params.set('subjectId', subjectId);
    if (topic) params.set('topic', topic);
    if (difficulty) params.set('difficulty', difficulty);
    const { ok, data } = await teacherFetch(`/teacher/question-bank?${params}`);
    if (ok) { setQuestions(data.questions || []); setPagination(data.pagination || {}); }
    setLoading(false);
  }, [page, subjectId, topic, difficulty]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className={css.tabBar}>
        <h2 className={css.tabTitle}>Question Bank</h2>
        <button className={css.primaryBtn} onClick={() => setShowAdd(true)}><AddIcon fontSize="small" /> Add Question</button>
      </div>

      <div className={css.filterRow}>
        <select className={css.filterSelect} value={subjectId} onChange={e => { setSubjectId(e.target.value); setPage(1); }}>
          <option value="">All Subjects</option>
          {subjects.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
        <input className={css.filterInput} placeholder="Filter by topic…" value={topic} onChange={e => { setTopic(e.target.value); setPage(1); }} />
        <select className={css.filterSelect} value={difficulty} onChange={e => { setDifficulty(e.target.value); setPage(1); }}>
          <option value="">Any Difficulty</option>
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
      </div>

      {loading ? (
        <SkeletonTableRows rows={5} cols={5} />
      ) : questions.length === 0 ? (
        <EmptyState message="No questions in bank yet. Add one above." />
      ) : (
        <div className={css.tableWrap}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Question</th>
                <th>Subject</th>
                <th>Topic</th>
                <th>Difficulty</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {questions.map(q => (
                <tr key={q._id}>
                  <td className={css.qText}>{q.question_text}</td>
                  <td>{q.subjectName || '—'}</td>
                  <td>{q.topic || '—'}</td>
                  <td><span className={css.diffChip}>{q.difficulty || '—'}</span></td>
                  <td>{q.source || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination.pages > 1 && (
        <div className={css.pagination}>
          <button className={css.pageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
          <span className={css.pageInfo}>{page} / {pagination.pages}</span>
          <button className={css.pageBtn} disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>Next ›</button>
        </div>
      )}

      {showAdd && (
        <AddQBankModal
          subjects={subjects}
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── Curriculum Tab ───────────────────────────────────────────────────────────
// Curriculum is per (subject, class) — a subject like Maths can be taken by
// several class levels, each with its own curriculum file/topics/summary.
function CurriculumTab({ subjects: assignedSubjects, school }) {
  const curriculumSetByAdmin = !!school?.controlSettings?.curriculumSetByAdmin;
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState('');
  const [deleting, setDeleting] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewRow, setViewRow] = useState(null);
  const fileInputRef = useRef(null);
  const [pendingRow, setPendingRow] = useState(null); // { subjectId, classId }
  // Coverage state — keyed by `${subjectId}:${classId}`
  const [coverage, setCoverage] = useState({});
  const [coverageLoading, setCoverageLoading] = useState({});
  const [coverageOpen, setCoverageOpen] = useState({});

  // Flatten subjects → one row per (subject, class)
  const rows = subjects.flatMap(sub =>
    (sub.classes || []).map(cls => ({
      key: `${sub._id}:${cls._id}`,
      subjectId: sub._id,
      subjectName: sub.name,
      classId: cls._id,
      className: cls.name,
      curriculum: cls.curriculum,
    }))
  );

  const load = useCallback(async () => {
    setLoading(true);
    const { ok, data } = await teacherFetch('/teacher/curriculum');
    if (ok) {
      const subs = data.subjects || [];
      setSubjects(subs);
      // Auto-open coverage for rows that already have curriculum with topics
      const initOpen = {};
      subs.forEach(s => (s.classes || []).forEach(c => {
        if (c.curriculum?.topics?.length > 0) initOpen[`${s._id}:${c._id}`] = true;
      }));
      setCoverageOpen(prev => ({ ...initOpen, ...prev }));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-load coverage when a row with curriculum is first seen
  useEffect(() => {
    rows.forEach(row => {
      if (row.curriculum?.topics?.length > 0 && !coverage[row.key] && !coverageLoading[row.key]) {
        loadCoverage(row.subjectId, row.classId);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjects]);

  const loadCoverage = async (subjectId, classId) => {
    const key = `${subjectId}:${classId}`;
    setCoverageLoading(prev => ({ ...prev, [key]: true }));
    const { ok, data } = await teacherFetch(`/teacher/curriculum/coverage/${subjectId}/${classId}`);
    if (ok) setCoverage(prev => ({ ...prev, [key]: data.coverage }));
    setCoverageLoading(prev => ({ ...prev, [key]: false }));
  };

  const triggerUpload = (subjectId, classId) => {
    if (curriculumSetByAdmin) return;
    setPendingRow({ subjectId, classId });
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !pendingRow) return;
    const { subjectId, classId } = pendingRow;
    const key = `${subjectId}:${classId}`;
    const allowed = ['application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword', 'text/plain'];
    if (!allowed.includes(file.type)) { setError('Unsupported file type. Use PDF, DOCX, or TXT.'); return; }
    if (file.size > 20 * 1024 * 1024) { setError('File too large. Maximum 20 MB.'); return; }
    setUploading(key); setError(''); setSuccess('');
    try {
      const { ok: urlOk, data: urlData } = await teacherFetch('/teacher/curriculum/upload-url', {
        method: 'POST',
        body: JSON.stringify({ subjectId, classId, fileName: file.name, contentType: file.type, fileSize: file.size }),
      });
      if (!urlOk) { setError(urlData.error || 'Failed to get upload URL'); return; }
      const uploadRes = await fetch(urlData.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      if (!uploadRes.ok) { setError('File upload to storage failed. Try again.'); return; }
      setSuccess('Extracting topics and summary…');
      const { ok: confirmOk, data: confirmData } = await teacherFetch('/teacher/curriculum/confirm', {
        method: 'POST',
        body: JSON.stringify({ subjectId, classId, key: urlData.key, originalName: file.name }),
      });
      if (!confirmOk) { setError(confirmData.error || 'Failed to process curriculum'); setSuccess(''); return; }
      setSuccess('Curriculum uploaded and processed successfully!');
      setTimeout(() => setSuccess(''), 5000);
      // Reset coverage for replaced curriculum
      setCoverage(prev => { const n = { ...prev }; delete n[key]; return n; });
      load();
    } catch { setError('Connection error. Try again.'); setSuccess(''); }
    finally { setUploading(''); setPendingRow(null); }
  };

  const handleDelete = async (subjectId, classId) => {
    if (curriculumSetByAdmin) return;
    if (!window.confirm('Delete this curriculum? This cannot be undone.')) return;
    const key = `${subjectId}:${classId}`;
    setDeleting(key); setError('');
    try {
      const { ok, data } = await teacherFetch(`/teacher/curriculum/${subjectId}/${classId}`, { method: 'DELETE' });
      if (!ok) { setError(data.error || 'Failed to delete'); return; }
      setSuccess('Curriculum deleted.'); setTimeout(() => setSuccess(''), 3000);
      setCoverage(prev => { const n = { ...prev }; delete n[key]; return n; });
      load();
    } catch { setError('Connection error.'); }
    finally { setDeleting(''); }
  };

  return (
    <div>
      <div className={css.tabBar}>
        <h2 className={css.tabTitle}>Curriculum</h2>
        <span className={css.tabSubtitle}>{curriculumSetByAdmin ? 'Curriculum is managed by your school admin. You can view uploaded curriculum below.' : 'Upload curriculum documents per class. AI extracts topics automatically for exam generation.'}</span>
      </div>
      <input ref={fileInputRef} type="file" accept=".pdf,.docx,.doc,.txt" style={{ display: 'none' }} onChange={handleFileChange} />
      {curriculumSetByAdmin && (
        <div className={css.lockedBanner} style={{ marginBottom: 14 }}>🔒 Curriculum uploads are managed by your school admin.</div>
      )}
      {error && <div className={css.inlineError}>{error}</div>}
      {success && <div className={css.inlineSuccess}>{success}</div>}

      {loading ? <SkeletonTableRows rows={4} cols={4} /> : rows.length === 0 ? (
        <EmptyState message="No classes assigned for your subjects yet. Contact your school admin." />
      ) : (
        <div className={css.curriculumGrid}>
          {rows.map(row => {
            const hasCurriculum = !!(row.curriculum?.key);
            const isUploading = uploading === row.key;
            const isDeleting  = deleting  === row.key;
            const cov = coverage[row.key];
            const covLoading = !!coverageLoading[row.key];
            const isCovOpen = !!coverageOpen[row.key];

            return (
              <div key={row.key} className={`${css.curriculumCard} ${hasCurriculum ? css.curriculumCardFilled : ''}`}>
                <div className={css.curriculumCardHeader}>
                  <MenuBookIcon fontSize="small" style={{ color: hasCurriculum ? '#1a6e73' : '#90a4ae' }} />
                  <span className={css.curriculumCardName}>{row.subjectName} <span style={{ color: '#78909c', fontWeight: 500 }}>· {row.className}</span></span>
                  {hasCurriculum && <span className={css.curriculumBadge}>Uploaded</span>}
                </div>

                {hasCurriculum ? (
                  <>
                    <div className={css.curriculumCardMeta}>
                      <span>{row.curriculum.originalName}</span>
                      <span style={{ color: '#90a4ae', fontSize: '0.75rem' }}>
                        {row.curriculum.uploadedAt ? new Date(row.curriculum.uploadedAt).toLocaleDateString() : ''}
                      </span>
                    </div>

                    {/* Topic chips preview (first 6) */}
                    {row.curriculum.topics?.length > 0 && (
                      <div className={css.curriculumTopics}>
                        {row.curriculum.topics.slice(0, 6).map((t, i) => (
                          <span key={i} className={css.curriculumTopicChip}>{t}</span>
                        ))}
                        {row.curriculum.topics.length > 6 && (
                          <span className={css.curriculumTopicMore}>+{row.curriculum.topics.length - 6} more</span>
                        )}
                      </div>
                    )}

                    {/* Coverage section */}
                    {row.curriculum.topics?.length > 0 && (
                      <div className={css.coverageSection}>
                        <button
                          className={css.coverageToggleBtn}
                          onClick={() => setCoverageOpen(prev => ({ ...prev, [row.key]: !isCovOpen }))}
                          type="button"
                        >
                          <span>Topic Coverage</span>
                          {cov && !covLoading && (
                            <span className={css.coverageSummaryBadge}>
                              {cov.coveredCount}/{cov.total}
                            </span>
                          )}
                          <span className={css.coverageChevron}>{isCovOpen ? '▲' : '▼'}</span>
                        </button>

                        <div className={`${css.coverageBody} ${isCovOpen ? css.coverageBodyOpen : ''}`}>
                          {covLoading ? (
                            <div className={css.coverageLoading}>Checking coverage…</div>
                          ) : cov ? (
                            <>
                              {/* Progress bar */}
                              <div className={css.coverageBarWrap}>
                                <div className={css.coverageBar}>
                                  <div
                                    className={css.coverageBarFill}
                                    style={{ width: cov.total ? `${Math.round((cov.coveredCount / cov.total) * 100)}%` : '0%' }}
                                  />
                                </div>
                                <span className={css.coverageBarLabel}>
                                  {cov.coveredCount} of {cov.total} topics have exam questions
                                  {cov.total > 0 && ` (${Math.round((cov.coveredCount / cov.total) * 100)}%)`}
                                </span>
                              </div>
                              {/* Topic chips: covered vs uncovered */}
                              <div className={css.coverageTopicsGrid}>
                                {cov.topics.map((t, i) => (
                                  <span
                                    key={i}
                                    className={`${css.coverageTopicChip} ${t.covered ? css.coverageTopicChipCovered : css.coverageTopicChipUncovered}`}
                                    title={t.covered ? `${t.questionCount} question${t.questionCount !== 1 ? 's' : ''}` : 'No questions yet'}
                                  >
                                    {t.covered ? '✓' : '○'} {t.name}
                                    {t.covered && t.questionCount > 0 && (
                                      <span className={css.coverageTopicCount}>{t.questionCount}</span>
                                    )}
                                  </span>
                                ))}
                              </div>
                              <button
                                className={css.coverageRefreshBtn}
                                onClick={() => loadCoverage(row.subjectId, row.classId)}
                                type="button"
                              >
                                ↺ Refresh coverage
                              </button>
                            </>
                          ) : (
                            <div className={css.coverageLoading}>
                              <button className={css.coverageRefreshBtn} onClick={() => loadCoverage(row.subjectId, row.classId)} type="button">
                                Load coverage data
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className={css.curriculumCardActions}>
                      <button className={css.curriculumActionBtn} onClick={() => setViewRow(row)} title="View details">
                        <VisibilityIcon fontSize="small" /> View
                      </button>
                      <a className={css.curriculumActionBtn} href={row.curriculum.url} download={row.curriculum.originalName} title="Download">
                        <DownloadIcon fontSize="small" /> Download
                      </a>
                      {!curriculumSetByAdmin && (
                        <>
                          <button className={css.curriculumActionBtn} onClick={() => triggerUpload(row.subjectId, row.classId)} disabled={isUploading} title="Replace">
                            <UploadFileIcon fontSize="small" /> {isUploading ? 'Uploading…' : 'Replace'}
                          </button>
                          <button
                            className={`${css.curriculumActionBtn} ${css.curriculumActionBtnDanger}`}
                            onClick={() => handleDelete(row.subjectId, row.classId)} disabled={isDeleting} title="Delete"
                          >
                            <DeleteIcon fontSize="small" /> {isDeleting ? '…' : 'Delete'}
                          </button>
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <div className={css.curriculumUploadArea}>
                    <p className={css.curriculumUploadHint}>
                      {curriculumSetByAdmin
                        ? `No curriculum uploaded yet for ${row.className}. Your school admin manages curriculum uploads.`
                        : `No curriculum uploaded yet for ${row.className}. Supported: PDF, DOCX, TXT (max 20 MB)`}
                    </p>
                    {!curriculumSetByAdmin && (
                      <button className={css.primaryBtn} onClick={() => triggerUpload(row.subjectId, row.classId)} disabled={isUploading}>
                        <UploadFileIcon fontSize="small" /> {isUploading ? 'Uploading…' : 'Upload Curriculum'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* View / detail modal */}
      {viewRow && (
        <div className={css.modalOverlay} onClick={() => setViewRow(null)}>
          <div className={`${css.modalMedium} BubblePopIn`} onClick={e => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h2 className={css.modalTitle}>{viewRow.subjectName} — {viewRow.className} Curriculum</h2>
              <button className={css.closeBtn} onClick={() => setViewRow(null)}>✕</button>
            </div>
            <div className={css.modalBody}>
              <p style={{ fontSize: '0.82rem', color: '#546e7a', marginBottom: 8 }}>
                <strong>File:</strong> {viewRow.curriculum?.originalName} &nbsp;·&nbsp;
                Uploaded {viewRow.curriculum?.uploadedAt ? new Date(viewRow.curriculum.uploadedAt).toLocaleDateString() : '—'}
              </p>
              {viewRow.curriculum?.summary && (
                <>
                  <h4 style={{ marginBottom: 6, color: '#263238', fontSize: '0.85rem' }}>Summary</h4>
                  <p style={{ fontSize: '0.83rem', color: '#37474f', lineHeight: 1.6, marginBottom: 16 }}>{viewRow.curriculum.summary}</p>
                </>
              )}
              {viewRow.curriculum?.topics?.length > 0 && (
                <>
                  <h4 style={{ marginBottom: 8, color: '#263238', fontSize: '0.85rem' }}>Topics ({viewRow.curriculum.topics.length})</h4>
                  <div className={css.curriculumTopics} style={{ flexWrap: 'wrap' }}>
                    {viewRow.curriculum.topics.map((t, i) => (
                      <span key={i} className={css.curriculumTopicChip}>{t}</span>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className={css.modalFooter}>
              <a className={css.primaryBtn} href={viewRow.curriculum?.url} download={viewRow.curriculum?.originalName} style={{ textDecoration: 'none' }}>
                <DownloadIcon fontSize="small" /> Download File
              </a>
              <button className={css.cancelBtn} onClick={() => setViewRow(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Removes ratings invalidated by later admin setting changes.
function filterValidSkillRatings(ratingsMap, settings) {
  const validKeys = new Set();
  const validValues = new Set((settings?.scale || []).map(entry => entry.value));
  (settings?.groups || []).forEach(group => {
    group.items.forEach(item => validKeys.add(`${group.key}:${item.key}`));
  });
  return Object.fromEntries(
    Object.entries(ratingsMap).filter(([key, value]) => validKeys.has(key) && validValues.has(Number(value)))
  );
}

function TeacherSkillRatingTables({ settings, ratings, onChange, disabled }) {
  if (!settings?.groups?.length) {
    return <p style={{ color: '#90a4ae', fontSize: '0.82rem', marginBottom: 10 }}>Your school admin has not configured any skill or behavioural categories yet.</p>;
  }
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {settings.groups.map(group => (
        <div className={css.tableWrap} key={group.key} style={{ marginBottom: 0 }}>
          <table className={css.table}>
            <thead><tr><th>{group.title}</th><th style={{ width: 170 }}>Rating</th></tr></thead>
            <tbody>{group.items.map(item => {
              const key = `${group.key}:${item.key}`;
              return <tr key={key}><td>{item.label}</td><td><select value={ratings[key] ?? ''} disabled={disabled} onChange={event => onChange(key, event.target.value === '' ? null : Number(event.target.value))} style={{ width: '100%', border: '1px solid #cfd8dc', borderRadius: 7, padding: '6px 8px', fontSize: '0.8rem' }}><option value="">Not rated</option>{settings.scale.map(entry => <option key={entry.value} value={entry.value}>{entry.value} - {entry.label}</option>)}</select></td></tr>;
            })}</tbody>
          </table>
        </div>
      ))}
      <p style={{ color: '#90a4ae', fontSize: '0.76rem', margin: 0 }}>Rating scale: {settings.scale.map(entry => `${entry.value} = ${entry.label}`).join(' | ')}</p>
    </div>
  );
}

// ─── Student Profile Drawer ───────────────────────────────────────────────────
function StudentProfileDrawer({ membershipId, school, isFormTeacher, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [noteError, setNoteError] = useState('');

  // ── AI Career Path state ─────────────────────────────────────────────────
  const [careerPath, setCareerPath] = useState(null);
  const [careerPathLoading, setCareerPathLoading] = useState(true);

  const [skillsTerm, setSkillsTerm] = useState('first');
  const [skillsSession, setSkillsSession] = useState('');
  const [skillsSettings, setSkillsSettings] = useState(null);
  const [skillRatings, setSkillRatings] = useState({});
  const [formTeacherComment, setFormTeacherComment] = useState('');
  const [headTeacherComment, setHeadTeacherComment] = useState('');
  const [skillsCarriedOver, setSkillsCarriedOver] = useState(false);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillsSaving, setSkillsSaving] = useState(false);
  const [skillsMsg, setSkillsMsg] = useState('');

  // ── Exam filter state (term/session + subject) — affects cards, chart & table ──
  const [filterTermSession, setFilterTermSession] = useState('');
  const [filterSubject, setFilterSubject]         = useState('');
  const chartRef = useRef(null);

  useEffect(() => {
    if (!membershipId) return;
    setLoading(true);
    teacherFetch(`/teacher/analytics/students/${membershipId}`).then(({ ok, data }) => {
      if (ok) setProfile(data);
      setLoading(false);
    });
  }, [membershipId]);

  useEffect(() => {
    if (!membershipId) return;
    setCareerPathLoading(true);
    teacherFetch(`/teacher/analytics/students/${membershipId}/career-path`).then(({ ok, data }) => {
      if (ok) setCareerPath(data);
      setCareerPathLoading(false);
    });
  }, [membershipId]);

  const loadReportCardExtras = async () => {
    if (!skillsTerm || !skillsSession || !profile) return;
    const studentId = profile.student?.studentId;
    if (!studentId) return;
    setSkillsLoading(true);
    setSkillsMsg('');
    const params = new URLSearchParams({ term: skillsTerm, session: skillsSession });
    const { ok, data } = await teacherFetch(`/teacher/report-card-extras/${studentId}?${params}`);
    if (ok) {
      setSkillsSettings(data.settings);
      const loadedRatings = (data.ratings || []).reduce((map, rating) => ({ ...map, [`${rating.groupKey}:${rating.itemKey}`]: rating.value }), {});
      setSkillRatings(filterValidSkillRatings(loadedRatings, data.settings));
      setFormTeacherComment(data.formTeacherComment || '');
      setHeadTeacherComment(data.headTeacherComment || '');
      setSkillsCarriedOver(!!data.carriedOver);
    } else setSkillsMsg(data.error || 'Could not load skill ratings.');
    setSkillsLoading(false);
  };

  const saveReportCardExtras = async () => {
    if (!skillsSettings || !isFormTeacher || !skillsTerm || !skillsSession || !profile) return;
    const studentId = profile.student?.studentId;
    if (!studentId) return;
    setSkillsSaving(true); setSkillsMsg('');
    const ratingsPayload = Object.entries(skillRatings).filter(([, value]) => value !== null && value !== '').map(([key, value]) => {
      const [groupKey, itemKey] = key.split(':');
      return { groupKey, itemKey, value: Number(value) };
    });
    const { ok, data } = await teacherFetch('/teacher/report-card-extras', {
      method: 'PATCH',
      body: JSON.stringify({ studentId, term: skillsTerm, session: skillsSession, skillRatings: ratingsPayload, formTeacherComment }),
    });
    if (ok) {
      setSkillsCarriedOver(false);
      setSkillsMsg('Skill ratings saved');
    } else setSkillsMsg(data.error || 'Could not save skill ratings.');
    setSkillsSaving(false);
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true); setNoteError('');
    const { ok, data } = await teacherFetch(`/teacher/analytics/intervention/${membershipId}`, {
      method: 'POST',
      body: JSON.stringify({ note: noteText }),
    });
    if (ok) {
      setNoteText('');
      // Refresh profile to show new note
      const res = await teacherFetch(`/teacher/analytics/students/${membershipId}`);
      if (res.ok) setProfile(res.data);
    } else {
      setNoteError(data?.error || 'Failed to save note');
    }
    setAddingNote(false);
  };

  const s = profile?.student;

  const fmtSecs = (secs) => {
    if (!secs) return '—';
    const m = Math.floor(secs / 60), sec = secs % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  const termLabel = (t) => t === 'first' ? 'First' : t === 'second' ? 'Second' : t === 'third' ? 'Third' : (t || '—');

  // Term/session options derived from the student's real exam data
  const termOptions = React.useMemo(() => {
    const map = new Map();
    (profile?.examHistory || []).forEach(e => {
      if (e.term) map.set(`${e.term}||${e.session}`, { term: e.term, session: e.session });
    });
    return Array.from(map.values()).sort((a, b) => {
      const order = { first: 0, second: 1, third: 2 };
      return (order[a.term] ?? 9) - (order[b.term] ?? 9);
    });
  }, [profile]);

  // Subject options derived from the student's real exam data
  const subjectOptions = React.useMemo(() => (
    Array.from(new Set((profile?.examHistory || []).map(e => e.subjectName).filter(Boolean))).sort()
  ), [profile]);

  // Apply term/session + subject filters — drives cards, chart, and table below
  const filteredExamHistory = React.useMemo(() => (
    (profile?.examHistory || []).filter(e => {
      if (filterTermSession) {
        const combined = `${e.term}||${e.session}`;
        if (combined !== filterTermSession) return false;
      }
      if (filterSubject && e.subjectName !== filterSubject) return false;
      return true;
    })
  ), [profile, filterTermSession, filterSubject]);

  const filteredSummary = React.useMemo(() => {
    const submitted = filteredExamHistory.filter(e => e.attemptStatus === 'submitted');
    const avgPercentage = submitted.length > 0
      ? Math.round(submitted.reduce((sum, e) => sum + (e.percentage || 0), 0) / submitted.length)
      : null;
    return {
      totalExams: filteredExamHistory.length,
      submitted: submitted.length,
      missed: filteredExamHistory.filter(e => e.attemptStatus === 'missed').length,
      avgPercentage,
    };
  }, [filteredExamHistory]);

  const trend = React.useMemo(() => (
    filteredExamHistory
      .filter(e => e.attemptStatus === 'submitted' && e.percentage != null)
      .map(e => ({ date: e.submittedAt, percentage: e.percentage, title: e.title }))
      .sort((a, b) => new Date(a.date) - new Date(b.date))
  ), [filteredExamHistory]);

  const hasActiveFilters = !!(filterTermSession || filterSubject);

  // School is Premium-tier → exports carry the school logo + required contact details
  const isPremiumSchool = school?.subscription?.plan === 'premium';

  // ── Export handlers (cards + score trend chart + exam history table) ──────
  const handleProfileExcelExport = () => {
    if (!profile) return;
    const sName = `${s?.firstName || ''} ${s?.lastName || ''}`.trim() || 'student';
    const filterDesc = [
      filterTermSession ? `${termLabel(filterTermSession.split('||')[0])} Term — ${filterTermSession.split('||')[1] || '—'}` : 'All Terms',
      filterSubject || 'All Subjects',
    ].join(' · ');

    const wb = XLSX.utils.book_new();

    // ── School branding rows (Premium schools only get the extra contact details) ──
    const schoolRows = [];
    if (school?.name) schoolRows.push(['School', school.name]);
    if (isPremiumSchool) {
      if (school?.motto) schoolRows.push(['Motto', school.motto]);
      if (school?.address) schoolRows.push(['Address', school.address]);
      if (school?.phone) schoolRows.push(['Phone', school.phone]);
      if (school?.contactEmail) schoolRows.push(['Email', school.contactEmail]);
    }
    if (schoolRows.length) schoolRows.push([]);

    // ── Summary sheet ──
    const summaryRows = [
      ...schoolRows,
      ['Student', sName],
      ['Class', `${s?.className || ''} ${s?.classArm || ''}`.trim()],
      ['Email', s?.email || ''],
      ['Filters', filterDesc],
      ['Generated', new Date().toLocaleString()],
      [],
      ['Metric', 'Value'],
      ['Exams', filteredSummary.totalExams],
      ['Submitted', filteredSummary.submitted],
      ['Avg Score', filteredSummary.avgPercentage != null ? `${filteredSummary.avgPercentage}%` : '—'],
      ['Missed', filteredSummary.missed],
    ];
    const summaryWs = XLSX.utils.aoa_to_sheet(summaryRows);
    summaryWs['!cols'] = [{ wch: 16 }, { wch: 32 }];
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

    // ── Score Trend sheet ──
    const trendRows = [['Date', 'Exam', 'Percentage']];
    trend.forEach(t => trendRows.push([
      t.date ? new Date(t.date).toLocaleDateString('en-GB') : '',
      t.title || '',
      t.percentage != null ? `${t.percentage}%` : '',
    ]));
    const trendWs = XLSX.utils.aoa_to_sheet(trendRows);
    trendWs['!cols'] = [{ wch: 14 }, { wch: 32 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, trendWs, 'Score Trend');

    // ── Exam History sheet ──
    const histRows = [['Exam', 'Subject', 'Term', 'Session', 'Score', 'Out Of', 'Percentage', 'Status', 'Time Taken', 'Date']];
    filteredExamHistory.forEach(e => histRows.push([
      e.title || '',
      e.subjectName || '',
      termLabel(e.term),
      e.session || '',
      e.score ?? '',
      e.totalScore ?? '',
      e.percentage != null ? `${e.percentage}%` : '',
      e.attemptStatus === 'submitted' ? (e.passed ? 'Pass' : 'Fail') : e.attemptStatus,
      fmtSecs(e.timeTaken),
      (e.submittedAt || e.startedAt) ? new Date(e.submittedAt || e.startedAt).toLocaleDateString('en-GB') : '',
    ]));
    const histWs = XLSX.utils.aoa_to_sheet(histRows);
    histWs['!cols'] = [{ wch: 28 }, { wch: 16 }, { wch: 10 }, { wch: 14 }, { wch: 8 }, { wch: 8 }, { wch: 11 }, { wch: 10 }, { wch: 12 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, histWs, 'Exam History');

    XLSX.writeFile(wb, `${sName.replace(/\s+/g, '_')}_profile_report.xlsx`);
  };

  const handleProfilePDFExport = async () => {
    if (!profile) return;
    const sName = `${s?.firstName || ''} ${s?.lastName || ''}`.trim() || 'Student';
    const filterDesc = [
      filterTermSession ? `${termLabel(filterTermSession.split('||')[0])} Term — ${filterTermSession.split('||')[1] || '—'}` : 'All Terms',
      filterSubject || 'All Subjects',
    ].join(' · ');

    let chartImg = '';
    if (chartRef.current && trend.length >= 2) {
      try {
        const html2canvas = (await import('html2canvas')).default;
        const canvas = await html2canvas(chartRef.current, { backgroundColor: '#ffffff', scale: 2 });
        chartImg = canvas.toDataURL('image/png');
      } catch (err) {
        chartImg = '';
      }
    }

    const cardsHtml = [
      { label: 'Exams', value: filteredSummary.totalExams },
      { label: 'Submitted', value: filteredSummary.submitted },
      { label: 'Avg Score', value: filteredSummary.avgPercentage != null ? `${filteredSummary.avgPercentage}%` : '—' },
      { label: 'Missed', value: filteredSummary.missed },
    ].map(c => `<div class="card"><div class="cardLabel">${c.label}</div><div class="cardValue">${c.value}</div></div>`).join('');

    // ── School branding header — Premium schools show their own logo + required
    // contact details; non-Premium schools show the Bubble Ai logo instead ──
    const toAbsoluteUrl = (url) => {
      if (!url) return '';
      if (/^(https?:)?\/\//i.test(url)) return url;
      return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
    };
    const schoolContactParts = [school?.phone, school?.contactEmail, school?.address].filter(Boolean);
    const schoolHeaderHtml = isPremiumSchool
      ? `<div class="schoolHeader">
          ${school?.logo ? `<img src="${toAbsoluteUrl(school.logo)}" class="schoolLogo" alt="logo" />` : ''}
          <div>
            <div class="schoolName">${school?.name || 'School Report'}</div>
            ${school?.motto ? `<div class="schoolMotto">${school.motto}</div>` : ''}
            ${schoolContactParts.length ? `<div class="schoolContact">${schoolContactParts.join(' · ')}</div>` : ''}
          </div>
        </div>`
      : `<div class="schoolHeader schoolHeaderBasic">
          <img src="${toAbsoluteUrl('/favicon.ico')}" class="schoolLogo" alt="Bubble Ai logo" />
          <div class="schoolName">Bubble Ai Schools</div>
        </div>`;
    const footerHtml = `<div class="footer">${isPremiumSchool ? new Date().toLocaleDateString('en-GB') : `Generated by Bubble Ai Schools · ${new Date().toLocaleDateString('en-GB')}`}</div>`;

    const historyRows = filteredExamHistory.map(e => {
      const result = e.attemptStatus === 'submitted' ? (e.passed ? 'Pass' : 'Fail') : e.attemptStatus;
      const resultColor = e.attemptStatus === 'missed' ? '#c62828' : (e.passed ? '#2e7d32' : '#607d8b');
      const dateVal = e.submittedAt || e.startedAt;
      return `<tr>
        <td>${e.title || ''}</td>
        <td>${e.subjectName || ''}</td>
        <td>${termLabel(e.term)}${e.session ? ` — ${e.session}` : ''}</td>
        <td>${e.score ?? '—'}${e.totalScore ? ` / ${e.totalScore}` : ''}</td>
        <td>${e.percentage != null ? `${e.percentage}%` : '—'}</td>
        <td style="color:${resultColor};font-weight:600">${result}</td>
        <td>${dateVal ? new Date(dateVal).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${sName} – Student Profile</title>
<style>
  body{font-family:sans-serif;font-size:13px;color:#1a2a36;padding:24px}
  h1{font-size:19px;margin:0 0 2px}
  h2{font-size:14px;color:#1a6e73;margin:22px 0 10px;border-bottom:1px solid #e0f4f5;padding-bottom:4px}
  .subtitle{margin:0 0 4px;color:#607d8b}
  .filters{margin:0 0 14px;color:#78909c;font-size:12px;font-style:italic}
  .cards{display:flex;gap:12px;flex-wrap:wrap}
  .card{flex:1 1 120px;background:#f7fbfc;border-radius:10px;padding:12px 14px;text-align:center;border:1px solid #e0f4f5}
  .cardLabel{font-size:11px;color:#90a4ae;margin-bottom:4px}
  .cardValue{font-size:18px;font-weight:700;color:#1a6e73}
  table{width:100%;border-collapse:collapse;margin-bottom:12px}
  th,td{border:1px solid #e0e0e0;padding:6px 8px;text-align:left;font-size:12px}
  th{background:#e0f4f5;font-weight:600;color:#1a6e73}
  img.chart{max-width:100%;border:1px solid #e0e0e0;border-radius:8px;padding:8px;background:#fff}
  .schoolHeader{display:flex;align-items:center;gap:14px;background:#0C1E28;padding:14px 18px;border-radius:10px;margin-bottom:18px}
  .schoolHeaderBasic{background:#37474f}
  .schoolLogo{width:48px;height:48px;object-fit:contain;border-radius:8px;background:#fff;padding:4px;flex-shrink:0}
  .schoolName{font-size:16px;font-weight:700;color:#5fbec5}
  .schoolMotto{font-size:11px;font-style:italic;color:#b0bec5;margin-top:2px}
  .schoolContact{font-size:10.5px;color:#90a4ae;margin-top:2px}
  .footer{margin-top:18px;padding-top:10px;border-top:1px solid #e0e0e0;text-align:center;font-size:10.5px;color:#90a4ae}
  @media print{body{padding:12px}}
</style>
</head><body>
${schoolHeaderHtml}
<h1>${sName}</h1>
<p class="subtitle">${s?.className || ''} ${s?.classArm || ''} · ${s?.email || ''}</p>
<p class="filters">Filters: ${filterDesc} · Generated ${new Date().toLocaleString()}</p>
<h2>Summary</h2>
<div class="cards">${cardsHtml}</div>
${chartImg ? `<h2>Score Trend</h2><img class="chart" src="${chartImg}" />` : ''}
<h2>Exam History</h2>
<table><thead><tr><th>Exam</th><th>Subject</th><th>Term</th><th>Score</th><th>Percentage</th><th>Result</th><th>Date</th></tr></thead><tbody>${historyRows || '<tr><td colspan="7" style="text-align:center;color:#90a4ae">No exam attempts</td></tr>'}</tbody></table>
${footerHtml}
</body></html>`;

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), chartImg ? 500 : 250);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 900, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 'min(640px, 100vw)', background: '#fff', overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)', padding: '28px 24px 40px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
            {loading ? (
              <span className={css.skel} style={{ width: 58, height: 58, borderRadius: '50%', display: 'block' }} />
            ) : (
              <AvatarOrInitials src={s?.profileImg} firstName={s?.firstName} lastName={s?.lastName} size={58} />
            )}
            <div style={{ minWidth: 0 }}>
              {loading ? <span className={css.skel} style={{ height: 18, width: 160, display: 'block' }} /> : (
                <h2 style={{ margin: 0, fontSize: '1.18rem', fontWeight: 700, color: '#1a2a36', overflowWrap: 'anywhere' }}>{s?.firstName} {s?.lastName}</h2>
              )}
              {!loading && s && (
                <div style={{ fontSize: '0.8rem', color: '#78909c', marginTop: 3, overflowWrap: 'anywhere' }}>
                  {s.className}{s.classArm ? ` (${s.classArm})` : ''} · {s.email}
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#90a4ae', flexShrink: 0 }}>
            <CloseIcon />
          </button>
        </div>

        <p style={{ fontSize: '0.8rem', color: '#90a4ae', margin: '0 0 20px', lineHeight: 1.5 }}>
          Full performance profile for this student in your subject(s): predicted career path, score trend,
          exam history, weak topics, intervention notes, and form-teacher skill ratings.
        </p>

        {loading ? (
          <>
            {[140, 90, 110].map((w, i) => <span key={i} className={css.skel} style={{ height: 11, width: `${w}px`, display: 'block', marginBottom: 10 }} />)}
          </>
        ) : !profile ? (
          <p style={{ color: '#e53935' }}>Failed to load profile.</p>
        ) : (
          <>
            {/* Predicted Career Path (AI, cron-generated — read-only) */}
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 10, color: '#455a64' }}>
                Predicted Career Path
              </div>
              {careerPathLoading ? (
                <span className={css.skel} style={{ height: 40, display: 'block', borderRadius: 8 }} />
              ) : (
                <CareerPathSummaryCard
                  enabled={careerPath?.enabled}
                  status={careerPath?.status}
                  eligibility={careerPath?.eligibility}
                  snapshot={careerPath?.snapshot}
                  lastComputedAt={careerPath?.lastComputedAt}
                />
              )}
            </div>

            {/* Exam filters — term/session + subject; affects cards, chart & table below */}
            {(termOptions.length > 0 || subjectOptions.length > 1) && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18 }}>
                <select
                  value={filterTermSession}
                  onChange={e => setFilterTermSession(e.target.value)}
                  style={{ padding: '6px 10px', border: '1px solid #cfd8dc', borderRadius: 8, fontSize: '0.8rem', color: '#455a64' }}
                >
                  <option value="">All Terms</option>
                  {termOptions.map(o => (
                    <option key={`${o.term}||${o.session}`} value={`${o.term}||${o.session}`}>
                      {termLabel(o.term)} Term — {o.session}
                    </option>
                  ))}
                </select>
                {subjectOptions.length > 1 && (
                  <select
                    value={filterSubject}
                    onChange={e => setFilterSubject(e.target.value)}
                    style={{ padding: '6px 10px', border: '1px solid #cfd8dc', borderRadius: 8, fontSize: '0.8rem', color: '#455a64' }}
                  >
                    <option value="">All Subjects</option>
                    {subjectOptions.map(subj => <option key={subj} value={subj}>{subj}</option>)}
                  </select>
                )}
                {hasActiveFilters && (
                  <button
                    onClick={() => { setFilterTermSession(''); setFilterSubject(''); }}
                    style={{ padding: '6px 12px', border: '1px solid #cfd8dc', borderRadius: 8, fontSize: '0.8rem', background: '#fff', cursor: 'pointer', color: '#607d8b' }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}

            {/* Summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
              {[
                { label: 'Exams', value: filteredSummary.totalExams ?? '—' },
                { label: 'Submitted', value: filteredSummary.submitted ?? '—' },
                { label: 'Avg Score', value: filteredSummary.avgPercentage != null ? `${filteredSummary.avgPercentage}%` : '—' },
                { label: 'Missed', value: filteredSummary.missed ?? '—' },
              ].map(c => (
                <div key={c.label} style={{ background: '#f7fbfc', borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.72rem', color: '#90a4ae', marginBottom: 4 }}>{c.label}</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a6e73' }}>{c.value}</div>
                </div>
              ))}
            </div>

            {/* Score trend chart */}
            {trend.length >= 2 && (
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 10, color: '#455a64' }}>Score Trend</div>
                <div ref={chartRef} style={{ background: '#fff' }}>
                  <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={trend} margin={{ top: 4, right: 10, left: -20, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eceff1" />
                      <XAxis dataKey="title" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(0, 10)} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} unit="%" />
                      <Tooltip formatter={(v) => `${v}%`} labelFormatter={l => l} />
                      <Line type="monotone" dataKey="percentage" stroke="#5fbec5" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Exam history */}
            <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 8, color: '#455a64' }}>Exam History</div>
            {filteredExamHistory.length === 0 ? (
              <p style={{ color: '#90a4ae', fontSize: '0.83rem' }}>
                {(profile.examHistory || []).length === 0 ? 'No exam attempts yet.' : 'No exams match the selected filters.'}
              </p>
            ) : (
              <div className={css.tableWrap} style={{ marginBottom: 12 }}>
                <table className={css.table}>
                  <thead>
                    <tr><th>Exam</th><th>Subject</th><th>Score</th><th>Out of</th><th>Status</th><th>Date</th></tr>
                  </thead>
                  <tbody>
                    {filteredExamHistory.map((e, i) => (
                      <tr key={i}>
                        <td className={css.bold}>{e.title}</td>
                        <td>{e.subjectName}</td>
                        <td>{e.percentage != null ? `${e.percentage}%` : '—'}</td>
                        <td style={{ color: '#78909c', fontSize: '0.82rem' }}>{e.totalScore != null ? e.totalScore : '—'}</td>
                        <td><StatusChip status={e.attemptStatus === 'submitted' ? (e.passed ? 'published' : 'closed') : e.attemptStatus} /></td>
                        <td>{e.submittedAt ? new Date(e.submittedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Export buttons — cards + score trend chart + exam history table (respects active filters) */}
            <div className={css.drawerExportRow} style={{ marginBottom: 20 }}>
              <button className={css.drawerExportBtn} onClick={handleProfilePDFExport}>
                🖨 Export PDF
              </button>
              <button className={css.drawerExportBtn} style={{ background: 'linear-gradient(135deg,#1a6e73,#2a9d8f)' }} onClick={handleProfileExcelExport}>
                📊 Export Excel
              </button>
            </div>

            {/* Top weak topics */}
            {(profile.aggregateWeakTopics || []).length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 8, color: '#455a64' }}>Top Weak Topics</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {profile.aggregateWeakTopics.map(t => (
                    <span key={t.topic} style={{ background: '#fff3e0', color: '#e65100', borderRadius: 10, padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600 }}>
                      {t.topic} <span style={{ opacity: 0.7 }}>×{t.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Intervention notes */}
            <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 8, color: '#455a64' }}>Intervention Notes</div>
            {(profile.interventionNotes || []).length === 0 ? (
              <p style={{ color: '#90a4ae', fontSize: '0.83rem', marginBottom: 10 }}>No notes yet.</p>
            ) : (
              <div style={{ marginBottom: 12 }}>
                {profile.interventionNotes.map((n, i) => (
                  <div key={i} style={{ background: '#fff8e1', borderRadius: 8, padding: '8px 12px', marginBottom: 6, fontSize: '0.82rem', border: '1px solid #ffecb3' }}>
                    <div style={{ color: '#7a5b00' }}>{n.note}</div>
                    <div style={{ color: '#b28900', fontSize: '0.72rem', marginTop: 3 }}>{new Date(n.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
              <input
                style={{ flex: '1 1 180px', minWidth: 0, border: '1px solid #cfd8dc', borderRadius: 8, padding: '7px 12px', fontSize: '0.83rem' }}
                placeholder="Add an intervention note…"
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddNote()}
              />
              <button className={css.primaryBtn} onClick={handleAddNote} disabled={addingNote || !noteText.trim()}>
                {addingNote ? '…' : 'Add'}
              </button>
            </div>
            {noteError && <div className={css.inlineError} style={{ marginTop: 6 }}>{noteError}</div>}

            {/* Form-teacher report card skills */}
            <div style={{ marginTop: 22, borderTop: '1px solid #eceff1', paddingTop: 14 }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 8, color: '#455a64' }}>Skill Ratings &amp; Form Teacher&apos;s Comment</div>
              <p style={{ fontSize: '0.77rem', color: '#90a4ae', marginBottom: 10 }}>
                Record the configurable skill ratings and form-teacher comment for this student&apos;s term report.
              </p>

              {/* Term + Session picker */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <select
                  value={skillsTerm}
                  onChange={e => setSkillsTerm(e.target.value)}
                  style={{ flex: '1 1 120px', minWidth: 0, border: '1px solid #cfd8dc', borderRadius: 8, padding: '6px 10px', fontSize: '0.82rem' }}
                >
                  <option value="first">First Term</option>
                  <option value="second">Second Term</option>
                  <option value="third">Third Term</option>
                </select>
                <input
                  placeholder="Session (e.g. 2025/2026)"
                  value={skillsSession}
                  onChange={e => setSkillsSession(e.target.value)}
                  style={{ flex: '1 1 140px', minWidth: 0, border: '1px solid #cfd8dc', borderRadius: 8, padding: '6px 10px', fontSize: '0.82rem' }}
                />
                <button
                  className={css.primaryBtn}
                  onClick={loadReportCardExtras}
                  disabled={skillsLoading || !skillsSession}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {skillsLoading ? '…' : 'Load'}
                </button>
              </div>

              {skillsLoading && <p style={{ color: '#90a4ae', fontSize: '0.82rem' }}>Loading skill report settings...</p>}
              {!skillsLoading && !skillsSettings && <p style={{ color: '#90a4ae', fontSize: '0.82rem' }}>Choose a term and session above, then select Load to view skill ratings.</p>}
              {!skillsLoading && skillsSettings && !isFormTeacher && (
                <div style={{ background: '#f5f7fa', border: '1px solid #e0e6e9', borderRadius: 8, padding: '10px 12px', color: '#607d8b', fontSize: '0.8rem', marginBottom: 10 }}>
                  Only this class&apos;s form teacher can record skill ratings and write the form teacher&apos;s comment.
                </div>
              )}
              {skillsSettings && isFormTeacher && (
                <>
                  {skillsCarriedOver && <div style={{ background: '#eef8f8', border: '1px solid #b9dfe1', borderRadius: 8, padding: '9px 12px', color: '#1a6e73', fontSize: '0.78rem', marginBottom: 10 }}>Showing the latest available ratings as a starting point. Save to record them for this term.</div>}
                  <TeacherSkillRatingTables
                    settings={skillsSettings}
                    ratings={skillRatings}
                    onChange={(key, value) => setSkillRatings(previous => ({ ...previous, [key]: value }))}
                    disabled={skillsSaving}
                  />
                  <div style={{ marginTop: 12 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.8rem', color: '#455a64', marginBottom: 5 }}>Form Teacher&apos;s Comment</label>
                    <textarea rows={4} maxLength={1000} value={formTeacherComment} onChange={e => setFormTeacherComment(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #cfd8dc', borderRadius: 8, padding: '8px 10px', fontSize: '0.82rem', resize: 'vertical' }} />
                  </div>
                  <button className={css.primaryBtn} onClick={saveReportCardExtras} disabled={skillsSaving || !skillsSession}>{skillsSaving ? 'Saving...' : 'Save Skill Report'}</button>
                </>
              )}
              {skillsMsg && (
                <div style={{ fontSize: '0.77rem', color: skillsMsg === 'Skill ratings saved' ? '#2e7d32' : '#c62828', marginTop: 8 }}>{skillsMsg}</div>
              )}
              {skillsSettings && headTeacherComment && (
                <div style={{ marginTop: 12, background: '#f5f7fa', borderRadius: 8, padding: '9px 12px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#455a64', marginBottom: 4 }}>Principal&apos;s Remark</div>
                  <div style={{ color: '#607d8b', fontSize: '0.82rem', whiteSpace: 'pre-wrap' }}>{headTeacherComment}</div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── My Students Tab (enhanced with analytics columns) ────────────────────────
function StudentsTab({ school }) {
  const [students, setStudents]         = useState([]);
  const [atRisk,   setAtRisk]           = useState([]);
  const [loading,  setLoading]          = useState(true);
  const [search,   setSearch]           = useState('');
  const [filterClass, setFilterClass]   = useState('');
  const [filterRisk,  setFilterRisk]    = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    Promise.all([
      teacherFetch('/teacher/students'),
      teacherFetch('/teacher/analytics/students'),
    ]).then(([studRes, riskRes]) => {
      if (studRes.ok)  setStudents(studRes.data.students || []);
      if (riskRes.ok)  setAtRisk(riskRes.data.students || []);
      setLoading(false);
    });
  }, []);

  // Merge analytics data into student list
  const riskMap = {};
  atRisk.forEach(r => { riskMap[String(r.studentId)] = r; });

  const classes = [...new Map(
    students.filter(s => s.className).map(s => [s.className, s.className])
  ).values()];

  const enriched = students.map(s => ({
    ...s,
    ...(riskMap[String(s.userId || s.studentId)] || {}),
  }));

  const filtered = enriched.filter(s => {
    const q = search.toLowerCase();
    const matchSearch = !q || `${s.firstName} ${s.lastName}`.toLowerCase().includes(q);
    const matchClass  = !filterClass || s.className === filterClass;
    const matchRisk   = !filterRisk  || s.risk === filterRisk;
    return matchSearch && matchClass && matchRisk;
  });

  return (
    <div>
      <div className={css.tabBar}>
        <h2 className={css.tabTitle}>My Students</h2>
        <span className={css.hintText} style={{ alignSelf: 'center' }}>{students.length} student{students.length !== 1 ? 's' : ''}</span>
      </div>

      <div className={css.filterRow}>
        <input className={css.filterInput} placeholder="Search by name…" value={search} onChange={e => setSearch(e.target.value)} />
        <select className={css.filterSelect} value={filterClass} onChange={e => setFilterClass(e.target.value)}>
          <option value="">All Classes</option>
          {classes.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className={css.filterSelect} value={filterRisk} onChange={e => setFilterRisk(e.target.value)}>
          <option value="">All Risk Levels</option>
          <option value="high">High Risk</option>
          <option value="medium">Medium Risk</option>
          <option value="low">Low Risk</option>
        </select>
      </div>

      {loading ? (
        <SkeletonTableRows rows={6} cols={7} />
      ) : filtered.length === 0 ? (
        <EmptyState message={students.length === 0 ? 'No students in your assigned classes yet.' : 'No students match your filters.'} />
      ) : (
        <div className={css.tableWrap}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Class</th>
                <th>Arm</th>
                <th>Avg Score</th>
                <th>Submitted</th>
                <th>Trend</th>
                <th>Risk</th>
                <th aria-label="View profile" />
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr
                  key={s.membershipId}
                  className={css.clickableRow}
                  tabIndex={0}
                  role="button"
                  title="View student profile"
                  onClick={() => setSelectedStudent(s)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedStudent(s); } }}
                >
                  <td className={css.studentName}>
                    <AvatarOrInitials src={s.profileImg || s.avatar} firstName={s.firstName} lastName={s.lastName} size={28} className={css.studentAvatar} />
                    <span style={{ fontWeight: 600 }}>{s.firstName} {s.lastName}</span>
                  </td>
                  <td>{s.className || '—'}</td>
                  <td>{s.classArm || '—'}</td>
                  <td>{s.avgPercentage != null ? `${s.avgPercentage}%` : '—'}</td>
                  <td>{s.submittedCount ?? '—'}</td>
                  <td><TrendIcon declining={s.declining} scores={s.recentPercentages} /></td>
                  <td>{s.risk ? <RiskBadge risk={s.risk} /> : '—'}</td>
                  <td className={css.rowChevron}><ChevronRightIcon fontSize="small" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedStudent && (
        <StudentProfileDrawer
          membershipId={selectedStudent.membershipId}
          school={school}
          isFormTeacher={!!selectedStudent.isFormTeacher}
          onClose={() => setSelectedStudent(null)}
        />
      )}
    </div>
  );
}

function TeacherProfileTab({ teacher, school, onTeacherUpdated }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [profileForm, setProfileForm] = useState({
    firstName: teacher?.firstName || '',
    lastName: teacher?.lastName || '',
    phone: teacher?.phone || '',
    dob: teacher?.dob || '',
    address: teacher?.address || '',
    city: teacher?.city || '',
    stateRegion: teacher?.stateRegion || '',
    postalCode: teacher?.postalCode || '',
    country: teacher?.country || '',
    countryOfOrigin: teacher?.countryOfOrigin || '',
    occupation: teacher?.occupation || '',
    emergencyContactName: teacher?.emergencyContactName || '',
    emergencyContactRelationship: teacher?.emergencyContactRelationship || '',
    emergencyContactPhone: teacher?.emergencyContactPhone || '',
    emergencyContactEmail: teacher?.emergencyContactEmail || '',
    emergencyContactAddress: teacher?.emergencyContactAddress || '',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  const fileInputRef = useRef(null);
  const [pwd, setPwd] = useState({ currentPassword: '', newPassword: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdMessage, setPwdMessage] = useState('');

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const previewSrc = previewUrl || teacher?.profileImg || '';

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    setError('');
    setMessage('');

    const validation = validateImageFile(file, 2);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    let finalFile = file;
    if (file.size > 2 * 1024 * 1024) {
      try {
        const result = await compressImageIfNeeded(file, 2);
        finalFile = result.file;
      } catch {
        setError('Image compression failed. Try a smaller image.');
        return;
      }
    }

    if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setSelectedFile(finalFile);
    setPreviewUrl(URL.createObjectURL(finalFile));
  };

  const uploadPassport = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setError('');
    setMessage('');
    try {
      const token = localStorage.getItem('school-token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await fetch('/teacher/profile-image', {
        method: 'POST',
        headers: { 'x-access-token': token || '' },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.profileImg) {
        setError(data.error || 'Upload failed. Please try again.');
        return;
      }
      onTeacherUpdated(prev => prev ? { ...prev, profileImg: data.profileImg } : prev);
      setSelectedFile(null);
      if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
      setMessage('Passport image updated.');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const setProfileField = key => event => setProfileForm(form => ({ ...form, [key]: event.target.value }));

  const handleProfileSave = async event => {
    event.preventDefault();
    setProfileSaving(true);
    setProfileError('');
    setProfileMessage('');
    try {
      const { ok, data } = await teacherFetch('/teacher/profile', {
        method: 'PATCH',
        body: JSON.stringify(profileForm),
      });
      if (!ok) {
        setProfileError(data.error || 'Profile update failed.');
        return;
      }
      onTeacherUpdated(data.teacher);
      setProfileMessage('Profile details updated successfully.');
    } catch {
      setProfileError('Connection error. Please try again.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async event => {
    event.preventDefault();
    setPwdSaving(true);
    setPwdError('');
    setPwdMessage('');
    try {
      const { ok, data } = await teacherFetch('/teacher/change-password', {
        method: 'POST',
        body: JSON.stringify(pwd),
      });
      if (!ok) {
        setPwdError(data.error || 'Password change failed.');
        return;
      }
      setPwd({ currentPassword: '', newPassword: '' });
      setPwdMessage(data.message || 'Password changed successfully.');
    } catch {
      setPwdError('Connection error. Please try again.');
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <div>
      <div className={css.tabBar}>
        <div>
          <h2 className={css.tabTitle}>Settings</h2>
          <p className={css.tabSub}>Update your passport image for teacher-facing views.</p>
        </div>
      </div>

      <div className={css.profileSection}>
        <div className={css.profileLabel}>Passport Photo</div>
        <div className={css.teacherPassportBox}>
          <div className={css.teacherPassportPreview}>
            <AvatarOrInitials src={previewSrc} firstName={teacher?.firstName} lastName={teacher?.lastName} size={112} />
          </div>
          <div className={css.teacherPassportMeta}>
            <div className={css.teacherPassportName}>{teacher?.firstName} {teacher?.lastName}</div>
            <div className={css.teacherPassportHint}>Choose a clear passport-style photo. It will appear in the students drawer and your teacher profile.</div>
            <input ref={fileInputRef} type="file" accept="image/*" className={css.teacherPassportInput} onChange={handleFileChange} />
            <div className={css.teacherPassportActions}>
              <button type="button" className={css.cancelBtn} onClick={() => fileInputRef.current?.click()}>
                Choose Image
              </button>
              <button type="button" className={css.primaryBtn} onClick={uploadPassport} disabled={uploading || !selectedFile}>
                {uploading ? 'Uploading…' : 'Upload Passport'}
              </button>
            </div>
            {message && <div className={css.inlineSuccess} style={{ marginTop: 10 }}>{message}</div>}
            {error && <div className={css.inlineError} style={{ marginTop: 10 }}>{error}</div>}
          </div>
        </div>
      </div>

      <div className={css.profileSection}>
        <div className={css.profileLabel}>Account</div>
        <div className={css.profileFieldGrid}>
          <div className={css.profileField}>
            <div className={css.profileFieldLabel}>Email</div>
            <div className={css.profileFieldValue}>{teacher?.email || '—'}</div>
          </div>
          <div className={css.profileField}>
            <div className={css.profileFieldLabel}>School</div>
            <div className={css.profileFieldValue}>{school?.name || '—'}</div>
          </div>
          <div className={css.profileField}>
            <div className={css.profileFieldLabel}>Phone</div>
            <div className={css.profileFieldValue}>{teacher?.phone || '—'}</div>
          </div>
          <div className={css.profileField}>
            <div className={css.profileFieldLabel}>Staff ID</div>
            <div className={css.profileFieldValue}>{teacher?.staffId || '—'}</div>
          </div>
        </div>
      </div>

      <div className={css.profileSection}>
        <div className={css.profileLabel}>Personal and contact details</div>
        {profileError && <div className={css.inlineError}>{profileError}</div>}
        {profileMessage && <div className={css.inlineSuccess}>{profileMessage}</div>}
        <form onSubmit={handleProfileSave}>
          <div className={css.formGrid2}>
            <div className={css.formRow}>
              <label>First Name</label>
              <input className={css.formInput} value={profileForm.firstName} onChange={setProfileField('firstName')} required />
            </div>
            <div className={css.formRow}>
              <label>Last Name</label>
              <input className={css.formInput} value={profileForm.lastName} onChange={setProfileField('lastName')} required />
            </div>
          </div>
          <div className={css.formGrid3}>
            <div className={css.formRow}>
              <label>Phone</label>
              <input className={css.formInput} value={profileForm.phone} onChange={setProfileField('phone')} />
            </div>
            <div className={css.formRow}>
              <label>Date of Birth</label>
              <input className={css.formInput} type="date" value={profileForm.dob} onChange={setProfileField('dob')} />
            </div>
            <div className={css.formRow}>
              <label>Occupation</label>
              <input className={css.formInput} value={profileForm.occupation} onChange={setProfileField('occupation')} />
            </div>
          </div>
          <div className={css.formRow}>
            <label>Full Address</label>
            <textarea className={css.formTextarea} rows={2} value={profileForm.address} onChange={setProfileField('address')} />
          </div>
          <div className={css.formGrid3}>
            <div className={css.formRow}>
              <label>City</label>
              <input className={css.formInput} value={profileForm.city} onChange={setProfileField('city')} />
            </div>
            <div className={css.formRow}>
              <label>State / Region</label>
              <input className={css.formInput} value={profileForm.stateRegion} onChange={setProfileField('stateRegion')} />
            </div>
            <div className={css.formRow}>
              <label>Postal Code</label>
              <input className={css.formInput} value={profileForm.postalCode} onChange={setProfileField('postalCode')} />
            </div>
          </div>
          <div className={css.formGrid2}>
            <div className={css.formRow}>
              <label>Country</label>
              <input className={css.formInput} value={profileForm.country} onChange={setProfileField('country')} />
            </div>
            <div className={css.formRow}>
              <label>Country of Origin</label>
              <input className={css.formInput} value={profileForm.countryOfOrigin} onChange={setProfileField('countryOfOrigin')} />
            </div>
          </div>

          <div className={css.profileLabel}>Emergency contact</div>
          <div className={css.formGrid2}>
            <div className={css.formRow}>
              <label>Name</label>
              <input className={css.formInput} value={profileForm.emergencyContactName} onChange={setProfileField('emergencyContactName')} />
            </div>
            <div className={css.formRow}>
              <label>Relationship</label>
              <input className={css.formInput} value={profileForm.emergencyContactRelationship} onChange={setProfileField('emergencyContactRelationship')} />
            </div>
          </div>
          <div className={css.formGrid2}>
            <div className={css.formRow}>
              <label>Phone</label>
              <input className={css.formInput} value={profileForm.emergencyContactPhone} onChange={setProfileField('emergencyContactPhone')} />
            </div>
            <div className={css.formRow}>
              <label>Email</label>
              <input className={css.formInput} type="email" value={profileForm.emergencyContactEmail} onChange={setProfileField('emergencyContactEmail')} />
            </div>
          </div>
          <div className={css.formRow}>
            <label>Address</label>
            <textarea className={css.formTextarea} rows={2} value={profileForm.emergencyContactAddress} onChange={setProfileField('emergencyContactAddress')} />
          </div>
          <button type="submit" className={css.primaryBtn} disabled={profileSaving}>
            {profileSaving ? <ThreeCircles height="18" width="18" color="#fff" visible /> : 'Save Profile Details'}
          </button>
        </form>
      </div>

      <div className={css.profileSection}>
        <div className={css.profileLabel}>Change password</div>
        {pwdError && <div className={css.inlineError}>{pwdError}</div>}
        {pwdMessage && <div className={css.inlineSuccess}>{pwdMessage}</div>}
        <form onSubmit={handleChangePassword}>
          <div className={css.formGrid2}>
            <div className={css.formRow}>
              <label>Current Password</label>
              <SchoolPasswordInput
                className={css.formInput}
                value={pwd.currentPassword}
                onChange={event => setPwd(current => ({ ...current, currentPassword: event.target.value }))}
                required
              />
            </div>
            <div className={css.formRow}>
              <label>New Password</label>
              <SchoolPasswordInput
                className={css.formInput}
                value={pwd.newPassword}
                onChange={event => setPwd(current => ({ ...current, newPassword: event.target.value }))}
                minLength={8}
                required
              />
            </div>
          </div>
          <button type="submit" className={css.primaryBtn} disabled={pwdSaving}>
            {pwdSaving ? <ThreeCircles height="18" width="18" color="#fff" visible /> : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Class Summary Tab ────────────────────────────────────────────────────────
function ClassSummaryTab() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    teacherFetch('/teacher/analytics/class-summary').then(({ ok, data }) => {
      if (ok) setClasses(data.classes || []);
      setLoading(false);
    });
  }, []);

  return (
    <div>
      <h2 className={css.tabTitle}>Class Analytics</h2>
      <p className={css.tabSub}>Performance summary for each of your assigned classes.</p>
      {loading ? (
        <SkeletonTableRows rows={4} cols={6} />
      ) : classes.length === 0 ? (
        <EmptyState message="No class data yet. Create and publish exams to see class analytics." />
      ) : (
        <div className={css.tableWrap}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Class</th>
                <th>Students</th>
                <th>Exams</th>
                <th>Avg Score</th>
                <th>Pass Rate</th>
                <th>At-Risk</th>
                <th>Top Weak Topic</th>
              </tr>
            </thead>
            <tbody>
              {classes.map(c => (
                <tr key={String(c.classId)}>
                  <td className={css.bold}>{c.className}</td>
                  <td>{c.studentCount}</td>
                  <td>{c.examCount}</td>
                  <td>
                    {c.avgPercentage != null ? (
                      <span style={{ color: c.avgPercentage < 50 ? '#c62828' : '#2e7d32', fontWeight: 700 }}>
                        {c.avgPercentage}%
                      </span>
                    ) : '—'}
                  </td>
                  <td>{c.passRate != null ? `${c.passRate}%` : '—'}</td>
                  <td>
                    {c.atRiskCount > 0 ? (
                      <span style={{ color: '#e53935', fontWeight: 600 }}>{c.atRiskCount}</span>
                    ) : '0'}
                  </td>
                  <td>{c.topWeakTopic || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Topic Analytics Tab ──────────────────────────────────────────────────────
function TopicAnalyticsTab({ subjects, classes }) {
  const [topics, setTopics]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [classId, setClassId]     = useState('');
  const [subjectId, setSubjectId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (classId)   params.set('classId',   classId);
    if (subjectId) params.set('subjectId', subjectId);
    const { ok, data } = await teacherFetch(`/teacher/analytics/topics?${params}`);
    if (ok) setTopics(data.topics || []);
    setLoading(false);
  }, [classId, subjectId]);

  useEffect(() => { load(); }, [load]);

  const maxPct = topics.length > 0 ? Math.max(...topics.map(t => t.percentage)) : 100;

  function topicColor(pct) {
    if (pct < 40) return { bg: '#fce4ec', text: '#c62828' };
    if (pct < 65) return { bg: '#fff3e0', text: '#e65100' };
    return { bg: '#e8f5e9', text: '#2e7d32' };
  }

  return (
    <div>
      <h2 className={css.tabTitle}>Topic Analytics</h2>
      <p className={css.tabSub}>See which topics students struggle with most across all your exams.</p>

      <div className={css.filterRow}>
        <select className={css.filterSelect} value={classId} onChange={e => setClassId(e.target.value)}>
          <option value="">All Classes</option>
          {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
        <select className={css.filterSelect} value={subjectId} onChange={e => setSubjectId(e.target.value)}>
          <option value="">All Subjects</option>
          {subjects.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
      </div>

      {loading ? (
        <SkeletonTableRows rows={6} cols={5} />
      ) : topics.length === 0 ? (
        <EmptyState message="No topic data yet. Students need to complete graded exams first." />
      ) : (
        <div className={css.tableWrap}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Topic</th>
                <th>Correct Rate</th>
                <th>Progress</th>
                <th>Students Affected</th>
                <th>Flag</th>
              </tr>
            </thead>
            <tbody>
              {topics.map(t => {
                const { bg, text } = topicColor(t.percentage);
                const isGap = t.studentsAffected > 0 && (t.percentage < 50);
                return (
                  <tr key={t.topic}>
                    <td className={css.bold}>{t.topic}</td>
                    <td>
                      <span style={{ background: bg, color: text, borderRadius: 10, padding: '2px 9px', fontWeight: 700, fontSize: '0.82rem' }}>
                        {t.percentage}%
                      </span>
                    </td>
                    <td style={{ minWidth: 120 }}>
                      <div style={{ background: '#eceff1', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                        <div style={{ width: `${(t.percentage / Math.max(maxPct, 1)) * 100}%`, height: '100%', background: text, borderRadius: 4, transition: 'width 0.4s' }} />
                      </div>
                    </td>
                    <td>{t.studentsAffected}</td>
                    <td>
                      {isGap && (
                        <span style={{ background: '#fce4ec', color: '#c62828', borderRadius: 8, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, width: 'fit-content' }}>
                          <WarningAmberIcon style={{ fontSize: 13 }} /> Teaching Gap
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Teaching Insights Tab ────────────────────────────────────────────────────
function InsightsTab({ subjects }) {
  const [insights, setInsights]   = useState(null);
  const [loading, setLoading]     = useState(false);
  const [subjectId, setSubjectId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (subjectId) params.set('subjectId', subjectId);
    const { ok, data } = await teacherFetch(`/teacher/analytics/insights?${params}`);
    if (ok) setInsights(data);
    setLoading(false);
  }, [subjectId]);

  useEffect(() => { load(); }, [load]);

  const d = insights;

  return (
    <div>
      <h2 className={css.tabTitle}>Teaching Insights</h2>
      <p className={css.tabSub}>Data-driven signals to help you identify where to focus your teaching efforts.</p>

      <div className={css.filterRow} style={{ marginBottom: 20 }}>
        <select className={css.filterSelect} value={subjectId} onChange={e => setSubjectId(e.target.value)}>
          <option value="">All Subjects</option>
          {subjects.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
      </div>

      {loading ? (
        <SkeletonTableRows rows={5} cols={4} />
      ) : !d ? (
        <EmptyState message="No insights yet. Publish exams and wait for students to complete them." />
      ) : (
        <>
          {/* Summary row */}
          <div className={css.statGrid} style={{ marginBottom: 24 }}>
            <div className={css.statCard}>
              <div className={css.statLabel}>Teaching Gap Topics</div>
              <div className={css.statValue} style={{ color: d.teachingGapTopics.length > 0 ? '#e53935' : '#2e7d32' }}>
                {d.teachingGapTopics.length}
              </div>
            </div>
            <div className={css.statCard}>
              <div className={css.statLabel}>Low Correct-Rate Qs</div>
              <div className={css.statValue} style={{ color: d.lowCorrectRateQuestions.length > 0 ? '#f57c00' : '#2e7d32' }}>
                {d.lowCorrectRateQuestions.length}
              </div>
            </div>
            <div className={css.statCard}>
              <div className={css.statLabel}>Improvement Rate</div>
              <div className={css.statValue}>{d.improvementRate != null ? `${d.improvementRate}%` : '—'}</div>
            </div>
            <div className={css.statCard}>
              <div className={css.statLabel}>Time-Pressured Students</div>
              <div className={css.statValue} style={{ color: d.timePressureStudents.length > 0 ? '#f57c00' : '#2e7d32' }}>
                {d.timePressureStudents.length}
              </div>
            </div>
          </div>

          {/* Teaching gap topics */}
          {d.teachingGapTopics.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#c62828', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <WarningAmberIcon fontSize="small" /> Teaching Gap Topics
              </div>
              <p style={{ fontSize: '0.8rem', color: '#90a4ae', marginBottom: 10 }}>
                Topics where &gt;60% of your students scored below 50% — these likely need to be retaught.
              </p>
              <div className={css.tableWrap}>
                <table className={css.table}>
                  <thead><tr><th>Topic</th><th>Failing Students</th><th>Total Students</th><th>Fail Rate</th></tr></thead>
                  <tbody>
                    {d.teachingGapTopics.map(t => (
                      <tr key={t.topic}>
                        <td className={css.bold}>{t.topic}</td>
                        <td style={{ color: '#e53935', fontWeight: 700 }}>{t.failingStudents}</td>
                        <td>{t.totalStudents}</td>
                        <td><span style={{ background: '#fce4ec', color: '#c62828', borderRadius: 10, padding: '2px 9px', fontWeight: 700, fontSize: '0.82rem' }}>{t.failRate}%</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Low correct-rate questions */}
          {d.lowCorrectRateQuestions.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#e65100', marginBottom: 8 }}>
                Questions Needing Review (&lt;40% correct rate)
              </div>
              <p style={{ fontSize: '0.8rem', color: '#90a4ae', marginBottom: 10 }}>
                These questions had very low correct rates — consider reviewing them in class or checking if the question is ambiguous.
              </p>
              <div className={css.tableWrap}>
                <table className={css.table}>
                  <thead><tr><th>Q#</th><th>Exam</th><th>Topic</th><th>Difficulty</th><th>Correct Rate</th><th>Avg Time</th></tr></thead>
                  <tbody>
                    {d.lowCorrectRateQuestions.map((q, i) => (
                      <tr key={`${q.examTitle}-${q.number}-${i}`}>
                        <td className={css.bold}>Q{q.number}</td>
                        <td>{q.examTitle || '—'}</td>
                        <td>{q.topic || '—'}</td>
                        <td><span className={css.diffChip}>{q.difficulty || '—'}</span></td>
                        <td><span style={{ background: '#fff3e0', color: '#e65100', borderRadius: 10, padding: '2px 9px', fontWeight: 700, fontSize: '0.82rem' }}>{q.correctRate}%</span></td>
                        <td>{q.avgTimeSecs != null ? `${q.avgTimeSecs}s` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Class arm comparison */}
          {d.classArmComparison.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#1a6e73', marginBottom: 8 }}>Class Arm Comparison</div>
              <p style={{ fontSize: '0.8rem', color: '#90a4ae', marginBottom: 10 }}>
                Compare performance across different arms for the same subject — large gaps may indicate uneven teaching or different student profiles.
              </p>
              {d.classArmComparison.map((comp, i) => (
                <div key={i} style={{ background: '#f7fbfc', borderRadius: 10, padding: '14px 18px', marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 10, color: '#37474f' }}>
                    {comp.subjectName}{comp.className ? ` · ${comp.className}` : ''}
                  </div>
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {comp.arms.map(arm => (
                      <div key={arm.arm} style={{ background: '#fff', borderRadius: 8, padding: '8px 16px', textAlign: 'center', minWidth: 80, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                        <div style={{ fontSize: '0.72rem', color: '#90a4ae' }}>Arm {arm.arm}</div>
                        <div style={{ fontWeight: 700, fontSize: '1.1rem', color: arm.avgPercentage < 50 ? '#e53935' : '#2e7d32' }}>
                          {arm.avgPercentage != null ? `${arm.avgPercentage}%` : '—'}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#b0bec5' }}>{arm.responses} resp.</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Time-pressured students */}
          {d.timePressureStudents.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#f57c00', marginBottom: 8 }}>Time Management Alerts</div>
              <p style={{ fontSize: '0.8rem', color: '#90a4ae', marginBottom: 10 }}>
                Students consistently using &gt;90% of exam time — they may need time management coaching.
              </p>
              <div className={css.tableWrap}>
                <table className={css.table}>
                  <thead><tr><th>Student</th><th>Avg Time Used</th><th>Exams</th></tr></thead>
                  <tbody>
                    {d.timePressureStudents.map(s => (
                      <tr key={s.studentId}>
                        <td className={css.bold}>{s.firstName} {s.lastName}</td>
                        <td><span style={{ background: '#fff3e0', color: '#e65100', borderRadius: 10, padding: '2px 9px', fontWeight: 700, fontSize: '0.82rem' }}>{s.avgTimeUsedPct}%</span></td>
                        <td>{s.examsCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {d.teachingGapTopics.length === 0 && d.lowCorrectRateQuestions.length === 0 && d.timePressureStudents.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#78909c' }}>
              <CheckCircleIcon style={{ fontSize: 48, color: '#43a047', marginBottom: 8, display: 'block', margin: '0 auto 10px' }} />
              No critical signals detected. Students are performing well across all topics.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Calendar Tab (read-only, audience-filtered, Premium-gated) ──────────────
function calEventTypeBadgeClass(type) {
  return {
    exam: css.calBadgeExam, holiday: css.calBadgeHoliday, meeting: css.calBadgeMeeting,
    deadline: css.calBadgeDeadline, activity: css.calBadgeActivity,
  }[type] || css.calBadgeOther;
}
function fmtCalDateHeading(d) {
  return new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtCalTime(d) {
  return new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function CalendarTab({ school }) {
  const managementEnabled = school?.subscription?.plan === 'premium' && school?.subscription?.schoolManagementEnabled !== false;
  const [monthDate, setMonthDate] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d; });
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!managementEnabled) { setLoading(false); return; }
    setLoading(true);
    const from = monthDate.toISOString().slice(0, 10);
    const to = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).toISOString().slice(0, 10);
    teacherFetch(`/teacher/calendar?from=${from}&to=${to}`)
      .then(({ ok, data }) => {
        if (ok) { setEvents(data.events || []); setError(''); }
        else setError(data.error || 'Failed to load events.');
      })
      .catch(() => setError('Connection error.'))
      .finally(() => setLoading(false));
  }, [monthDate, managementEnabled]);

  if (!managementEnabled) {
    return (
      <div className={css.card}>
        <div className={css.premiumLockBanner}>
          🔒 Calendar &amp; Events is a <strong>Premium</strong> feature. Ask your school admin to upgrade the plan to unlock it.
        </div>
      </div>
    );
  }

  const grouped = {};
  events.forEach(ev => {
    const key = new Date(ev.startDate).toISOString().slice(0, 10);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(ev);
  });
  const sortedKeys = Object.keys(grouped).sort();

  return (
    <div>
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      <div className={css.card}>
        <div className={css.calHeaderRow}>
          <button className={css.calNavBtn} onClick={() => setMonthDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}>‹</button>
          <span className={css.calMonthLabel}>{monthDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</span>
          <button className={css.calNavBtn} onClick={() => setMonthDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}>›</button>
        </div>

        {loading ? (
          <EmptyState message="Loading events…" />
        ) : sortedKeys.length === 0 ? (
          <EmptyState message="No events this month." />
        ) : (
          <div className={css.calAgenda}>
            {sortedKeys.map(dateKey => (
              <div key={dateKey} className={css.calDayGroup}>
                <div className={css.calDayHeading}>{fmtCalDateHeading(dateKey)}</div>
                {grouped[dateKey].map(ev => (
                  <div key={ev._id} className={css.calEventCard}>
                    <span className={`${css.calBadge} ${calEventTypeBadgeClass(ev.type)}`}>{ev.type}</span>
                    <div>
                      <div className={css.calEventTitle}>{ev.title}</div>
                      <div className={css.calEventMeta}>
                        {!ev.allDay && `${fmtCalTime(ev.startDate)}${ev.endDate ? ` – ${fmtCalTime(ev.endDate)}` : ''} · `}
                        {ev.audience === 'class'
                          ? ((ev.classIds || []).map(c => c.name).join(', ') || 'Class')
                          : ev.audience.charAt(0).toUpperCase() + ev.audience.slice(1)}
                        {ev.location ? ` · ${ev.location}` : ''}
                      </div>
                      {ev.description && <div className={css.calEventDesc}>{ev.description}</div>}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Attendance Tab ───────────────────────────────────────────────────────────
const ATTENDANCE_STATUSES = [
  { value: 'present', label: 'Present', color: '#2e7d32', bg: '#e8f5e9' },
  { value: 'absent',  label: 'Absent',  color: '#c62828', bg: '#fce4ec' },
  { value: 'late',    label: 'Late',    color: '#e65100', bg: '#fff3e0' },
  { value: 'excused', label: 'Excused', color: '#546e7a', bg: '#eceff1' },
];

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function AttendanceTab({ classes, subjects, school }) {
  const managementEnabled = school?.subscription?.plan === 'premium' && school?.subscription?.schoolManagementEnabled !== false;
  const [mode, setMode]           = useState('mark'); // 'mark' | 'history'
  const [classId, setClassId]     = useState('');
  const [classArm, setClassArm]   = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [date, setDate]           = useState(todayStr());
  const [periodLabel, setPeriodLabel] = useState('');
  const [roster, setRoster]       = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [history, setHistory]     = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const selectedClass = classes.find(c => String(c._id) === classId);
  const arms = selectedClass?.arms || [];

  const loadRoster = useCallback(async () => {
    if (!classId || !subjectId || !date) { setRoster([]); return; }
    setLoadingRoster(true);
    setError(''); setSuccess('');
    const params = new URLSearchParams({ classId, subjectId, date });
    if (classArm) params.set('classArm', classArm);
    const { ok, data } = await teacherFetch(`/teacher/attendance/roster?${params}`);
    if (ok) {
      setRoster(data.roster || []);
      if (data.periodLabel) setPeriodLabel(data.periodLabel);
    } else {
      setError(data.error || 'Failed to load roster.');
      setRoster([]);
    }
    setLoadingRoster(false);
  }, [classId, classArm, subjectId, date]);

  useEffect(() => { if (mode === 'mark') loadRoster(); }, [mode, loadRoster]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    const params = new URLSearchParams();
    if (classId) params.set('classId', classId);
    if (classArm) params.set('classArm', classArm);
    if (subjectId) params.set('subjectId', subjectId);
    const { ok, data } = await teacherFetch(`/teacher/attendance/history?${params}`);
    if (ok) setHistory(data.records || []);
    setLoadingHistory(false);
  }, [classId, classArm, subjectId]);

  useEffect(() => { if (mode === 'history') loadHistory(); }, [mode, loadHistory]);

  const setStatus = (membershipId, status) => {
    setRoster(prev => prev.map(s => s.membershipId === membershipId ? { ...s, status } : s));
  };
  const markAllPresent = () => setRoster(prev => prev.map(s => ({ ...s, status: 'present' })));

  const handleSubmit = async () => {
    if (!classId || !subjectId || !date || roster.length === 0) return;
    setSaving(true);
    setError(''); setSuccess('');
    const { ok, data } = await teacherFetch('/teacher/attendance', {
      method: 'POST',
      body: JSON.stringify({
        classId, classArm, date, subjectId, periodLabel,
        records: roster.map(s => ({ membershipId: s.membershipId, status: s.status, note: s.note })),
      }),
    });
    if (ok) setSuccess('Attendance saved.');
    else setError(data.error || 'Failed to save attendance.');
    setSaving(false);
  };

  if (!managementEnabled) {
    return (
      <div>
        <h2 className={css.tabTitle}>Attendance</h2>
        <div className={css.premiumLockBanner}>
          🔒 Attendance is a <strong>Premium</strong> feature. Ask your school admin to upgrade the plan from Settings to unlock it.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={css.tabBar}>
        <h2 className={css.tabTitle}>Attendance</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={mode === 'mark' ? css.primaryBtn : css.cancelBtn} onClick={() => setMode('mark')}>Mark Attendance</button>
          <button className={mode === 'history' ? css.primaryBtn : css.cancelBtn} onClick={() => setMode('history')}>History</button>
        </div>
      </div>

      {error && <div className={css.inlineError} style={{ marginBottom: 12 }}>{error}</div>}
      {success && <div className={css.inlineError} style={{ marginBottom: 12, color: '#2e7d32', background: '#e8f5e9' }}>{success}</div>}

      <div className={css.filterRow}>
        <select className={css.filterSelect} value={classId} onChange={e => { setClassId(e.target.value); setClassArm(''); }}>
          <option value="">Select Class…</option>
          {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
        <select className={css.filterSelect} value={classArm} onChange={e => setClassArm(e.target.value)} disabled={!classId}>
          <option value="">All Arms</option>
          {arms.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className={css.filterSelect} value={subjectId} onChange={e => setSubjectId(e.target.value)}>
          <option value="">Select Subject…</option>
          {subjects.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
        {mode === 'mark' && (
          <>
            <input type="date" className={css.filterInput} value={date} onChange={e => setDate(e.target.value)} />
            <input className={css.filterInput} placeholder="Period label (optional)" value={periodLabel} onChange={e => setPeriodLabel(e.target.value)} />
          </>
        )}
      </div>

      {mode === 'mark' ? (
        (!classId || !subjectId) ? (
          <EmptyState message="Select a class and subject to start marking attendance." />
        ) : loadingRoster ? (
          <SkeletonTableRows rows={5} cols={3} />
        ) : roster.length === 0 ? (
          <EmptyState message="No active students found for this class/arm." />
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
              <button className={css.cancelBtn} onClick={markAllPresent}>Mark all present</button>
            </div>
            <div className={css.tableWrap}>
              <table className={css.table}>
                <thead>
                  <tr><th>Student</th><th>ID</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {roster.map(s => (
                    <tr key={s.membershipId}>
                      <td className={css.bold}>{s.firstName} {s.lastName}</td>
                      <td>{s.studentId || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {ATTENDANCE_STATUSES.map(st => (
                            <button
                              key={st.value}
                              onClick={() => setStatus(s.membershipId, st.value)}
                              style={{
                                background: s.status === st.value ? st.color : st.bg,
                                color: s.status === st.value ? '#fff' : st.color,
                                border: 'none', borderRadius: 14, padding: '4px 10px',
                                fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
                              }}
                            >
                              {st.label}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button className={css.saveBtn} onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving…' : 'Save Attendance'}
              </button>
            </div>
          </>
        )
      ) : (
        loadingHistory ? (
          <SkeletonTableRows rows={6} cols={5} />
        ) : history.length === 0 ? (
          <EmptyState message="No attendance records yet." />
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr><th>Date</th><th>Class</th><th>Subject</th><th>Period</th><th>Present</th><th>Absent</th><th>Late</th><th>Excused</th></tr>
              </thead>
              <tbody>
                {history.map(r => (
                  <tr key={r._id}>
                    <td>{r.date}</td>
                    <td>{r.className} {r.classArm ? `(${r.classArm})` : ''}</td>
                    <td>{r.subjectName}</td>
                    <td>{r.periodLabel || '—'}</td>
                    <td>{r.counts.present}</td>
                    <td>{r.counts.absent}</td>
                    <td>{r.counts.late}</td>
                    <td>{r.counts.excused}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

// ─── Lesson Plans Tab ─────────────────────────────────────────────────────────
const LESSON_PLAN_STATUSES = ['draft', 'submitted', 'approved', 'needs_revision'];
const lessonPlanStatusLabel = { draft: 'Draft', submitted: 'Submitted', approved: 'Approved', needs_revision: 'Needs Revision' };
const lessonPlanStatusChipClass = {
  draft: css.chipDraft,
  submitted: css.chipScheduled,
  approved: css.chipClosed,
  needs_revision: css.chipDraft,
};

function currentAcademicSession() {
  const now = new Date();
  const y = now.getFullYear();
  // Nigerian academic sessions typically start around September.
  return now.getMonth() >= 8 ? `${y}/${y + 1}` : `${y - 1}/${y}`;
}

function defaultLessonStages() {
  return [['Introduction', 5], ['Presentation 1', 0], ['Presentation 2', 0], ['Evaluation', 0], ['Conclusion', 0]].map(([stage, durationMinutes]) => ({
    stage, durationMinutes, teacherActivities: '', studentActivities: '',
  }));
}

function renumberPresentationStages(stages) {
  let presentationNumber = 0;
  return stages.map(stage => {
    if (!/^presentation\b/i.test(stage.stage)) return stage;
    presentationNumber += 1;
    return { ...stage, stage: `Presentation ${presentationNumber}` };
  });
}

function lessonPlanExportRows(plan, includeRemark = false) {
  const rows = [
    ['Lesson Plan', (plan.topics || []).join(', ') || plan.topic || ''], ['Topics', (plan.topics || []).join('\n') || plan.topic || ''], ['Class', plan.classId?.name || ''], ['Subject', plan.subjectId?.name || ''],
    ['Term', plan.term || ''], ['Session', plan.session || ''], ['Week', plan.week || ''], ['Lesson number(s)', plan.lessonNumbers || ''], ['Sub-topic', plan.subTopic || ''],
    ['Teacher', plan.teacherName || '' ], ['No. in class', plan.noInClass || ''], ['Average age', plan.averageAge || ''],
    ['Sex', plan.sex || ''], ['Date', plan.date ? String(plan.date).slice(0, 10) : ''], ['Rationale', plan.rationale || ''],
    ['Previous knowledge', plan.previousKnowledge || ''], ['Objectives', (plan.objectives || []).join('\n')],
    ['Teaching methods', (plan.teachingMethods || []).join('\n')], ['Teaching materials', (plan.teachingMaterials || []).join('\n')],
    ['References', (plan.references || []).join('\n')],
  ];
  if (includeRemark && plan.principalRemark) rows.push(['Principal remark', plan.principalRemark]);
  return rows;
}

function exportLessonPlanExcel(plan) {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(lessonPlanExportRows(plan, plan.status === 'approved')), 'Summary');
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([
    ['Stage', 'Duration (minutes)', 'Teacher activities', 'Student activities'],
    ...(plan.developmentStages || []).map(stage => [stage.stage, stage.durationMinutes, stage.teacherActivities, stage.studentActivities]),
  ]), 'Development');
  XLSX.writeFile(workbook, `${((plan.topics || []).join('_') || plan.topic || 'lesson-plan').replace(/[^a-z0-9]+/gi, '_')}.xlsx`);
}

function exportLessonPlanPdf(plan) {
  const popup = window.open('', '_blank', 'width=1000,height=800');
  if (!popup) return;
  const rows = lessonPlanExportRows(plan, plan.status === 'approved').map(row => `<tr><th>${row[0]}</th><td>${String(row[1]).replace(/</g, '&lt;').replace(/\n/g, '<br>')}</td></tr>`).join('');
  const stages = (plan.developmentStages || []).map(stage => `<tr><td>${stage.stage || ''}</td><td>${stage.durationMinutes || 0}</td><td>${stage.teacherActivities || ''}</td><td>${stage.studentActivities || ''}</td></tr>`).join('');
  popup.document.write(`<html><head><title>${plan.topic || 'Lesson Plan'}</title><style>body{font:14px Arial;color:#263238;padding:28px}h1{font-size:22px}table{border-collapse:collapse;width:100%;margin:12px 0 24px}th,td{border:1px solid #b0bec5;padding:7px;text-align:left;vertical-align:top}th{background:#eceff1;width:22%}@media print{button{display:none}}</style></head><body><h1>Lesson Plan: ${plan.topic || ''}</h1><table>${rows}</table><h2>Development of the Lesson</h2><table><tr><th>Stage</th><th>Minutes</th><th>Teacher activities</th><th>Student activities</th></tr>${stages}</table><button onclick="window.print()">Print / Save as PDF</button></body></html>`);
  popup.document.close();
}

function LessonPlanFormModal({ classes, subjects, school, teacher, aiEnabled, initial, onClose, onSaved }) {
  const isEdit = !!initial?._id;
  const [subjectId, setSubjectId] = useState(initial?.subjectId?._id || initial?.subjectId || '');
  const [classId, setClassId] = useState(initial?.classId?._id || initial?.classId || '');
  const [classArm, setClassArm] = useState(initial?.classArm || '');
  const [term, setTerm] = useState(initial?.term || school?.currentTerm || 'first');
  const [session, setSession] = useState(initial?.session || school?.academicSession || currentAcademicSession());
  const [week, setWeek] = useState(initial?.week || undefined);
  const [lessonNumbers, setLessonNumbers] = useState(initial?.lessonNumbers || '');
  const [topics, setTopics] = useState(initial?.topics?.length ? initial.topics : (initial?.topic ? [initial.topic] : []));
  const [topic, setTopic] = useState(initial?.topic || initial?.topics?.join(', ') || '');
  const [durationMinutes, setDurationMinutes] = useState(initial?.durationMinutes || undefined);
  const [teacherName, setTeacherName] = useState(initial?.teacherName || [teacher?.firstName, teacher?.lastName].filter(Boolean).join(' '));
  const [noInClass, setNoInClass] = useState(initial?.noInClass ?? '');
  const [averageAge, setAverageAge] = useState(initial?.averageAge ?? '');
  const [sex, setSex] = useState(initial?.sex || 'mixed');
  const [date, setDate] = useState(initial?.date ? String(initial.date).slice(0, 10) : '');
  const [subTopic, setSubTopic] = useState(initial?.subTopic || '');
  const [rationale, setRationale] = useState(initial?.rationale || '');
  const [previousKnowledge, setPreviousKnowledge] = useState(initial?.previousKnowledge || '');
  const [teachingMaterials, setTeachingMaterials] = useState(initial?.teachingMaterials?.length ? initial.teachingMaterials : ['']);
  const [references, setReferences] = useState(initial?.references?.length ? initial.references : ['']);
  const [teachingMethods, setTeachingMethods] = useState(initial?.teachingMethods?.length ? initial.teachingMethods : ['']);
  const [developmentStages, setDevelopmentStages] = useState(initial?.developmentStages?.length ? initial.developmentStages : defaultLessonStages());
  const [objectives, setObjectives] = useState(initial?.objectives?.length ? initial.objectives : ['']);
  const [resources, setResources] = useState(initial?.resources?.length ? initial.resources : ['']);
  const [aiGenerated, setAiGenerated] = useState(initial?.aiGenerated || false);
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [showAllPlanCoverageTopics, setShowAllPlanCoverageTopics] = useState(false);
  const [selectedCurriculumTopics, setSelectedCurriculumTopics] = useState(initial?.topics?.length ? initial.topics : (initial?.topic ? [initial.topic] : []));
  const statsRequestRef = useRef(0);

  const selectedClass = classes.find(c => String(c._id) === classId);
  const selectedSubject = subjects.find(s => String(s._id) === subjectId);
  const classCurriculum = (selectedSubject?.curriculum || []).find(c => String(c.classId) === classId);
  const curriculumTopics = classCurriculum?.topics || [];
  const arms = selectedClass?.arms || [];

  useEffect(() => {
    if (!subjectId || !classId) {
      setCoverage(null);
      return;
    }
    let alive = true;
    setCoverageLoading(true);
    teacherFetch(`/teacher/lesson-plans/coverage/${subjectId}/${classId}`).then(({ ok, data }) => {
      if (alive && ok) setCoverage(data.coverage || null);
    }).finally(() => {
      if (alive) setCoverageLoading(false);
    });
    return () => { alive = false; };
  }, [subjectId, classId]);

  useEffect(() => {
    if (isEdit || !classId) return undefined;
    const requestId = ++statsRequestRef.current;
    const timer = setTimeout(async () => {
      const params = new URLSearchParams({ classId });
      if (classArm) params.set('classArm', classArm);
      const { ok, data } = await teacherFetch(`/teacher/lesson-plans/class-stats?${params}`);
      if (ok && requestId === statsRequestRef.current) {
        setNoInClass(data.count ?? '');
        setAverageAge(data.averageAge ?? '');
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [classId, classArm, isEdit]);

  const toggleCurriculumTopic = (curriculumTopic) => {
    setTopics(previous => {
      const next = previous.includes(curriculumTopic)
        ? previous.filter(item => item !== curriculumTopic)
        : [...previous, curriculumTopic];
      setSelectedCurriculumTopics(next);
      setTopic(next.join(', '));
      return next;
    });
  };

  const updateTopicText = (value) => {
    setTopic(value);
    setTopics(value.split(/\s*(?:,|&|\band\b)\s*/i).map(item => item.trim()).filter(Boolean));
    setSelectedCurriculumTopics([]);
  };

  const updateListItem = (list, setList, idx, value) => {
    setList(prev => prev.map((v, i) => (i === idx ? value : v)));
  };
  const addListItem = (setList) => setList(prev => [...prev, '']);
  const removeListItem = (setList) => (idx) => setList(prev => prev.filter((_, i) => i !== idx));

  const updateStage = (index, changes) => setDevelopmentStages(prev => prev.map((stage, stageIndex) => stageIndex === index ? { ...stage, ...changes } : stage));
  const addPresentationStage = () => setDevelopmentStages(prev => {
    const evaluationIndex = prev.findIndex(stage => /^evaluation$/i.test(stage.stage));
    const presentationCount = prev.filter(stage => /^presentation\b/i.test(stage.stage)).length;
    const newStage = { stage: `Presentation ${presentationCount + 1}`, durationMinutes: 0, teacherActivities: '', studentActivities: '' };
    const insertAt = evaluationIndex < 0 ? prev.length : evaluationIndex;
    return [...prev.slice(0, insertAt), newStage, ...prev.slice(insertAt)];
  });
  const removePresentationStage = (index) => setDevelopmentStages(prev => renumberPresentationStages(prev.filter((stage, stageIndex) => stageIndex !== index)));
  const totalStageMinutes = developmentStages.reduce((total, stage) => total + (Number(stage.durationMinutes) || 0), 0);
  const requestedDuration = Number(durationMinutes) || 45;
  const stageTimeDifference = requestedDuration - totalStageMinutes;

  const handleAiDraft = async () => {
    if (!subjectId || !classId || !lessonNumbers.trim() || !topics.length) {
      setError('Select a subject, enter the lesson number(s), and enter a topic before drafting with AI.');
      return;
    }
    setDrafting(true); setError('');
    const { ok, data } = await teacherFetch('/teacher/lesson-plans/ai-draft', {
      method: 'POST',
      body: JSON.stringify({ subjectId, classId, topic: topic.trim(), topics, week, lessonNumbers: lessonNumbers.trim(), durationMinutes }),
    });
    if (ok) {
      setObjectives(data.draft.objectives?.length ? data.draft.objectives : ['']);
      setResources(data.draft.resources?.length ? data.draft.resources : ['']);
      setSubTopic(data.draft.subTopic || '');
      setRationale(data.draft.rationale || '');
      setPreviousKnowledge(data.draft.previousKnowledge || '');
      setTeachingMaterials(data.draft.teachingMaterials?.length ? data.draft.teachingMaterials : ['']);
      setReferences(data.draft.references?.length ? data.draft.references : ['']);
      setTeachingMethods(data.draft.teachingMethods?.length ? data.draft.teachingMethods : ['']);
      setDevelopmentStages(data.draft.developmentStages?.length ? renumberPresentationStages(data.draft.developmentStages) : defaultLessonStages());
      setAiGenerated(true);
    } else {
      setError(data.error || 'AI draft generation failed.');
    }
    setDrafting(false);
  };

  const handleSave = async () => {
    if (!subjectId || !classId || !term || !session || !week || !lessonNumbers.trim() || !topics.length) {
      setError('Subject, class, term, session, week, lesson number(s) and topic are required.');
      return;
    }
    if (stageTimeDifference !== 0) {
      setError(`Stage time is ${stageTimeDifference > 0 ? 'too little' : 'too much'} by ${Math.abs(stageTimeDifference)} minute${Math.abs(stageTimeDifference) === 1 ? '' : 's'}. Adjust the stage times to total ${requestedDuration} minutes.`);
      return;
    }
    setSaving(true); setError('');
    const payload = {
      subjectId, classId, classArm, term, session, week: Number(week), lessonNumbers: lessonNumbers.trim(), topic: topic.trim(), topics,
      teacherName: teacherName.trim(), noInClass, averageAge, sex, date, subTopic: subTopic.trim(),
      rationale: rationale.trim(), previousKnowledge: previousKnowledge.trim(),
      teachingMaterials: teachingMaterials.map(item => item.trim()).filter(Boolean),
      references: references.map(item => item.trim()).filter(Boolean),
      teachingMethods: teachingMethods.map(item => item.trim()).filter(Boolean),
      developmentStages,
      objectives: objectives.map(o => o.trim()).filter(Boolean),
      resources: resources.map(r => r.trim()).filter(Boolean),
      durationMinutes: Number(durationMinutes) || 45,
      aiGenerated,
    };
    const { ok, data } = isEdit
      ? await teacherFetch(`/teacher/lesson-plans/${initial._id}`, { method: 'PATCH', body: JSON.stringify(payload) })
      : await teacherFetch('/teacher/lesson-plans', { method: 'POST', body: JSON.stringify(payload) });
    if (ok) {
      onSaved(data.plan);
    } else {
      setError(data.error || 'Failed to save lesson plan.');
    }
    setSaving(false);
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.lessonPlanModal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <div className={css.modalHeader}>
          <h2 className={css.modalTitle}>{isEdit ? 'Edit Lesson Plan' : 'New Lesson Plan'}</h2>
          <button className={css.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div className={css.modalBody}>
          {error && <div className={css.inlineError} style={{ marginBottom: 12 }}>{error}</div>}

          <div className={css.filterRow} style={{ marginBottom: 10 }}>
            <select className={css.filterSelect} value={subjectId} onChange={e => setSubjectId(e.target.value)} disabled={isEdit}>
              <option value="">Select Subject…</option>
              {subjects.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
            <select className={css.filterSelect} value={classId} onChange={e => { setClassId(e.target.value); setClassArm(''); }} disabled={isEdit}>
              <option value="">Select Class…</option>
              {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
            <select className={css.filterSelect} value={classArm} onChange={e => setClassArm(e.target.value)} disabled={isEdit || !classId}>
              <option value="">All Arms</option>
              {arms.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <div className={css.filterRow} style={{ marginBottom: 10 }}>
            <select className={css.filterSelect} value={term} onChange={e => setTerm(e.target.value)} disabled={isEdit}>
              <option value="first">First Term</option>
              <option value="second">Second Term</option>
              <option value="third">Third Term</option>
            </select>
            <input className={css.filterInput} placeholder="Session (e.g. 2024/2025)" value={session} onChange={e => setSession(e.target.value)} disabled={isEdit} />
            <input type="number" min="1" className={css.filterInput} placeholder="Week (Number)" value={week} onChange={e => setWeek(e.target.value)} style={{ maxWidth: 90 }} />
            <input className={css.filterInput} placeholder="Lesson(s), e.g. 1 or 3,4 or 3 & 5" value={lessonNumbers} onChange={e => setLessonNumbers(e.target.value)} style={{ minWidth: 190 }} />
            <input type="number" min="1" className={css.filterInput} placeholder="Duration (mins)" value={durationMinutes} onChange={e => setDurationMinutes(e.target.value)} style={{ maxWidth: 130 }} />
          </div>

          <div className={css.filterRow} style={{ marginBottom: 10 }}>
            <input className={css.filterInput} placeholder="Teacher name" value={teacherName} onChange={e => setTeacherName(e.target.value)} />
            <input type="number" min="1" className={css.filterInput} placeholder="No. in class" value={noInClass} onChange={e => setNoInClass(e.target.value)} />
            <input type="number" min="1" className={css.filterInput} placeholder="Average age" value={averageAge} onChange={e => setAverageAge(e.target.value)} />
            <select className={css.filterSelect} value={sex} onChange={e => setSex(e.target.value)}>
              <option value="mixed">Mixed</option><option value="male">Male</option><option value="female">Female</option>
            </select>
            <input type="date" className={css.filterInput} value={date} onChange={e => setDate(e.target.value)} />
          </div>

          {(curriculumTopics.length > 0 || coverageLoading) && (
            <div className={css.currGenPanel}>
              <div className={css.currGenHeader}>
                <span className={css.currGenTitle}>Curriculum Grounding</span>
                <span className={css.currGenSub}>
                  Choose a curriculum topic to keep this lesson aligned with the uploaded scheme of work.
                </span>
              </div>
              {coverageLoading ? (
                <span className={css.currGenSub}>Checking previously covered topics…</span>
              ) : (
                <>
                  <div className={css.currGenTopicsWrap}>
                    <div className={css.currGenTopicsRow}>
                      {curriculumTopics.map(curriculumTopic => (
                        <button
                          key={curriculumTopic}
                          type="button"
                          className={`${css.currGenTopicBtn} ${selectedCurriculumTopics.includes(curriculumTopic) ? css.currGenTopicBtnSel : ''}`}
                          onClick={() => toggleCurriculumTopic(curriculumTopic)}
                        >
                          {curriculumTopic}
                        </button>
                      ))}
                    </div>
                    {coverage && coverage.total > 0 && (
                      <div className={css.coverageSection}>
                        <div className={css.coverageBarWrap}>
                          <div className={css.coverageBar}>
                            <div className={css.coverageBarFill} style={{ width: `${Math.round((coverage.coveredCount / coverage.total) * 100)}%` }} />
                          </div>
                          <span className={css.coverageBarLabel}>
                            {coverage.coveredCount} of {coverage.total} curriculum topics covered
                          </span>
                        </div>
                        <div className={css.coverageTopicsGrid}>
                          {(showAllPlanCoverageTopics ? coverage.topics : coverage.topics.slice(0, 5)).map(item => (
                            <span key={item.name} className={`${css.coverageTopicChip} ${item.covered ? css.coverageTopicChipCovered : css.coverageTopicChipUncovered}`}>
                              {item.name}{item.covered ? ` · ${item.lessonCount || 1} taught` : ' · not taught'}
                            </span>
                          ))}
                        </div>
                        {coverage.topics.length > 5 && <button className={css.aiTopicsMoreBtn} type="button" onClick={() => setShowAllPlanCoverageTopics(value => !value)}>{showAllPlanCoverageTopics ? '▲ Show less' : `▼ +${coverage.topics.length - 5} more topics`}</button>}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <div className={css.lessonPlanTopicRow}>
            <input className={css.filterInput} placeholder="Topic(s), separated by comma or &" value={topic} onChange={e => updateTopicText(e.target.value)} />
            <input className={css.filterInput} placeholder="Sub-topic" value={subTopic} onChange={e => setSubTopic(e.target.value)} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <button
              className={css.primaryBtn}
              type="button"
              onClick={handleAiDraft}
              disabled={drafting || !aiEnabled}
              title={aiEnabled ? '' : 'AI generation is not enabled on your plan'}
            >
              {aiEnabled ? <AutoFixHighIcon fontSize="small" /> : <LockIcon fontSize="small" />}
              {drafting ? ' Drafting…' : ' AI Draft'}
            </button>
          </div>

          <textarea className={css.filterInput} style={{ width: '100%', minHeight: 76, boxSizing: 'border-box', marginBottom: 10, resize: 'vertical' }} value={rationale} onChange={e => setRationale(e.target.value)} placeholder="Rationale / purpose of lesson" />
          <textarea className={css.filterInput} style={{ width: '100%', minHeight: 64, boxSizing: 'border-box', marginBottom: 14, resize: 'vertical' }} value={previousKnowledge} onChange={e => setPreviousKnowledge(e.target.value)} placeholder="Previous knowledge" />

          <h4 style={{ marginBottom: 6, color: '#263238', fontSize: '0.85rem' }}>Learning Objectives</h4>
          {objectives.map((o, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <input
                className={css.filterInput}
                style={{ flex: 1, boxSizing: 'border-box' }}
                value={o}
                onChange={e => updateListItem(objectives, setObjectives, i, e.target.value)}
                placeholder={`Objective ${i + 1}`}
              />
              {objectives.length > 1 && (
                <button className={css.cancelBtn} type="button" onClick={() => removeListItem(setObjectives)(i)}>✕</button>
              )}
            </div>
          ))}
          <button className={css.cancelBtn} type="button" onClick={() => addListItem(setObjectives)} style={{ marginBottom: 16 }}>+ Add Objective</button>

          <h4 style={{ marginBottom: 6, color: '#263238', fontSize: '0.85rem' }}>Development of the Lesson · {requestedDuration} minutes</h4>
          {stageTimeDifference !== 0 && <div className={css.inlineError} style={{ marginBottom: 10 }}>Stage time is {stageTimeDifference > 0 ? 'too little' : 'too much'} by {Math.abs(stageTimeDifference)} minute{Math.abs(stageTimeDifference) === 1 ? '' : 's'}. The stages must total exactly {requestedDuration} minutes before saving.</div>}
          <div className={css.tableWrap} style={{ marginBottom: 16 }}>
            <table className={css.table}>
              <thead><tr><th>Stage</th><th>Min</th><th>Teacher activities</th><th>Student activities</th><th /></tr></thead>
              <tbody>
                {developmentStages.map((row, i) => (
                  <tr key={`${row.stage}-${i}`}>
                    <td>{/^presentation\b/i.test(row.stage) ? <input className={css.filterInput} value={row.stage} onChange={e => updateStage(i, { stage: e.target.value })} /> : <strong>{row.stage}</strong>}</td>
                    <td><input type="number" min="0" className={css.filterInput} style={{ width: 64 }} value={row.durationMinutes} onChange={e => updateStage(i, { durationMinutes: Number(e.target.value) })} /></td>
                    <td><textarea className={css.filterInput} value={row.teacherActivities} onChange={e => updateStage(i, { teacherActivities: e.target.value })} /></td>
                    <td><textarea className={css.filterInput} value={row.studentActivities} onChange={e => updateStage(i, { studentActivities: e.target.value })} /></td>
                    <td>{/^presentation\b/i.test(row.stage) && <button className={css.cancelBtn} type="button" onClick={() => removePresentationStage(i)} title="Delete presentation stage">✕</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className={css.cancelBtn} type="button" onClick={addPresentationStage} style={{ marginBottom: 16 }}>+ Add Presentation Stage</button>

          <h4 style={{ marginBottom: 6, color: '#263238', fontSize: '0.85rem' }}>Teaching Methods</h4>
          {teachingMethods.map((item, i) => <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}><input className={css.filterInput} style={{ flex: 1 }} value={item} onChange={e => updateListItem(teachingMethods, setTeachingMethods, i, e.target.value)} placeholder={`Method ${i + 1}`} />{teachingMethods.length > 1 && <button className={css.cancelBtn} type="button" onClick={() => removeListItem(setTeachingMethods)(i)}>✕</button>}</div>)}
          <button className={css.cancelBtn} type="button" onClick={() => addListItem(setTeachingMethods)} style={{ marginBottom: 16 }}>+ Add Method</button>

          <h4 style={{ marginBottom: 6, color: '#263238', fontSize: '0.85rem' }}>Teaching Materials</h4>
          {teachingMaterials.map((item, i) => <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}><input className={css.filterInput} style={{ flex: 1 }} value={item} onChange={e => updateListItem(teachingMaterials, setTeachingMaterials, i, e.target.value)} placeholder={`Material ${i + 1}`} />{teachingMaterials.length > 1 && <button className={css.cancelBtn} type="button" onClick={() => removeListItem(setTeachingMaterials)(i)}>✕</button>}</div>)}
          <button className={css.cancelBtn} type="button" onClick={() => addListItem(setTeachingMaterials)} style={{ marginBottom: 16 }}>+ Add Material</button>

          <h4 style={{ marginBottom: 6, color: '#263238', fontSize: '0.85rem' }}>References</h4>
          {references.map((item, i) => <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}><input className={css.filterInput} style={{ flex: 1 }} value={item} onChange={e => updateListItem(references, setReferences, i, e.target.value)} placeholder={`Reference ${i + 1}`} />{references.length > 1 && <button className={css.cancelBtn} type="button" onClick={() => removeListItem(setReferences)(i)}>✕</button>}</div>)}
          <button className={css.cancelBtn} type="button" onClick={() => addListItem(setReferences)} style={{ marginBottom: 16 }}>+ Add Reference</button>

          <h4 style={{ marginBottom: 6, color: '#263238', fontSize: '0.85rem' }}>Resources</h4>
          {resources.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <input
                className={css.filterInput}
                style={{ flex: 1, boxSizing: 'border-box' }}
                value={r}
                onChange={e => updateListItem(resources, setResources, i, e.target.value)}
                placeholder={`Resource ${i + 1}`}
              />
              {resources.length > 1 && (
                <button className={css.cancelBtn} type="button" onClick={() => removeListItem(setResources)(i)}>✕</button>
              )}
            </div>
          ))}
          <button className={css.cancelBtn} type="button" onClick={() => addListItem(setResources)}>+ Add Resource</button>
        </div>
        <div className={css.modalFooter}>
          <button className={css.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save as Draft'}</button>
          <button className={css.cancelBtn} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function LessonPlansTab({ classes, subjects, school, teacher }) {
  const managementEnabled = school?.subscription?.plan === 'premium' && school?.subscription?.schoolManagementEnabled !== false;
  const aiEnabled = school?.subscription?.plan !== 'standard' || !!school?.subscription?.aiQuestionsEnabled;
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [armFilter, setArmFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [termFilter, setTermFilter] = useState('');
  const [sessionFilter, setSessionFilter] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modalState, setModalState] = useState(null); // null | 'new' | plan object to edit
  const [submitting, setSubmitting] = useState('');
  const [deleting, setDeleting] = useState('');
  const [deleteConfirmPlan, setDeleteConfirmPlan] = useState(null);
  const [expandedPlan, setExpandedPlan] = useState('');

  const load = useCallback(async () => {
    if (!managementEnabled) { setLoading(false); return; }
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (classFilter) params.set('classId', classFilter);
    if (armFilter) params.set('classArm', armFilter);
    if (subjectFilter) params.set('subjectId', subjectFilter);
    if (termFilter) params.set('term', termFilter);
    if (sessionFilter) params.set('session', sessionFilter);
    const { ok, data } = await teacherFetch(`/teacher/lesson-plans?${params}`);
    if (ok) setPlans(data.plans || []);
    else setError(data.error || 'Failed to load lesson plans.');
    setLoading(false);
  }, [managementEnabled, statusFilter, classFilter, armFilter, subjectFilter, termFilter, sessionFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (plan) => {
    setModalState(null);
    setSuccess(plan.status === 'draft' ? 'Lesson plan saved.' : 'Lesson plan updated.');
    setTimeout(() => setSuccess(''), 3000);
    load();
  };

  const handleSubmit = async (id) => {
    setSubmitting(id); setError('');
    const { ok, data } = await teacherFetch(`/teacher/lesson-plans/${id}/submit`, { method: 'POST' });
    if (ok) { setSuccess('Lesson plan submitted for review.'); setTimeout(() => setSuccess(''), 3000); load(); }
    else setError(data.error || 'Failed to submit lesson plan.');
    setSubmitting('');
  };

  const handleDelete = async () => {
    if (!deleteConfirmPlan) return;
    setDeleting(deleteConfirmPlan._id); setError('');
    const { ok, data } = await teacherFetch(`/teacher/lesson-plans/${deleteConfirmPlan._id}`, { method: 'DELETE' });
    if (ok) { setDeleteConfirmPlan(null); setSuccess('Lesson plan deleted.'); setTimeout(() => setSuccess(''), 3000); load(); }
    else setError(data.error || 'Failed to delete lesson plan.');
    setDeleting('');
  };

  if (!managementEnabled) {
    return (
      <div>
        <h2 className={css.tabTitle}>Lesson Plans</h2>
        <div className={css.premiumLockBanner}>
          🔒 Lesson Plans is a <strong>Premium</strong> feature. Ask your school admin to upgrade the plan from Settings to unlock it.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={css.tabBar}>
        <h2 className={css.tabTitle}>Lesson Plans</h2>
        <button className={css.primaryBtn} onClick={() => setModalState('new')}>
          <AddIcon fontSize="small" /> New Lesson Plan
        </button>
      </div>

      {error && <div className={css.inlineError} style={{ marginBottom: 12 }}>{error}</div>}
      {success && <div className={css.inlineSuccess} style={{ marginBottom: 12 }}>{success}</div>}

      <div className={css.filterRow}>
        <select className={css.filterSelect} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {LESSON_PLAN_STATUSES.map(s => <option key={s} value={s}>{lessonPlanStatusLabel[s]}</option>)}
        </select>
        <select className={css.filterSelect} value={classFilter} onChange={e => { setClassFilter(e.target.value); setArmFilter(''); }}><option value="">All Classes</option>{classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}</select>
        <select className={css.filterSelect} value={armFilter} onChange={e => setArmFilter(e.target.value)} disabled={!classFilter}><option value="">All Arms</option>{(classes.find(c => String(c._id) === String(classFilter))?.arms || []).map(arm => <option key={arm} value={arm}>{arm}</option>)}</select>
        <select className={css.filterSelect} value={subjectFilter} onChange={e => setSubjectFilter(e.target.value)}><option value="">All Subjects</option>{subjects.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}</select>
        <select className={css.filterSelect} value={termFilter} onChange={e => setTermFilter(e.target.value)}><option value="">All Terms</option><option value="first">First Term</option><option value="second">Second Term</option><option value="third">Third Term</option></select>
        <input className={css.filterSelect} style={{ width: 130 }} placeholder="Session" value={sessionFilter} onChange={e => setSessionFilter(e.target.value)} />
      </div>

      {loading ? (
        <SkeletonTableRows rows={5} cols={6} />
      ) : plans.length === 0 ? (
        <EmptyState message="No lesson plans yet. Create one to get started." />
      ) : (
        <div className={css.tableWrap}>
          <table className={css.table}>
            <thead>
              <tr><th>Topic</th><th>Class</th><th>Subject</th><th>Term / Wk</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {plans.map(p => (
                <React.Fragment key={p._id}>
                <tr>
                  <td className={css.bold}>{(p.topics || []).join(', ') || p.topic}</td>
                  <td>{p.classId?.name || '—'} {p.classArm ? `(${p.classArm})` : ''}</td>
                  <td>{p.subjectId?.name || '—'}</td>
                  <td>{p.term ? `${p.term.charAt(0).toUpperCase()}${p.term.slice(1)}` : '—'} · Wk {p.week}{p.lessonNumbers ? ` · Lesson(s) ${p.lessonNumbers}` : ''}</td>
                  <td>
                    <span className={`${css.chip} ${lessonPlanStatusChipClass[p.status] || css.chipDraft}`}>
                      {lessonPlanStatusLabel[p.status] || p.status}
                    </span>
                    {p.status === 'needs_revision' && p.reviewNotes && (
                      <div className={css.lessonPlanReviewNote} style={{ fontSize: '0.72rem', color: '#7b2923', marginTop: 4, padding: '6px 8px', border: '1px solid #efc5bf', borderRadius: 6 }}>{p.reviewNotes}</div>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className={css.cancelBtn} onClick={() => setExpandedPlan(expandedPlan === p._id ? '' : p._id)} title="Show lesson plan details">{expandedPlan === p._id ? 'Hide' : 'View'}</button>
                      {(p.status === 'draft' || p.status === 'needs_revision') && (
                        <>
                          <button className={css.cancelBtn} onClick={() => setModalState(p)}><EditIcon fontSize="small" /></button>
                          <button className={css.saveBtn} onClick={() => handleSubmit(p._id)} disabled={submitting === p._id}>
                            {submitting === p._id ? 'Submitting…' : 'Submit'}
                          </button>
                        </>
                      )}
                      {p.status === 'draft' && (
                        <button className={`${css.cancelBtn} ${css.lessonPlanDeleteBtn}`} onClick={() => setDeleteConfirmPlan(p)} disabled={deleting === p._id} title="Delete draft lesson plan" aria-label="Delete draft lesson plan">
                          <DeleteIcon fontSize="small" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedPlan === p._id && <tr><td colSpan="6"><div className={css.lessonPlanDetails}>
                  <div className={css.lessonPlanDetailHeader}>
                    <div>
                      <h3 className={css.lessonPlanDetailTitle}>{(p.topics || []).join(', ') || p.topic}</h3>
                      <p className={css.lessonPlanDetailSubtitle}>{p.subjectId?.name || 'Subject'} · {p.classId?.name || 'Class'}{p.classArm ? ` · ${p.classArm}` : ''}</p>
                    </div>
                  </div>
                  <div className={css.lessonPlanDetailGrid}>
                    {[['Teacher', p.teacherName || '—'], ['Term / session', `${p.term || '—'} · ${p.session || '—'}`], ['Week', p.week || '—'], ['Lesson(s)', p.lessonNumbers || '—'], ['Duration', `${p.durationMinutes || 0} minutes`], ['Class size', p.noInClass ?? '—'], ['Average age', p.averageAge ?? '—'], ['Sex', p.sex || '—'], ['Date', p.date ? String(p.date).slice(0, 10) : '—']].map(([label, value]) => <div className={css.lessonPlanDetailItem} key={label}><span className={css.lessonPlanDetailLabel}>{label}</span><span className={css.lessonPlanDetailValue}>{value}</span></div>)}
                  </div>
                  <div className={css.lessonPlanDetailSection}><h4>Lesson Focus</h4><p><strong>Topic(s):</strong> {(p.topics || []).join(', ') || p.topic || '—'}{p.subTopic ? `\nSub-topic: ${p.subTopic}` : ''}</p></div>
                  <div className={css.lessonPlanDetailSection}><h4>Rationale and Previous Knowledge</h4><div>{formatLargeText(`**Rationale:** ${p.rationale || '—'}\n**Previous knowledge:** ${p.previousKnowledge || '—'}`)}</div></div>
                  <div className={css.lessonPlanDetailSection}><h4>Learning Objectives</h4>{p.objectives?.length ? <ul className={css.lessonPlanDetailList}>{p.objectives.map((item, i) => <li key={i}>{item}</li>)}</ul> : <p>—</p>}</div>
                  <div className={css.lessonPlanDetailGrid}>
                    {[['Teaching methods', p.teachingMethods], ['Teaching materials', p.teachingMaterials], ['References', p.references], ['Resources', p.resources]].map(([label, items]) => <div className={css.lessonPlanDetailSection} key={label}><h4>{label}</h4>{items?.length ? <ul className={css.lessonPlanDetailList}>{items.map((item, i) => <li key={i}>{item}</li>)}</ul> : <p>—</p>}</div>)}
                  </div>
                  <div className={css.lessonPlanDetailSection}><h4>Development of the Lesson</h4><div className={css.lessonPlanDetailTable}><table className={css.table}><thead><tr><th>Stage</th><th>Min</th><th>Teacher activities</th><th>Student activities</th></tr></thead><tbody>{(p.developmentStages || []).map((stage, i) => <tr key={i}><td>{stage.stage}</td><td>{stage.durationMinutes}</td><td>{stage.teacherActivities}</td><td>{stage.studentActivities}</td></tr>)}</tbody></table></div></div>
                  {p.status === 'approved' && p.principalRemark && <div className={css.lessonPlanDetailSection}><h4>Principal Remark</h4><p>{p.principalRemark}</p></div>}
                  {p.status === 'needs_revision' && p.reviewNotes && <div className={`${css.lessonPlanDetailSection} ${css.lessonPlanReviewNote}`}><h4>Review Notes</h4><p>{p.reviewNotes}</p></div>}
                  <div className={css.drawerExportRow}>
                    <button className={css.drawerExportBtn} onClick={() => exportLessonPlanPdf(p)}>🖨 Export PDF</button>
                    <button className={css.drawerExportBtn} style={{ background: 'linear-gradient(135deg,#1a6e73,#2a9d8f)' }} onClick={() => exportLessonPlanExcel(p)}>📊 Export Excel</button>
                  </div>
                </div></td></tr>}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalState && (
        <LessonPlanFormModal
          classes={classes}
          subjects={subjects}
          school={school}
          teacher={teacher}
          aiEnabled={aiEnabled}
          initial={modalState === 'new' ? null : modalState}
          onClose={() => setModalState(null)}
          onSaved={handleSaved}
        />
      )}

      {deleteConfirmPlan && (
        <div className={css.modalOverlay} onClick={() => { if (!deleting) setDeleteConfirmPlan(null); }}>
          <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h2 className={css.modalTitle}>Delete Lesson Plan</h2>
              <button className={css.closeBtn} onClick={() => setDeleteConfirmPlan(null)} disabled={!!deleting}>✕</button>
            </div>
            <div className={css.modalBody}>
              <p style={{ margin: 0, color: '#455a64', fontSize: '0.92rem' }}>
                You are about to permanently delete <strong>&ldquo;{(deleteConfirmPlan.topics || []).join(', ') || deleteConfirmPlan.topic}&rdquo;</strong>. This action cannot be undone.
              </p>
            </div>
            <div className={css.modalFooter}>
              <button className={css.cancelBtn} onClick={() => setDeleteConfirmPlan(null)} disabled={!!deleting}>Cancel</button>
              <button className={css.saveBtn} style={{ background: '#dc2626' }} onClick={handleDelete} disabled={!!deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── My Payslips Tab ───────────────────────────────────────────────────────────
const payslipStatusChipClass = {
  pending: css.chipDraft, processing: css.chipDraft, paid: css.chipClosed, failed: css.chipDraft, no_bank_details: css.chipDraft,
};
const payslipStatusLabel = {
  pending: 'Pending', processing: 'Processing', paid: 'Paid', failed: 'Failed', no_bank_details: 'No Bank Details',
};

function PayslipsTab({ school }) {
  const managementEnabled = school?.subscription?.plan === 'premium' && school?.subscription?.schoolManagementEnabled !== false;
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewSlip, setViewSlip] = useState(null);

  useEffect(() => {
    if (!managementEnabled) { setLoading(false); return; }
    teacherFetch('/teacher/payroll/payslips').then(({ ok, data }) => {
      if (ok) setPayslips(data.payslips || []);
      else setError(data.error || 'Failed to load payslips.');
      setLoading(false);
    });
  }, [managementEnabled]);

  const fmtNaira = (n) => `₦${Number(n || 0).toLocaleString('en-NG')}`;

  if (!managementEnabled) {
    return (
      <div>
        <h2 className={css.tabTitle}>My Payslips</h2>
        <div className={css.premiumLockBanner}>
          🔒 Payroll is a <strong>Premium</strong> feature. Ask your school admin to upgrade the plan from Settings to unlock it.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={css.tabBar}>
        <h2 className={css.tabTitle}>My Payslips</h2>
      </div>
      {error && <div className={css.inlineError} style={{ marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <SkeletonTableRows rows={4} cols={4} />
      ) : payslips.length === 0 ? (
        <EmptyState message="No payslips yet. They'll appear here once your school processes a payroll run." />
      ) : (
        <div className={css.tableWrap}>
          <table className={css.table}>
            <thead><tr><th>Period</th><th>Net Pay</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {payslips.map(p => (
                <tr key={p.runId}>
                  <td className={css.bold}>{p.period}</td>
                  <td>{fmtNaira(p.netPay)}</td>
                  <td>
                    <span className={`${css.chip} ${payslipStatusChipClass[p.payoutStatus] || css.chipDraft}`}>
                      {payslipStatusLabel[p.payoutStatus] || p.payoutStatus}
                    </span>
                  </td>
                  <td>
                    <button className={css.cancelBtn} onClick={() => setViewSlip(p)}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewSlip && (
        <div className={css.symOverlay} onClick={() => setViewSlip(null)}>
          <div className={`${css.symModal} BubblePopIn`} style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className={css.symHeader}>
              <span className={css.symTitle}>Payslip — {viewSlip.period}</span>
              <button className={css.symCloseBtn} type="button" onClick={() => setViewSlip(null)}>✕</button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <table className={css.table} style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <tbody>
                  <tr><td style={{ padding: '4px 0', color: '#666' }}>Basic Salary</td><td style={{ padding: '4px 0', textAlign: 'right' }}>{fmtNaira(viewSlip.basic)}</td></tr>
                  {(viewSlip.allowances || []).map((a, i) => (
                    <tr key={`a${i}`}><td style={{ padding: '4px 0', color: '#666' }}>{a.label || 'Allowance'}</td><td style={{ padding: '4px 0', textAlign: 'right' }}>+{fmtNaira(a.amount)}</td></tr>
                  ))}
                  {(viewSlip.deductions || []).map((d, i) => (
                    <tr key={`d${i}`}><td style={{ padding: '4px 0', color: '#666' }}>{d.label || 'Deduction'}</td><td style={{ padding: '4px 0', textAlign: 'right' }}>-{fmtNaira(d.amount)}</td></tr>
                  ))}
                  <tr><td style={{ padding: '10px 0 0', fontWeight: 700, borderTop: '1px solid #eceff1' }}>Net Pay</td><td style={{ padding: '10px 0 0', textAlign: 'right', fontWeight: 700, borderTop: '1px solid #eceff1' }}>{fmtNaira(viewSlip.netPay)}</td></tr>
                </tbody>
              </table>
              <button className={css.saveBtn} style={{ marginTop: 16, width: '100%' }} onClick={() => window.print()}>Print / Save as PDF</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Store & Inventory Tab ─────────────────────────────────────────────────────
const teacherRequestStatusChipClass = {
  pending: css.chipDraft, approved: css.chipClosed, rejected: css.chipDraft,
};

function TeacherInventoryTab({ school }) {
  const managementEnabled = school?.subscription?.plan === 'premium' && school?.subscription?.schoolManagementEnabled !== false;
  const [mode, setMode] = useState('browse'); // 'browse' | 'my-requests'
  const [items, setItems] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [requestTarget, setRequestTarget] = useState(null);

  const loadItems = useCallback(async () => {
    if (!managementEnabled) return;
    setLoading(true); setError('');
    const { ok, data } = await teacherFetch('/teacher/inventory');
    if (ok) setItems(data.items || []);
    else setError(data.error || 'Failed to load inventory items.');
    setLoading(false);
  }, [managementEnabled]);

  const loadRequests = useCallback(async () => {
    if (!managementEnabled) return;
    setLoading(true); setError('');
    const { ok, data } = await teacherFetch('/teacher/inventory/requests');
    if (ok) setRequests(data.requests || []);
    else setError(data.error || 'Failed to load your requests.');
    setLoading(false);
  }, [managementEnabled]);

  useEffect(() => { if (mode === 'browse') loadItems(); }, [mode, loadItems]);
  useEffect(() => { if (mode === 'my-requests') loadRequests(); }, [mode, loadRequests]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  if (!managementEnabled) {
    return (
      <div>
        <h2 className={css.tabTitle}>Store &amp; Inventory</h2>
        <div className={css.premiumLockBanner}>
          🔒 Store &amp; Inventory is a <strong>Premium</strong> feature. Ask your school admin to upgrade the plan from Settings to unlock it.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={css.tabBar}>
        <h2 className={css.tabTitle}>Store &amp; Inventory</h2>
      </div>
      {error && <div className={css.inlineError} style={{ marginBottom: 12 }}>{error}</div>}
      {success && <div className={css.inlineSuccess} style={{ marginBottom: 12 }}>{success}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className={mode === 'browse' ? css.primaryBtn : css.cancelBtn} onClick={() => setMode('browse')}>Browse Items</button>
        <button className={mode === 'my-requests' ? css.primaryBtn : css.cancelBtn} onClick={() => setMode('my-requests')}>My Requests</button>
      </div>

      {mode === 'browse' && (
        loading ? (
          <SkeletonTableRows rows={5} cols={4} />
        ) : items.length === 0 ? (
          <EmptyState message="No inventory items available yet." />
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead><tr><th>Item</th><th>Category</th><th>In Stock</th><th>Location</th><th></th></tr></thead>
              <tbody>
                {items.map(item => (
                  <tr key={item._id}>
                    <td className={css.bold}>{item.name}</td>
                    <td style={{ textTransform: 'capitalize' }}>{item.category}</td>
                    <td>{item.quantityInStock} {item.unit}</td>
                    <td>{item.location || '—'}</td>
                    <td>
                      <button className={css.saveBtn} onClick={() => setRequestTarget(item)}>Request</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {mode === 'my-requests' && (
        loading ? (
          <SkeletonTableRows rows={5} cols={4} />
        ) : requests.length === 0 ? (
          <EmptyState message="You haven't requested any items yet." />
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead><tr><th>Item</th><th>Qty</th><th>Issued To</th><th>Status</th></tr></thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r._id}>
                    <td className={css.bold}>{r.itemId?.name || '—'}</td>
                    <td>{r.quantity} {r.itemId?.unit}</td>
                    <td>{r.issueTo || '—'}</td>
                    <td>
                      <span className={`${css.chip} ${teacherRequestStatusChipClass[r.status] || css.chipDraft}`}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {requestTarget && (
        <TeacherInventoryRequestModal
          item={requestTarget}
          onClose={() => setRequestTarget(null)}
          onSuccess={() => { setRequestTarget(null); flash('Request submitted for admin approval.'); if (mode === 'my-requests') loadRequests(); }}
        />
      )}
    </div>
  );
}

function TeacherInventoryRequestModal({ item, onClose, onSuccess }) {
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [issueTo, setIssueTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!Number(quantity) || Number(quantity) <= 0) { setError('Enter a positive quantity.'); return; }
    setLoading(true); setError('');
    const { ok, data } = await teacherFetch(`/teacher/inventory/${item._id}/request`, {
      method: 'POST', body: JSON.stringify({ quantity: Number(quantity), reason, issueTo }),
    });
    setLoading(false);
    if (ok) onSuccess();
    else setError(data.error || 'Failed to submit request.');
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Request — {item.name}</h2>
        <p style={{ fontSize: '0.82rem', color: '#78909c', marginTop: -8 }}>{item.quantityInStock} {item.unit} currently in stock.</p>
        {error && <div className={css.inlineError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <label className={css.formLabel}>Quantity</label>
          <input className={css.formInput} type="number" min="1" value={quantity} onChange={e => setQuantity(e.target.value)} required autoFocus />
          <label className={css.formLabel}>Reason</label>
          <input className={css.formInput} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Classroom supplies for term" />
          <label className={css.formLabel}>Issue To (class/department)</label>
          <input className={css.formInput} value={issueTo} onChange={e => setIssueTo(e.target.value)} placeholder="e.g. JSS 2A" />
          <div className={css.modalFooter}>
            <button type="button" className={css.cancelBtn} onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className={css.saveBtn} disabled={loading}>{loading ? 'Submitting…' : 'Submit Request'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function TeacherDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    return NAV.some(item => item.key === requested) ? requested : 'overview';
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [teacher, setTeacher] = useState(null);
  const [school, setSchool] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Auth guard
  useEffect(() => {
    const token = localStorage.getItem('school-token');
    const actor = localStorage.getItem('school-actor');
    if (!token || actor !== 'teacher') {
      navigate('/school');
    }
  }, [navigate]);

  // Bootstrap
  useEffect(() => {
    async function bootstrap() {
      setLoading(true);
      try {
        const [profileRes] = await Promise.all([
          teacherFetch('/teacher/profile'),
        ]);
        if (!profileRes.ok) {
          if (profileRes.status === 401 || profileRes.status === 403) {
            localStorage.removeItem('school-token');
            localStorage.removeItem('school-actor');
            navigate('/school');
            return;
          }
          setError('Failed to load profile');
          setLoading(false);
          return;
        }
        const t = profileRes.data.teacher;
        setTeacher(t);
        // /school/profile is school-admin only (403s for teachers) — the school doc
        // (logo, name, subscription plan, motto, contact, etc.) now comes bundled
        // with /teacher/profile instead.
        setSchool(profileRes.data.school || { name: t?.schoolName || '' });

        // Teacher's assigned classes come populated from the profile endpoint
        setClasses(t?.assignedClasses || []);

        // Derive subjects from teacher.assignedSubjects (already populated)
        setSubjects(t?.assignedSubjects || []);
      } catch {
        setError('Connection error. Try refreshing.');
      } finally {
        setLoading(false);
      }
    }
    bootstrap();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('school-token');
    localStorage.removeItem('school-actor');
    navigate('/school');
  };

  const aiEnabled = school?.subscription?.plan !== 'standard' || !!school?.subscription?.aiQuestionsEnabled;
  const pageTitle = NAV.find(item => item.key === activeTab)?.label || 'Dashboard';

  if (loading) {
    return (
      <div className={css.shell}>
        {/* Skeleton sidebar */}
        <aside className={css.sidebar}>
          <div className={css.sidebarLogo}>
            <img src="/favicon.ico" alt="" className={css.sidebarLogoImg} />
            <div className={css.sidebarLogoText}>Bubble Ai Schools</div>
          </div>
          <div style={{ padding: '10px 20px 16px' }}>
            <span className={css.skel} style={{ height: 9, width: '65%', display: 'block' }} />
          </div>
          <nav>
            {NAV.map(({ key }) => (
              <div key={key} className={css.navItem}>
                <span className={css.skel} style={{ height: 14, width: 14, borderRadius: 4 }} />
                <span className={css.skel} style={{ height: 11, width: '55%' }} />
              </div>
            ))}
          </nav>
          <div className={css.sidebarBottom}>
            <span className={css.skel} style={{ height: 11, width: '60%', marginBottom: 7, display: 'block' }} />
            <span className={css.skel} style={{ height: 9, width: '80%', display: 'block' }} />
          </div>
        </aside>

        {/* Skeleton main content */}
        <main className={css.main}>
          <div className={css.content}>
            {/* Stat cards row */}
            <span className={css.skel} style={{ height: 22, width: '28%', marginBottom: 6, display: 'block' }} />
            <span className={css.skel} style={{ height: 11, width: '20%', marginBottom: 24, display: 'block' }} />
            <div className={css.statGrid} style={{ marginBottom: 28 }}>
              {[52, 42, 44, 38].map((w, i) => (
                <div key={i} className={css.statCard}>
                  <span className={css.skel} style={{ height: 9, width: `${w}%`, marginBottom: 10, display: 'block' }} />
                  <span className={css.skel} style={{ height: 28, width: '40%', display: 'block' }} />
                </div>
              ))}
            </div>
            {/* Content card */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 22, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 16 }}>
              <span className={css.skel} style={{ height: 13, width: '30%', marginBottom: 18, display: 'block' }} />
              {[80, 65, 72, 58, 68].map((w, i) => (
                <span key={i} className={css.skel} style={{ height: 11, width: `${w}%`, marginBottom: 11, display: 'block' }} />
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return <div className={css.loaderPage}><p style={{ color: 'crimson' }}>{error}</p></div>;
  }

  return (
    <div className={css.shell}>
      {mobileNavOpen && <div className={css.navOverlay} onClick={() => setMobileNavOpen(false)} />}
      {/* Sidebar */}
      <aside className={`${css.sidebar} ${mobileNavOpen ? css.sidebarOpen : ''}`}>
        <div className={css.sidebarLogo}>
          <img src={school?.logo || '/favicon.ico'} alt={school?.name || 'Bubble Ai Schools'} className={css.sidebarLogoImg} />
          <div className={css.sidebarLogoText}>{school?.name || 'Bubble Ai Schools'}</div>
        </div>
        <nav>
          {NAV.map(({ key, label, Icon }) => (
            <div
              key={key}
              className={`${css.navItem} ${activeTab === key ? css.navItemActive : ''}`}
              onClick={() => { setActiveTab(key); setMobileNavOpen(false); }}
            >
              <Icon fontSize="small" />
              <span>{label}</span>
            </div>
          ))}
        </nav>
        <div className={css.sidebarBottom}>
          <div className={css.teacherName}>{teacher?.firstName} {teacher?.lastName}</div>
          <div className={css.teacherEmail}>{teacher?.email}</div>
          <div className={css.navItem} onClick={handleLogout} style={{ marginTop: 8, color: '#e57373' }}>
            <LogoutIcon fontSize="small" />
            <span>Logout</span>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className={css.main}>
        <header className={adminCss.topbar}>
          <div className={adminCss.topbarLeft}>
            <button className={adminCss.hamburgerBtn} onClick={() => setMobileNavOpen(true)} aria-label="Open menu">
              <MenuIcon fontSize="small" />
            </button>
            <h1 className={adminCss.topbarTitle}>{pageTitle}</h1>
          </div>
          <div className={adminCss.topbarRight}>
            {teacher && (
              <div className={adminCss.adminBadge}>
                <AvatarOrInitials
                  src={teacher.profileImg}
                  firstName={teacher.firstName}
                  lastName={teacher.lastName}
                  size={28}
                  className={adminCss.adminAvatar}
                />
                <span>{teacher.firstName} {teacher.lastName}</span>
              </div>
            )}
            <SchoolNotificationCenter inline />
          </div>
        </header>
        <div className={css.content}>
          {activeTab === 'overview' && <SchoolPWAInstallBanner />}
          {activeTab === 'overview'       && <OverviewTab teacher={teacher} school={school} />}
          {activeTab === 'exams'          && <ExamsTab subjects={subjects} classes={classes} aiEnabled={aiEnabled} school={school} />}
          {activeTab === 'students'       && <StudentsTab school={school} />}
          {activeTab === 'class-summary'  && <ClassSummaryTab />}
          {activeTab === 'topic-analytics'&& <TopicAnalyticsTab subjects={subjects} classes={classes} />}
          {activeTab === 'insights'       && <InsightsTab subjects={subjects} />}
          {activeTab === 'question-bank'  && <QuestionBankTab subjects={subjects} />}
          {activeTab === 'curriculum'     && <CurriculumTab subjects={subjects} school={school} />}
          {activeTab === 'calendar'       && <CalendarTab school={school} />}
          {activeTab === 'timetable'      && <TimetableGrid mode="multi" classes={classes} subjects={subjects} fetchFn={teacherFetch} basePath="/teacher" term={school?.currentTerm} session={school?.academicSession} managementEnabled={school?.subscription?.plan === 'premium' && school?.subscription?.schoolManagementEnabled !== false} />}
          {activeTab === 'attendance'     && <AttendanceTab classes={classes} subjects={subjects} school={school} />}
          {activeTab === 'lesson-plans'   && <LessonPlansTab classes={classes} subjects={subjects} school={school} teacher={teacher} />}
          {activeTab === 'payslips'       && <PayslipsTab school={school} />}
          {activeTab === 'inventory'      && <TeacherInventoryTab school={school} />}
          {activeTab === 'settings'       && <TeacherProfileTab key={teacher?._id || 'teacher-loading'} teacher={teacher} school={school} onTeacherUpdated={setTeacher} />}
          {activeTab === 'messages'       && (
            <MessagingSegment
              fetchFn={teacherFetchRaw}
              basePath="/teacher"
              actorType="teacher"
              currentActorId={teacher?._id}
              managementEnabled={school?.subscription?.plan === 'premium' && school?.subscription?.schoolManagementEnabled !== false}
              canCreateClassGroup
              classOptions={classes}
            />
          )}
          <SchoolPoweredFooter role="teacher" />
        </div>
      </main>
    </div>
  );
}
